<template>
  <div class="wds2-editor-shell">
    <div class="wds2-editor-content text-white">
      
      <!-- Header with Mode Selector -->
      <div class="editor-header">
        <div class="header-title-row">
          <div class="header-title-group">
            <button @click="changeGame" class="folder-icon-btn" title="Change Game">⇄</button>
            <button @click="forceSyncWithGit" class="force-sync-btn" title="Force Sync with Git — clear local cache and re-download from GitHub">🔄</button>
            <button @click="checkStructureValidation" class="validate-structure-btn" title="Validate Structure — check for ID duplicates and cross-faction references">🔍 Validate</button>
            <h1 class="text-2xl font-bold mb-4">{{ gameName }}</h1>
          </div>
          <button @click="openWizard" class="wizard-launch-btn" title="Setup Wizard — quickly populate bases, factions, unit names and statlines">🧙 Setup Wizard</button>
          <button @click="downloadAllFiles" class="download-files-btn" title="Download all game data files as a ZIP archive">⬇️ Download Files</button>
          <button @click="openGitModal" class="upload-git-btn" title="Upload all game data files directly to a GitHub repository"><img src="/Images/Github.png" alt="GitHub" class="github-icon" /> Upload to Git</button>
          <!-- <button v-if="isWds3Mode" @click="clearSampleArmy" class="clear-sample-btn" title="Remove the Sample Army faction and all its data from memory">🗑️ Clear Sample Army</button> -->
        </div>
        
        <!-- Mode Selector -->
        <div style="display: flex; align-items: center; gap: 1.5rem; margin-bottom: 1.5rem; height: 3.5rem;">
          <div class="mode-selector">
            <button 
              @click="switchEditorMode('game')" 
              :class="['mode-btn', editorMode === 'game' ? 'active' : '']"
            >
              🎮 Game Data
            </button>
            <button 
              @click="switchEditorMode('faction')" 
              :class="['mode-btn', editorMode === 'faction' ? 'active' : '']"
            >
              🏰 Faction Data
            </button>
          </div>

          <!-- Faction Selector (only in faction mode, on same line) -->
          <div v-if="editorMode === 'faction'" class="faction-selector-inline">
            <label class="text-sm font-semibold">Select Faction:</label>
            <select v-model="selectedFaction" @change="onFactionChange" class="faction-dropdown">
              <option :value="null" disabled>Choose a faction...</option>
              <option v-for="faction in factions" :key="faction.name" :value="faction">
                {{ faction.name }}
              </option>
            </select>
            <button @click="addNewFaction" class="add-faction-btn">+ Add Faction</button>
          </div>
        </div>

        <!-- Old div closed below, removed to avoid duplication -->
      </div>

      <!-- Three-Panel Layout -->
      <div class="three-panel-layout">
        
        <!-- Panel 1: Type Selector -->
        <div class="type-panel">
          <h3 class="panel-title">Data Type</h3>
          
          <!-- Game Mode Types -->
          <template v-if="editorMode === 'game'">
            <button 
              v-for="type in gameDataTypes" 
              :key="type.key"
              @click="selectType(type.key)"
              :class="['type-btn', selectedType === type.key ? 'active' : '']"
            >
              {{ type.icon }} {{ type.label }}
            </button>
          </template>
          
          <!-- Faction Mode Types -->
          <template v-else>
            <button 
              v-for="type in factionDataTypes" 
              :key="type.key"
              @click="selectType(type.key)"
              :class="['type-btn', selectedType === type.key ? 'active' : '']"
              :disabled="!selectedFaction"
            >
              {{ type.icon }} {{ type.label }}
            </button>
          </template>
        </div>

        <!-- Panel 2: Entity List (hidden for full-width types) -->
        <div v-if="!isFullWidthType" class="list-panel">
          <div class="panel-header">
            <h3 class="panel-title">{{ listTitle }}</h3>
            <button v-if="canAdd" @click="addNewEntity" class="add-btn">
              + Add
            </button>
          </div>
          
          <div class="search-container">
            <input 
              v-model="searchText" 
              type="text" 
              placeholder="Search..." 
              class="search-input"
            />
            <button 
              v-if="searchText" 
              @click="searchText = ''" 
              class="search-clear-btn"
              title="Clear search"
            >
              ✕
            </button>
          </div>

          <div class="entity-list">
            <div 
              v-for="entity in filteredEntities" 
              :key="entity.Id"
              @click="selectEntity(entity)"
              @mouseenter="showPreview(entity, $event)"
              @mousemove="updatePreviewPosition($event)"
              @mouseleave="hidePreview"
              :class="['entity-item', selectedEntity === entity ? 'selected' : '']"
            >
              <template v-if="selectedType === 'profiles'">
                <img
                  v-if="profileThumbnailMap[entity.Id]"
                  :src="profileThumbnailMap[entity.Id]"
                  class="entity-thumbnail"
                  :alt="entity.Name"
                />
                <span
                  v-else
                  class="entity-icon entity-icon--no-skin"
                  title="This profile has no skin associated yet!"
                >❗</span>
              </template>
              <template v-else-if="selectedType === 'units'">
                <span
                  v-if="!isUnitInHierarchy(entity.Id)"
                  class="entity-icon entity-icon--no-hierarchy"
                  title="This unit is not placed in any hierarchy category"
                >❗</span>
                <span v-else class="entity-icon">{{ currentTypeIcon }}</span>
              </template>
              <span v-else class="entity-icon">{{ currentTypeIcon }}</span>
              <span class="entity-name">{{ entityDisplayName(entity) }}</span>
              <button @click.stop="removeEntity(entity)" class="remove-btn">✕</button>
            </div>
          </div>
        </div>

        <!-- Panel 3: Editor -->
        <div class="editor-panel" :class="{ 'full-width': isFullWidthType }">
          <div class="panel-header">
            <h3 class="panel-title">
              {{ panelTitle }}
              <span v-if="selectedEntity && !isFullWidthType && selectedEntity.Id" class="entity-id-small">{{ selectedEntity.Id }}</span>
              <span v-if="selectedType === 'hierarchy'" class="hierarchy-section-toggle">
                <button @click="hierarchySection = 'Factions'" :class="['hierarchy-toggle-btn', hierarchySection === 'Factions' ? 'active' : '']">
                  🏰 Factions
                </button>
                <button @click="hierarchySection = 'Cards'" :class="['hierarchy-toggle-btn', hierarchySection === 'Cards' ? 'active' : '']">
                  🃏 Cards
                </button>
              </span>
            </h3>
          </div>

          <div v-if="selectedEntity || isFullWidthType" class="editor-content">
            <!-- Common Fields (not for full-width types) -->
            <template v-if="selectedEntity && !isFullWidthType">
              <div class="form-group">
                <label>Name</label>
                <input v-model="selectedEntity.Name" class="input-field" @input="markAsChanged" />
              </div>            <div v-if="selectedEntity.Description !== undefined || ['cards', 'rules', 'options', 'equipment'].includes(selectedType)" class="form-group">
              <label>Description</label>
              <textarea v-model="selectedEntity.Description" class="textarea-field" rows="8" @input="markAsChanged"></textarea>
            </div>
            </template>

            <!-- Unit-specific fields -->
            <template v-if="selectedType === 'units'">
              <div class="form-group">
                <label>Formation</label>
                <select v-model.number="selectedEntity.Formation" class="input-field" @change="onFormationChange">
                  <option v-if="gameFormations.length === 0" :value="selectedEntity.Formation">{{ selectedEntity.Formation }}</option>
                  <option v-for="f in gameFormations" :key="f.Index" :value="f.Index">{{ f.Type }}</option>
                </select>
              </div>

              <div class="form-group">
                <label>{{ defaultFrontLabel }}</label>
                <input v-model.number="selectedEntity.DefaultFront" type="number" class="input-field" @input="markAsChanged" />
              </div>

              <div class="form-group">
                <label>Linked Profiles</label>
                <div v-if="!selectedEntity.BasicProfileIds && !selectedEntity.ProfileIds" class="mb-2">
                  <button @click="$set(selectedEntity, 'BasicProfileIds', [])" :class="['small-btn', 'btn-warning']">+ Add Profile</button>
                </div>
                <div v-else>
                  <div 
                    v-for="(profileId, index) in (selectedEntity.BasicProfileIds || selectedEntity.ProfileIds)" 
                    :key="index" 
                    class="list-item"
                    draggable="true"
                    @dragstart="onDragStart(index)"
                    @dragover="onDragOver"
                    @drop="onDrop(index, 'BasicProfileIds')"
                    @dragend="onDragEnd"
                    :class="{ 'dragging': draggedIndex === index }"
                  >
                    <span class="drag-handle" title="Drag to reorder">⋮⋮</span>
                    <a 
                      href="#" 
                      @click.prevent="navigateToEntity(profileId, 'profiles')" 
                      @mouseenter="showPreviewById(profileId, $event)"
                      @mousemove="updatePreviewPosition($event)"
                      @mouseleave="hidePreview"
                      class="id-link"
                      :title="`Click to edit profile: ${profileId}`"
                    >
                      {{ getEntityName(profileId) }}
                    </a>
                    <button @click="(selectedEntity.BasicProfileIds || selectedEntity.ProfileIds).splice(index, 1); markAsChanged()" class="remove-btn-small">✕</button>
                  </div>
                  <button @click="addBasicProfileId()" class="small-btn mt-2">+ Add Profile</button>
                </div>
              </div>

              <div class="form-group">
                <label>Attributes</label>
                <div v-if="!selectedEntity.Attributes" class="mb-2">
                  <button @click="$set(selectedEntity, 'Attributes', {})" class="small-btn">+ Add Attributes</button>
                </div>
                <div v-else class="attributes-editor">
                  <div 
                    v-for="(value, key) in selectedEntity.Attributes" 
                    :key="key" 
                    class="attribute-row"
                    draggable="true"
                    @dragstart="dragStartAttribute('Attributes', key)"
                    @dragover="dragOverAttribute"
                    @drop="dropAttribute('Attributes', key)"
                  >
                    <span class="drag-handle" title="Drag to reorder">⋮⋮</span>
                    <input :value="key" @blur="(e) => renameAttribute('Attributes', key, e.target.value)" class="input-field-small" style="flex: 0 0 120px" placeholder="Key" />
                    <span class="text-gray-400 mx-2">=</span>
                    <input v-model="selectedEntity.Attributes[key]" class="input-field-small" placeholder="Value" />
                    <button @click="deleteAttribute('Attributes', key)" class="remove-btn-small">✕</button>
                  </div>
                  <button @click="addAttribute('Attributes')" class="small-btn mt-2">+ Add Attribute</button>
                </div>
              </div>

              <!-- Linked Options for Unit -->
              <div class="form-group">
                <label>Linked Options (from modifiedunit.json)</label>
                <div v-if="getLinkedOptionsForUnit(selectedEntity.Id).length === 0" class="text-gray-400 text-sm mb-2">
                  No options linked to this unit
                </div>
                <div v-else class="displayable-editor mb-2">
                  <div 
                    v-for="(mod, index) in modifiedUnits.filter(m => m.UnitId === selectedEntity.Id)" 
                    :key="index" 
                    class="displayable-row"
                  >
                    <span class="displayable-icon">⚙️</span>
                    <button
                      @click="openModifiedUnitDialog(mod)" 
                      class="option-link-btn"
                      :title="`Edit option link: ${getEntityName(mod.OptionId)}`"
                    >
                      {{ getEntityName(mod.OptionId) }}
                    </button>
                    <span class="text-gray-400 mx-2">→</span>
                    <input 
                      v-model="mod.NewUnitId" 
                      class="input-field-small" 
                      placeholder="New Unit ID (optional)"
                      style="flex: 1"
                      @input="markFactionFileDirty('modifiedunit.json')"
                    />
                    <input 
                      v-model="mod.NewProfileId" 
                      class="input-field-small ml-2" 
                      placeholder="New Profile ID (optional)"
                      style="flex: 1"
                      @input="markFactionFileDirty('modifiedunit.json')"
                    />
                    <button @click="removeUnitOptionLink(index)" class="remove-btn-small ml-2">✕</button>
                  </div>
                </div>
                <button @click="addUnitOptionLink()" class="small-btn">+ Link Option</button>
              </div>

              <!-- Hierarchy Management for Units -->
              <div class="form-group form-group-wide">
                <label>Hierarchy Placement</label>
                <div v-if="!selectedFaction" class="text-gray-400 text-sm mb-2">
                  No faction loaded
                </div>
                <div v-else-if="getUnitHierarchyLocations(selectedEntity.Id).length === 0" class="text-gray-400 text-sm mb-2">
                  This unit is not placed in any hierarchy category
                </div>
                <div v-else class="displayable-editor mb-2">
                  <div 
                    v-for="(location, index) in getUnitHierarchyLocations(selectedEntity.Id)" 
                    :key="index" 
                    class="displayable-row"
                  >
                    <span class="displayable-icon">📁</span>
                    <span class="id-link" style="flex: 1">{{ location.path }}</span>
                    <button @click="removeUnitFromHierarchyPath(selectedEntity.Id, location.pathArray)" class="remove-btn-small ml-2">✕</button>
                  </div>
                </div>
                <div v-if="selectedFaction" class="mt-2">
                  <select v-model="selectedHierarchyPath" class="input-field mb-2">
                    <option :value="null">-- Select a category --</option>
                    <option 
                      v-for="(pathObj, index) in getAvailableHierarchyPaths()" 
                      :key="index" 
                      :value="pathObj"
                    >
                      {{ pathObj.path }}
                    </option>
                  </select>
                  <button 
                    @click="addUnitToHierarchyPath(selectedEntity.Id, selectedHierarchyPath)" 
                    :disabled="!selectedHierarchyPath"
                    class="small-btn"
                  >
                    + Add to Category
                  </button>
                </div>
              </div>
            </template>

            <!-- Profile-specific fields -->
            <template v-if="selectedType === 'profiles'">
              <div class="form-group">
                <label>Base</label>
                <select v-model="selectedEntity.BaseId" @change="markAsChanged" class="input-field">
                  <option :value="null">No base selected</option>
                  <option 
                    v-for="base in bases" 
                    :key="base.Id" 
                    :value="base.Id"
                  >
                    {{ base.Name || base.Id }}
                  </option>
                </select>
              </div>

              <div class="form-group">
                <label>Children (References)</label>
                <div v-if="normalizedChildren.length === 0" class="mb-2">
                  <button @click="addChild()" class="small-btn">+ Add References</button>
                </div>
                <div v-else class="displayable-editor">
                  <div 
                    v-for="(child, index) in normalizedChildren" 
                    :key="index" 
                    v-if="getChildId(child)"
                    class="displayable-row"
                    draggable="true"
                    @dragstart="onDragStart(index)"
                    @dragover="onDragOver"
                    @drop="onDrop(index, 'Children')"
                    @dragend="onDragEnd"
                    :class="{ 'dragging': draggedIndex === index }"
                  >
                    <span class="drag-handle" title="Drag to reorder">⋮⋮</span>
                    <span class="displayable-icon">{{ getEntityIcon(getChildId(child)) }}</span>
                    <a 
                      href="#" 
                      @click.prevent="navigateToEntityById(getChildId(child))" 
                      @mouseenter="showPreviewById(getChildId(child), $event)"
                      @mousemove="updatePreviewPosition($event)"
                      @mouseleave="hidePreview"
                      class="id-link"
                      style="flex: 0 0 200px"
                      :title="`Click to edit: ${getChildId(child)}`"
                    >
                      {{ getEntityName(getChildId(child)) }}
                    </a>
                    <span class="text-gray-400 mx-2">→</span>
                    <input v-model="child.Info" class="input-field-small" placeholder="Info (optional)" @input="markAsChanged" />
                    <button @click="deleteChild(index)" class="remove-btn-small">✕</button>
                  </div>
                  <button @click="addChild()" class="small-btn mt-2">+ Add Reference</button>
                </div>
              </div>

              <!-- Linked Options for Profile -->
              <div class="form-group">
                <label>Linked Options (from modifiedprofile.json)</label>
                <div v-if="getLinkedOptionsForProfile(selectedEntity.Id).length === 0" class="text-gray-400 text-sm mb-2">
                  No options linked to this profile
                </div>
                <div v-else class="displayable-editor mb-2">
                  <div 
                    v-for="(mod, index) in modifiedProfiles.filter(m => m.ProfileId === selectedEntity.Id)" 
                    :key="index" 
                    class="displayable-row"
                  >
                    <span class="displayable-icon">⚙️</span>
                    <button
                      @click="openModifiedProfileDialog(mod)" 
                      class="option-link-btn"
                      :title="`Edit option link: ${getEntityName(mod.OptionId)}`"
                    >
                      {{ getEntityName(mod.OptionId) }}
                    </button>
                    <span class="text-gray-400 mx-2">in unit:</span>
                    <input 
                      v-model="mod.UnitId" 
                      class="input-field-small" 
                      placeholder="Unit ID"
                      style="flex: 1"
                      @input="markAsChanged"
                    />
                    <button @click="removeProfileOptionLink(index)" class="remove-btn-small ml-2">✕</button>
                  </div>
                </div>
                <button @click="addProfileOptionLink()" class="small-btn">+ Link Option</button>
              </div>
            </template>

            <!-- Statline-specific fields -->
            <template v-if="selectedType === 'statlines'">
              <div class="form-group">
                <label>Attributes (Stats)</label>
                <div v-if="!selectedEntity.Attributes" class="mb-2">
                  <button @click="$set(selectedEntity, 'Attributes', {})" class="small-btn">+ Add Attributes</button>
                </div>
                <div v-else class="attributes-editor">
                  <div 
                    v-for="(value, key) in selectedEntity.Attributes" 
                    :key="key" 
                    class="attribute-row"
                    draggable="true"
                    @dragstart="dragStartAttribute('Attributes', key)"
                    @dragover="dragOverAttribute"
                    @drop="dropAttribute('Attributes', key)"
                  >
                    <span class="drag-handle" title="Drag to reorder">⋮⋮</span>
                    <input :value="key" @blur="(e) => renameAttribute('Attributes', key, e.target.value)" class="input-field-small" style="flex: 0 0 80px" placeholder="Key" />
                    <span class="text-gray-400 mx-2">=</span>
                    <input v-model="selectedEntity.Attributes[key]" class="input-field-small" placeholder="Value" @input="markAsChanged" />
                    <button @click="deleteAttribute('Attributes', key)" class="remove-btn-small">✕</button>
                  </div>
                  <button @click="addAttribute('Attributes')" class="small-btn mt-2">+ Add Stat</button>
                </div>
              </div>

              <div class="form-group">
                <label>Children (Rules/References)</label>
                <div v-if="normalizedChildren.length === 0" class="mb-2">
                  <button @click="addChild()" class="small-btn">+ Add References</button>
                </div>
                <div v-else class="displayable-editor">
                  <div 
                    v-for="(child, index) in normalizedChildren" 
                    :key="index" 
                    v-if="getChildId(child)"
                    class="displayable-row"
                    draggable="true"
                    @dragstart="onDragStart(index)"
                    @dragover="onDragOver"
                    @drop="onDrop(index, 'Children')"
                    @dragend="onDragEnd"
                    :class="{ 'dragging': draggedIndex === index }"
                  >
                    <span class="drag-handle" title="Drag to reorder">⋮⋮</span>
                    <span class="displayable-icon">{{ getEntityIcon(getChildId(child)) }}</span>
                    <a 
                      href="#" 
                      @click.prevent="navigateToEntityById(getChildId(child))" 
                      @mouseenter="showPreviewById(getChildId(child), $event)"
                      @mousemove="updatePreviewPosition($event)"
                      @mouseleave="hidePreview"
                      class="id-link"
                      style="flex: 0 0 200px"
                      :title="`Click to edit: ${getChildId(child)}`"
                    >
                      {{ getEntityName(getChildId(child)) }}
                    </a>
                    <span class="text-gray-400 mx-2">→</span>
                    <input v-model="child.Info" class="input-field-small" placeholder="Info (optional)" @input="markAsChanged" />
                    <button @click="deleteChild(index)" class="remove-btn-small">✕</button>
                  </div>
                  <button @click="addChild()" class="small-btn mt-2">+ Add Reference</button>
                </div>
              </div>
            </template>

            <!-- Equipment-specific fields -->
            <template v-if="selectedType === 'equipment'">
              <div class="form-group">
                <label>Children (Stats/Rules)</label>
                <div v-if="normalizedChildren.length === 0" class="mb-2">
                  <button @click="addChild()" class="small-btn">+ Add References</button>
                </div>
                <div v-else class="displayable-editor">
                  <div 
                    v-for="(child, index) in normalizedChildren" 
                    :key="index" 
                    v-if="getChildId(child)"
                    class="displayable-row"
                    draggable="true"
                    @dragstart="onDragStart(index)"
                    @dragover="onDragOver"
                    @drop="onDrop(index, 'Children')"
                    @dragend="onDragEnd"
                    :class="{ 'dragging': draggedIndex === index }"
                  >
                    <span class="drag-handle" title="Drag to reorder">⋮⋮</span>
                    <span class="displayable-icon">{{ getEntityIcon(getChildId(child)) }}</span>
                    <a 
                      href="#" 
                      @click.prevent="navigateToEntityById(getChildId(child))" 
                      @mouseenter="showPreviewById(getChildId(child), $event)"
                      @mousemove="updatePreviewPosition($event)"
                      @mouseleave="hidePreview"
                      class="id-link"
                      style="flex: 0 0 200px"
                      :title="`Click to edit: ${getChildId(child)}`"
                    >
                      {{ getEntityName(getChildId(child)) }}
                    </a>
                    <span class="text-gray-400 mx-2">→</span>
                    <input v-model="child.Info" class="input-field-small" placeholder="Info (optional)" @input="markAsChanged" />
                    <button @click="deleteChild(index)" class="remove-btn-small">✕</button>
                  </div>
                  <button @click="addChild()" class="small-btn mt-2">+ Add Reference</button>
                </div>
              </div>
            </template>

            <!-- Base-specific fields -->
            <template v-if="selectedType === 'bases'">
              <div class="form-group">
                <label>Shape</label>
                <select v-model.number="selectedEntity.Shape" class="input-field" @change="markAsChanged">
                  <option :value="0">Square (0)</option>
                  <option :value="1">Round (1)</option>
                </select>
              </div>

              <div class="form-group">
                <label>Width (mm)</label>
                <input v-model.number="selectedEntity.Width" type="number" step="0.1" class="input-field" placeholder="e.g., 25" @input="markAsChanged" />
              </div>

              <div class="form-group">
                <label>Length (mm)</label>
                <input v-model.number="selectedEntity.Length" type="number" step="0.1" class="input-field" placeholder="e.g., 25" @input="markAsChanged" />
              </div>
            </template>

            <!-- Card-specific fields -->
            <template v-if="selectedType === 'cards'">
              <div class="form-group">
                <label>Attributes</label>
                <div v-if="!selectedEntity.Attributes" class="mb-2">
                  <button @click="$set(selectedEntity, 'Attributes', {})" class="small-btn">+ Add Attributes</button>
                </div>
                <div v-else class="attributes-editor">
                  <div 
                    v-for="(value, key) in selectedEntity.Attributes" 
                    :key="key" 
                    class="attribute-row"
                    draggable="true"
                    @dragstart="dragStartAttribute('Attributes', key)"
                    @dragover="dragOverAttribute"
                    @drop="dropAttribute('Attributes', key)"
                  >
                    <span class="drag-handle" title="Drag to reorder">⋮⋮</span>
                    <input :value="key" @blur="(e) => renameAttribute('Attributes', key, e.target.value)" class="input-field-small" style="flex: 0 0 120px" placeholder="Key" />
                    <span class="text-gray-400 mx-2">=</span>
                    <input v-model="selectedEntity.Attributes[key]" class="input-field-small" placeholder="Value" />
                    <button @click="deleteAttribute('Attributes', key)" class="remove-btn-small">✕</button>
                  </div>
                  <button @click="addAttribute('Attributes')" class="small-btn mt-2">+ Add Attribute</button>
                </div>
              </div>

              <div class="form-group">
                <label>UI Config</label>
                <div v-if="!selectedEntity.UIConfig" class="mb-2">
                  <button @click="initUIConfig()" class="small-btn">+ Add UI Config</button>
                </div>
                <div v-else class="ui-config-editor">
                  <div class="form-group">
                    <label class="text-sm">Width</label>
                    <input v-model.number="selectedEntity.UIConfig.Width" type="number" step="0.1" class="input-field" @input="markAsChanged" />
                  </div>
                  <div class="form-group">
                    <label class="text-sm">Length</label>
                    <input v-model.number="selectedEntity.UIConfig.Length" type="number" step="0.1" class="input-field" @input="markAsChanged" />
                  </div>
                  <div class="form-group">
                    <label class="text-sm">Image URL</label>
                    <input v-model="selectedEntity.UIConfig.ImageURL" class="input-field" placeholder="https://..." @input="markAsChanged" />
                  </div>
                  <div class="form-group">
                    <label class="text-sm">Image URL Back</label>
                    <input v-model="selectedEntity.UIConfig.ImageURLBack" class="input-field" placeholder="https://..." @input="markAsChanged" />
                  </div>
                  <div class="form-group">
                    <label class="text-sm">Color</label>
                    <input v-model="selectedEntity.UIConfig.Color" type="color" class="input-field" @input="markAsChanged" />
                  </div>
                  <div class="form-group">
                    <label class="text-sm">Text Color</label>
                    <input v-model="selectedEntity.UIConfig.TextColor" type="color" class="input-field" @input="markAsChanged" />
                  </div>
                  <div class="form-group">
                    <label class="text-sm">
                      <input v-model="selectedEntity.UIConfig.Border" type="checkbox" class="mr-2" @change="markAsChanged" />
                      Border
                    </label>
                  </div>
                  <div v-if="selectedEntity.UIConfig.Border" class="form-group">
                    <label class="text-sm">Border Color</label>
                    <input v-model="selectedEntity.UIConfig.BorderColor" type="color" class="input-field" @input="markAsChanged" />
                  </div>
                  <div class="form-group">
                    <label class="text-sm">Color 2</label>
                    <input v-model="selectedEntity.UIConfig.Color2" type="color" class="input-field" @input="markAsChanged" />
                  </div>
                  <div class="form-group">
                    <label class="text-sm">
                      <input v-model="selectedEntity.UIConfig.IsRound" type="checkbox" class="mr-2" @change="markAsChanged" />
                      Is Round
                    </label>
                  </div>
                  <div class="form-group">
                    <label class="text-sm">
                      <input v-model="selectedEntity.UIConfig.ShowInitials" type="checkbox" class="mr-2" @change="markAsChanged" />
                      Show Initials
                    </label>
                  </div>
                </div>
              </div>

              <div class="form-group">
                <label>Children (References)</label>
                <div v-if="normalizedChildren.length === 0" class="mb-2">
                  <button @click="addChild()" class="small-btn">+ Add References</button>
                </div>
                <div v-else class="displayable-editor">
                  <div 
                    v-for="(child, index) in normalizedChildren" 
                    :key="index" 
                    v-if="getChildId(child)"
                    class="displayable-row"
                    draggable="true"
                    @dragstart="onDragStart(index)"
                    @dragover="onDragOver"
                    @drop="onDrop(index, 'Children')"
                    @dragend="onDragEnd"
                    :class="{ 'dragging': draggedIndex === index }"
                  >
                    <span class="drag-handle" title="Drag to reorder">⋮⋮</span>
                    <span class="displayable-icon">{{ getEntityIcon(getChildId(child)) }}</span>
                    <a 
                      href="#" 
                      @click.prevent="navigateToEntityById(getChildId(child))" 
                      @mouseenter="showPreviewById(getChildId(child), $event)"
                      @mousemove="updatePreviewPosition($event)"
                      @mouseleave="hidePreview"
                      class="id-link"
                      style="flex: 0 0 200px"
                      :title="`Click to edit: ${getChildId(child)}`"
                    >
                      {{ getEntityName(getChildId(child)) }}
                    </a>
                    <span class="text-gray-400 mx-2">→</span>
                    <input v-model="child.Info" class="input-field-small" placeholder="Info (optional)" @input="markAsChanged" />
                    <button @click="deleteChild(index)" class="remove-btn-small">✕</button>
                  </div>
                  <button @click="addChild()" class="small-btn mt-2">+ Add Reference</button>
                </div>
              </div>
            </template>

            <!-- Option-specific fields -->
            <template v-if="selectedType === 'options'">
              <div class="form-group">
                <label>Type</label>
                <select v-model.number="selectedEntity.Type" class="input-field" @change="markAsChanged">
                  <option :value="0" title="Swap the whole unit for a different one.">Swap unit</option>
                  <option :value="1" title="Add or remove an item to a unit and ALL profiles in it.">Modify item on all</option>
                  <option :value="2" title="Add or remove an item to a unit and on basic profiles in it.">Modify item on basic</option>
                  <option :value="4" title="Add or remove an item from one single profile.">Modify item on single</option>
                  <option :value="3" title="Swaps the basic profile of the unit for a different one.">Swap basic</option>
                  <option :value="5" title="Swaps one single profile for a different one.">Swap single</option>
                  <option :value="6" title="Adds one single profile to the unit.">Add single</option>
                  <option :value="7" title="Adds an extra item to the unit.">Add extra</option>
                </select>
              </div>

              <div class="form-group">
                <label>Items To Add</label>
                <div v-if="normalizedItemsToAdd.length === 0" class="mb-2">
                  <button @click="addItemToOption()" class="small-btn">+ Add Items</button>
                </div>
                <div v-else class="displayable-editor">
                  <div 
                    v-for="(item, index) in normalizedItemsToAdd" 
                    :key="index" 
                    v-if="getChildId(item)"
                    class="displayable-row"
                    draggable="true"
                    @dragstart="onDragStart(index)"
                    @dragover="onDragOver"
                    @drop="onDrop(index, 'ItemsToAdd')"
                    @dragend="onDragEnd"
                    :class="{ 'dragging': draggedIndex === index }"
                  >
                    <span class="drag-handle" title="Drag to reorder">⋮⋮</span>
                    <span class="displayable-icon">{{ getEntityIcon(getChildId(item)) }}</span>
                    <a 
                      href="#" 
                      @click.prevent="navigateToEntityById(getChildId(item))" 
                      @mouseenter="showPreviewById(getChildId(item), $event)"
                      @mousemove="updatePreviewPosition($event)"
                      @mouseleave="hidePreview"
                      class="id-link"
                      style="flex: 0 0 200px"
                      :title="`Click to edit: ${getChildId(item)}`"
                    >
                      {{ getEntityName(getChildId(item)) }}
                    </a>
                    <span class="text-gray-400 mx-2">→</span>
                    <input v-model="item.Info" class="input-field-small" placeholder="Info" @input="markAsChanged" />
                    <button @click="deleteItemFromOption(index)" class="remove-btn-small">✕</button>
                  </div>
                  <button @click="addItemToOption()" class="small-btn mt-2">+ Add Item</button>
                </div>
              </div>

              <!-- Linked Units -->
              <div class="form-group">
                <label>Linked Units (from modifiedunit.json)</label>
                <div v-if="getLinkedUnitsForOption(selectedEntity.Id).length === 0" class="text-gray-400 text-sm mb-2">
                  This option is not linked to any units
                </div>
                <div v-else class="displayable-editor mb-2">
                  <div 
                    v-for="(mod, index) in modifiedUnits.filter(m => m.OptionId === selectedEntity.Id)" 
                    :key="'unit-' + mod.UnitId + '-' + mod.OptionId + '-' + index" 
                    class="linked-option-item"
                  >
                    <div class="displayable-row">
                      <span class="displayable-icon">🧑‍🤝‍🧑</span>
                      <a 
                        href="#" 
                        @click.prevent="navigateToEntity(mod.UnitId, 'units')" 
                        @mouseenter="showPreviewById(mod.UnitId, $event)"
                        @mousemove="updatePreviewPosition($event)"
                        @mouseleave="hidePreview"
                        class="id-link"
                        style="flex: 0 0 180px"
                        :title="`Click to edit unit: ${mod.UnitId}`"
                      >
                        {{ getEntityName(mod.UnitId) }}
                      </a>
                      <span class="text-gray-400 mx-2">→</span>
                      <!-- Type 0: Only New Unit ID (required) -->
                      <input 
                        v-if="selectedEntity.Type === 0"
                        v-model="mod.NewUnitId" 
                        class="input-field-small" 
                        placeholder="New Unit ID (required)"
                        style="flex: 1"
                        @input="markFactionFileDirty('modifiedunit.json')"
                        required
                      />
                      <!-- Types 3, 5, 6: Only New Profile ID (required) -->
                      <input 
                        v-else-if="[3, 5, 6].includes(selectedEntity.Type)"
                        v-model="mod.NewProfileId" 
                        class="input-field-small" 
                        placeholder="New Profile ID (required)"
                        style="flex: 1"
                        @input="markFactionFileDirty('modifiedunit.json')"
                        required
                      />
                      <!-- Other types: Both fields (optional) -->
                      <template v-else>
                        <input 
                          v-model="mod.NewUnitId" 
                          class="input-field-small" 
                          placeholder="New Unit ID (optional)"
                          style="flex: 1"
                          @input="markFactionFileDirty('modifiedunit.json')"
                        />
                        <input 
                          v-model="mod.NewProfileId" 
                          class="input-field-small ml-2" 
                          placeholder="New Profile ID (optional)"
                          style="flex: 1"
                          @input="markFactionFileDirty('modifiedunit.json')"
                        />
                      </template>
                      <button @click="removeOptionUnitLink(index)" class="remove-btn-small ml-2">✕</button>
                    </div>
                    <div class="ml-8 mt-2">
                      <label class="text-sm text-gray-400 mb-1">Referenced Items to Remove:</label>
                      <div v-if="!Array.isArray(mod.ReferencedItemsToRemove)" class="mb-2">
                        <button @click="$set(mod, 'ReferencedItemsToRemove', []); markFactionFileDirty('modifiedunit.json')" class="small-btn">+ Add Remove List</button>
                      </div>
                      <div v-else class="displayable-editor">
                        <div 
                          v-for="(item, idx) in mod.ReferencedItemsToRemove" 
                          :key="idx" 
                          class="displayable-row"
                        >
                          <span class="displayable-icon">{{ getEntityIcon(item.Id) }}</span>
                          <a 
                            href="#" 
                            @click.prevent="navigateToEntityById(item.Id)" 
                            @mouseenter="showPreviewById(item.Id, $event)"
                            @mousemove="updatePreviewPosition($event)"
                            @mouseleave="hidePreview"
                            class="id-link"
                            style="flex: 0 0 150px"
                          >
                            {{ getEntityName(item.Id) }}
                          </a>
                          <span class="text-gray-400 mx-2" title="Nested refs">{{ (item.Ref && item.Ref.length) ? '🔗 ' + item.Ref.length : '∅' }}</span>
                          <button @click="mod.ReferencedItemsToRemove.splice(idx, 1); markFactionFileDirty('modifiedunit.json')" class="remove-btn-small">✕</button>
                        </div>
                        <button @click="openEntitySearchForModifiedUnit(mod, 'remove')" class="small-btn mt-2">+ Add Reference</button>
                      </div>
                    </div>
                    <div class="ml-8 mt-2">
                      <label class="text-sm text-gray-400 mb-1">Referenced Items to Add:</label>
                      <div v-if="!Array.isArray(mod.ReferencedItemsToAdd)" class="mb-2">
                        <button @click="$set(mod, 'ReferencedItemsToAdd', []); markFactionFileDirty('modifiedunit.json')" class="small-btn">+ Add Add List</button>
                      </div>
                      <div v-else class="displayable-editor">
                        <div 
                          v-for="(item, idx) in mod.ReferencedItemsToAdd" 
                          :key="idx" 
                          class="displayable-row"
                        >
                          <span class="displayable-icon">{{ getEntityIcon(item.Id) }}</span>
                          <a 
                            href="#" 
                            @click.prevent="navigateToEntityById(item.Id)" 
                            @mouseenter="showPreviewById(item.Id, $event)"
                            @mousemove="updatePreviewPosition($event)"
                            @mouseleave="hidePreview"
                            class="id-link"
                            style="flex: 0 0 150px"
                          >
                            {{ getEntityName(item.Id) }}
                          </a>
                          <span class="text-gray-400 mx-2" title="Nested refs">{{ (item.Ref && item.Ref.length) ? '🔗 ' + item.Ref.length : '∅' }}</span>
                          <button @click="mod.ReferencedItemsToAdd.splice(idx, 1); markFactionFileDirty('modifiedunit.json')" class="remove-btn-small">✕</button>
                        </div>
                        <button @click="openEntitySearchForModifiedUnit(mod, 'add')" class="small-btn mt-2">+ Add Reference</button>
                      </div>
                    </div>
                  </div>
                </div>
                <button @click="addOptionUnitLink()" class="small-btn">+ Link to Unit</button>
              </div>

              <!-- Linked Profiles (only for option type 4) -->
              <div v-if="selectedEntity.Type === 4" class="form-group">
                <label>Linked Profiles (from modifiedprofile.json)</label>
                <div v-if="getLinkedProfilesForOption(selectedEntity.Id).length === 0" class="text-gray-400 text-sm mb-2">
                  This option is not linked to any profiles
                </div>
                <div v-else class="displayable-editor mb-2">
                  <div 
                    v-for="(mod, index) in modifiedProfiles.filter(m => m.OptionId === selectedEntity.Id)" 
                    :key="'profile-' + mod.ProfileId + '-' + mod.OptionId + '-' + index" 
                    class="linked-option-item"
                  >
                    <div class="displayable-row">
                      <span class="displayable-icon">👤</span>
                      <a 
                        href="#" 
                        @click.prevent="navigateToEntity(mod.ProfileId, 'profiles')" 
                        @mouseenter="showPreviewById(mod.ProfileId, $event)"
                        @mousemove="updatePreviewPosition($event)"
                        @mouseleave="hidePreview"
                        class="id-link"
                        style="flex: 0 0 180px"
                        :title="`Click to edit profile: ${mod.ProfileId}`"
                      >
                        {{ getEntityName(mod.ProfileId) }}
                      </a>
                      <span class="text-gray-400 mx-2">in unit:</span>
                      <input 
                        v-model="mod.UnitId" 
                        class="input-field-small" 
                        placeholder="Unit ID"
                        style="flex: 1"
                        @input="markFactionFileDirty('modifiedprofile.json')"
                      />
                      <button @click="removeOptionProfileLink(index)" class="remove-btn-small ml-2">✕</button>
                    </div>
                    <div class="ml-8 mt-2">
                      <label class="text-sm text-gray-400 mb-1">Referenced Items to Remove:</label>
                      <div v-if="!Array.isArray(mod.ReferencedItemsToRemove)" class="mb-2">
                        <button @click="$set(mod, 'ReferencedItemsToRemove', []); markFactionFileDirty('modifiedprofile.json')" class="small-btn">+ Add Remove List</button>
                      </div>
                      <div v-else class="displayable-editor">
                        <div 
                          v-for="(item, idx) in mod.ReferencedItemsToRemove" 
                          :key="idx" 
                          class="displayable-row"
                        >
                          <span class="displayable-icon">{{ getEntityIcon(item.Id) }}</span>
                          <a 
                            href="#" 
                            @click.prevent="navigateToEntityById(item.Id)" 
                            @mouseenter="showPreviewById(item.Id, $event)"
                            @mousemove="updatePreviewPosition($event)"
                            @mouseleave="hidePreview"
                            class="id-link"
                            style="flex: 0 0 150px"
                          >
                            {{ getEntityName(item.Id) }}
                          </a>
                          <span class="text-gray-400 mx-2" title="Nested refs">{{ (item.Ref && item.Ref.length) ? '🔗 ' + item.Ref.length : '∅' }}</span>
                          <button @click="mod.ReferencedItemsToRemove.splice(idx, 1); markFactionFileDirty('modifiedprofile.json')" class="remove-btn-small">✕</button>
                        </div>
                        <button @click="openEntitySearchForModifiedProfile(mod, 'remove')" class="small-btn mt-2">+ Add Reference</button>
                      </div>
                    </div>
                    <div class="ml-8 mt-2">
                      <label class="text-sm text-gray-400 mb-1">Referenced Items to Add:</label>
                      <div v-if="!Array.isArray(mod.ReferencedItemsToAdd)" class="mb-2">
                        <button @click="$set(mod, 'ReferencedItemsToAdd', []); markFactionFileDirty('modifiedprofile.json')" class="small-btn">+ Add Add List</button>
                      </div>
                      <div v-else class="displayable-editor">
                        <div 
                          v-for="(item, idx) in mod.ReferencedItemsToAdd" 
                          :key="idx" 
                          class="displayable-row"
                        >
                          <span class="displayable-icon">{{ getEntityIcon(item.Id) }}</span>
                          <a 
                            href="#" 
                            @click.prevent="navigateToEntityById(item.Id)" 
                            @mouseenter="showPreviewById(item.Id, $event)"
                            @mousemove="updatePreviewPosition($event)"
                            @mouseleave="hidePreview"
                            class="id-link"
                            style="flex: 0 0 150px"
                          >
                            {{ getEntityName(item.Id) }}
                          </a>
                          <span class="text-gray-400 mx-2" title="Nested refs">{{ (item.Ref && item.Ref.length) ? '🔗 ' + item.Ref.length : '∅' }}</span>
                          <button @click="mod.ReferencedItemsToAdd.splice(idx, 1); markFactionFileDirty('modifiedprofile.json')" class="remove-btn-small">✕</button>
                        </div>
                        <button @click="openEntitySearchForModifiedProfile(mod, 'add')" class="small-btn mt-2">+ Add Reference</button>
                      </div>
                    </div>
                  </div>
                </div>
                <button @click="addOptionProfileLink()" class="small-btn">+ Link to Profile</button>
              </div>

              <!-- Extra Items (only for option type 7) -->
              <div v-if="selectedEntity.Type === 7" class="form-group">
                <label>Extra Items (for AddExtra type)</label>
                <div v-if="!selectedEntity.ExtraItems" class="mb-2">
                  <button @click="$set(selectedEntity, 'ExtraItems', [])" class="small-btn">+ Add Extra Items</button>
                </div>
                <div v-else class="displayable-editor">
                  <div 
                    v-for="(item, index) in selectedEntity.ExtraItems" 
                    :key="index" 
                    class="displayable-row"
                    draggable="true"
                    @dragstart="onDragStart(index)"
                    @dragover="onDragOver"
                    @drop="onDropExtraItems(index)"
                    @dragend="onDragEnd"
                    :class="{ 'dragging': draggedIndex === index }"
                  >
                    <span class="drag-handle" title="Drag to reorder">⋮⋮</span>
                    <span class="displayable-icon">{{ getExtraItemIcon(item.Type) }}</span>
                    <a 
                      href="#" 
                      @click.prevent="navigateToExtraItem(item)" 
                      @mouseenter="showPreviewById(item.Id, $event)"
                      @mousemove="updatePreviewPosition($event)"
                      @mouseleave="hidePreview"
                      class="id-link"
                      style="flex: 0 0 200px"
                      :title="`Click to edit: ${item.Id}`"
                    >
                      {{ getEntityName(item.Id) || item.Id }}
                    </a>
                    <span class="text-gray-400 mx-2">Type:</span>
                    <select v-model="item.Type" class="input-field-small" style="flex: 0 0 120px" @change="markAsChanged">
                      <option value="Card">Card</option>
                      <option value="Unit">Unit</option>
                      <option value="Rule">Rule</option>
                      <option value="Equipment">Equipment</option>
                      <option value="Token">Token</option>
                    </select>
                    <button @click="selectedEntity.ExtraItems.splice(index, 1); markAsChanged()" class="remove-btn-small ml-2">✕</button>
                  </div>
                  <button @click="addExtraItem()" class="small-btn mt-2">+ Add Item</button>
                </div>
              </div>
            </template>

            <!-- Tokens Visual Editor -->
            <template v-if="selectedType === 'tokens'">
              <TokensEditor
                :value="tokens"
                @input="onTokensEditorInput"
              />
            </template>

            <!-- Terrain Visual Editor -->
            <template v-if="selectedType === 'terrains'">
              <TerrainEditor
                :value="terrains"
                @input="onTerrainsEditorInput"
              />
            </template>

            <!-- Deployments Visual Editor -->
            <template v-if="selectedType === 'deployments'">
              <DeploymentsEditor
                :value="deployments"
                @input="onDeploymentsEditorInput"
              />
            </template>

            <!-- Objectives Visual Editor -->
            <template v-if="selectedType === 'objectives'">
              <ObjectivesEditor
                :value="objectives"
                :available-token-skins="objectiveTokenSkins"
                :battlefield-width="gameConfigWidth"
                :battlefield-height="gameConfigHeight"
                @input="onObjectivesEditorInput"
              />
            </template>

            <!-- Hierarchy Editor -->
            <template v-if="selectedType === 'hierarchy'">
              <div v-if="!hierarchy || !hierarchy[hierarchySection]" class="text-gray-400 text-center py-8">
                <p>No hierarchy data loaded for {{ hierarchySection }}</p>
                <button @click="initializeHierarchySection()" class="small-btn mt-4">Initialize {{ hierarchySection }}</button>
              </div>
              <div v-else class="hierarchy-editor">
                <div style="display: flex; flex-direction: row; gap: 0.5rem;" class="mb-4">
                    <button @click="collapseAllFactions()" class="small-btn">Collapse All</button>
                    <button @click="expandAllFactions()" class="small-btn">Expand All</button>
                    <button @click="addHierarchyTopLevelNode()" class="small-btn" style="background: #10b981 !important; color: white;">+ Add</button>
                </div>

                <!-- Flexible Hierarchy: Auto-detects structure depth -->
                <div 
                  v-for="(level1Value, level1Key) in hierarchy[hierarchySection]" 
                  :key="level1Key"
                  class="hierarchy-faction"
                >
                  <!-- Level 1: Always show as a header -->
                  <div 
                    class="hierarchy-faction-header"
                    draggable="true"
                    @dragstart="onDragStartHierarchy(level1Key, 'faction')"
                    @dragover.prevent
                    @drop="onDropHierarchy(level1Key, 'faction')"
                    @dragend="onDragEndHierarchy"
                    :class="{ 'dragging': draggedHierarchyType === 'faction' && draggedHierarchyItem === level1Key }"
                  >
                    <span class="drag-handle" title="Drag to reorder">⋮⋮</span>
                    <button @click="toggleCategoryCollapse([level1Key])" class="collapse-btn" :title="isNodeCollapsed([level1Key]) ? 'Expand' : 'Collapse'">
                      {{ isNodeCollapsed([level1Key]) ? '▶' : '▼' }}
                    </button>
                    <span class="hierarchy-icon">{{ getNodeIcon([level1Key], level1Value) }}</span>
                    <input 
                      :value="level1Key"
                      @blur="(e) => renameNodeAtPath([level1Key], e.target.value)"
                      class="hierarchy-faction-input" 
                      :placeholder="level1Key"
                    />
                    <span class="text-gray-400 ml-2" v-if="isBranchNode(level1Value)">
                      ({{ Object.keys(level1Value).length }} {{ isLeafNode(level1Value) ? 'items' : 'children' }})
                    </span>
                    <span class="text-gray-400 ml-2" v-else-if="isLeafNode(level1Value)">
                      ({{ level1Value.length }} items)
                    </span>
                    <div class="hierarchy-node-actions">
                      <button 
                        v-if="isBranchNode(level1Value)" 
                        @click="addChildToPath([level1Key])" 
                        class="hierarchy-action-btn" 
                        title="Add child category"
                      >
                        📁+
                      </button>
                      <button 
                        @click="addItemToPath([level1Key])" 
                        class="hierarchy-action-btn" 
                        :title="isLeafNode(level1Value) ? 'Add item' : 'Convert to items and add'"
                      >
                        {{ hierarchySection === 'Factions' ? '🎲+' : '🃏+' }}
                      </button>
                    </div>
                    <button @click="removeNodeAtPath([level1Key])" class="remove-btn-small ml-auto">✕</button>
                  </div>

                  <!-- Level 1 Content -->
                  <div v-if="!isNodeCollapsed([level1Key])" class="hierarchy-categories">
                    <!-- If Level 1 is a leaf (array), show items directly -->
                    <template v-if="isLeafNode(level1Value)">
                      <div 
                        v-for="(itemId, index) in level1Value" 
                        :key="index"
                        class="hierarchy-unit"
                        draggable="true"
                        @dragstart="onDragStartHierarchy(index, 'unit', null, level1Key)"
                        @dragover.prevent
                        @drop="onDropHierarchy(index, 'unit', null, level1Key)"
                        @dragend="onDragEndHierarchy"
                        :class="{ 'dragging': draggedHierarchyType === 'unit' && draggedHierarchyItem === index && !draggedHierarchyFaction && draggedHierarchyCategory === level1Key }"
                      >
                        <span class="drag-handle" title="Drag to reorder">⋮⋮</span>
                        <span class="hierarchy-icon">{{ getItemIcon() }}</span>
                        <a 
                          href="#" 
                          @click.prevent="navigateToEntity(itemId, hierarchySection === 'Factions' ? 'units' : 'cards')" 
                          @mouseenter="showPreviewById(itemId, $event)"
                          @mousemove="updatePreviewPosition($event)"
                          @mouseleave="hidePreview"
                          class="id-link"
                        >
                          {{ getEntityName(itemId) || itemId }}
                        </a>
                        <button @click="removeItemAtPath([level1Key], index)" class="remove-btn-small">✕</button>
                      </div>
                    </template>

                    <!-- If Level 1 is a branch (object), show Level 2 -->
                    <template v-else-if="isBranchNode(level1Value)">
                      <div 
                        v-for="(level2Value, level2Key) in level1Value" 
                        :key="level2Key"
                        class="hierarchy-category"
                      >
                        <div 
                          class="hierarchy-category-header"
                          draggable="true"
                          @dragstart="onDragStartHierarchy(level2Key, 'category', level1Key)"
                          @dragover.prevent
                          @drop="onDropHierarchy(level2Key, 'category', level1Key)"
                          @dragend="onDragEndHierarchy"
                          :class="{ 'dragging': draggedHierarchyType === 'category' && draggedHierarchyItem === level2Key && draggedHierarchyFaction === level1Key }"
                        >
                          <span class="drag-handle" title="Drag to reorder">⋮⋮</span>
                          <button @click="toggleCategoryCollapse([level1Key, level2Key])" class="collapse-btn" :title="isNodeCollapsed([level1Key, level2Key]) ? 'Expand' : 'Collapse'">
                            {{ isNodeCollapsed([level1Key, level2Key]) ? '▶' : '▼' }}
                          </button>
                          <span class="hierarchy-icon">{{ getNodeIcon([level1Key, level2Key], level2Value) }}</span>
                          <input 
                            :value="level2Key"
                            @blur="(e) => renameNodeAtPath([level1Key, level2Key], e.target.value)"
                            class="hierarchy-category-input" 
                            :placeholder="level2Key"
                          />
                          <span class="text-gray-400 ml-2" v-if="isBranchNode(level2Value)">
                            ({{ Object.keys(level2Value).length }} children)
                          </span>
                          <span class="text-gray-400 ml-2" v-else-if="isLeafNode(level2Value)">
                            ({{ level2Value.length }} items)
                          </span>
                          <div class="hierarchy-node-actions">
                            <button 
                              v-if="isBranchNode(level2Value)" 
                              @click="addChildToPath([level1Key, level2Key])" 
                              class="hierarchy-action-btn" 
                              title="Add child category"
                            >
                              📁+
                            </button>
                            <button 
                              @click="addItemToPath([level1Key, level2Key])" 
                              class="hierarchy-action-btn" 
                              :title="isLeafNode(level2Value) ? 'Add item' : 'Convert to items and add'"
                            >
                              {{ hierarchySection === 'Factions' ? '🎲+' : '🃏+' }}
                            </button>
                          </div>
                          <button @click="removeNodeAtPath([level1Key, level2Key])" class="remove-btn-small ml-auto">✕</button>
                        </div>

                        <!-- Level 2 Content -->
                        <div v-if="!isNodeCollapsed([level1Key, level2Key])" class="hierarchy-units">
                          <!-- If Level 2 is a leaf (array), show items -->
                          <template v-if="isLeafNode(level2Value)">
                            <div 
                              v-for="(itemId, index) in level2Value" 
                              :key="index"
                              class="hierarchy-unit"
                              draggable="true"
                              @dragstart="onDragStartHierarchy(index, 'unit', level1Key, level2Key)"
                              @dragover.prevent
                              @drop="onDropHierarchy(index, 'unit', level1Key, level2Key)"
                              @dragend="onDragEndHierarchy"
                              :class="{ 'dragging': draggedHierarchyType === 'unit' && draggedHierarchyItem === index && draggedHierarchyFaction === level1Key && draggedHierarchyCategory === level2Key }"
                            >
                              <span class="drag-handle" title="Drag to reorder">⋮⋮</span>
                              <span class="hierarchy-icon">{{ getItemIcon() }}</span>
                              <a 
                                href="#" 
                                @click.prevent="navigateToEntity(itemId, hierarchySection === 'Factions' ? 'units' : 'cards')" 
                                @mouseenter="showPreviewById(itemId, $event)"
                                @mousemove="updatePreviewPosition($event)"
                                @mouseleave="hidePreview"
                                class="id-link"
                              >
                                {{ getEntityName(itemId) || itemId }}
                              </a>
                              <button @click="removeItemAtPath([level1Key, level2Key], index)" class="remove-btn-small">✕</button>
                            </div>
                          </template>

                          <!-- If Level 2 is a branch (object), show Level 3 -->
                          <template v-else-if="isBranchNode(level2Value)">
                            <div 
                              v-for="(level3Value, level3Key) in level2Value" 
                              :key="level3Key"
                              class="hierarchy-category"
                              style="margin-left: 1rem;"
                            >
                              <div 
                                class="hierarchy-category-header"
                                draggable="true"
                                @dragstart="onDragStartHierarchy(level3Key, 'category', level1Key, level2Key)"
                                @dragover.prevent
                                @drop="onDropHierarchy(level3Key, 'category', level1Key, level2Key)"
                                @dragend="onDragEndHierarchy"
                                :class="{ 'dragging': draggedHierarchyType === 'category' && draggedHierarchyItem === level3Key && draggedHierarchyFaction === level1Key && draggedHierarchyCategory === level2Key }"
                              >
                                <span class="drag-handle" title="Drag to reorder">⋮⋮</span>
                                <button @click="toggleCategoryCollapse([level1Key, level2Key, level3Key])" class="collapse-btn">
                                  {{ isNodeCollapsed([level1Key, level2Key, level3Key]) ? '▶' : '▼' }}
                                </button>
                                <span class="hierarchy-icon">{{ getNodeIcon([level1Key, level2Key, level3Key], level3Value) }}</span>
                                <input 
                                  :value="level3Key"
                                  @blur="(e) => renameNodeAtPath([level1Key, level2Key, level3Key], e.target.value)"
                                  class="hierarchy-category-input" 
                                  :placeholder="level3Key"
                                />
                                <span class="text-gray-400 ml-2" v-if="isBranchNode(level3Value)">
                                  ({{ Object.keys(level3Value).length }} children)
                                </span>
                                <span class="text-gray-400 ml-2" v-else-if="isLeafNode(level3Value)">
                                  ({{ level3Value.length }} items)
                                </span>
                                <div class="hierarchy-node-actions">
                                  <button 
                                    v-if="isBranchNode(level3Value)" 
                                    @click="addChildToPath([level1Key, level2Key, level3Key])" 
                                    class="hierarchy-action-btn" 
                                    title="Add child category"
                                  >
                                    📁+
                                  </button>
                                  <button 
                                    @click="addItemToPath([level1Key, level2Key, level3Key])" 
                                    class="hierarchy-action-btn" 
                                    :title="isLeafNode(level3Value) ? 'Add item' : 'Convert to items and add'"
                                  >
                                    {{ hierarchySection === 'Factions' ? '🎲+' : '🃏+' }}
                                  </button>
                                </div>
                                <button @click="removeNodeAtPath([level1Key, level2Key, level3Key])" class="remove-btn-small ml-auto">✕</button>
                              </div>

                              <!-- Level 3 Content (items only) -->
                              <div v-if="!isNodeCollapsed([level1Key, level2Key, level3Key]) && isLeafNode(level3Value)" class="hierarchy-units">
                                <div 
                                  v-for="(itemId, index) in level3Value" 
                                  :key="index"
                                  class="hierarchy-unit"
                                  draggable="true"
                                  @dragstart="onDragStartHierarchy(index, 'unit', level1Key, level2Key + '/' + level3Key)"
                                  @dragover.prevent
                                  @drop="onDropHierarchy(index, 'unit', level1Key, level2Key + '/' + level3Key)"
                                  @dragend="onDragEndHierarchy"
                                  :class="{ 'dragging': draggedHierarchyType === 'unit' && draggedHierarchyItem === index && draggedHierarchyFaction === level1Key && draggedHierarchyCategory === (level2Key + '/' + level3Key) }"
                                >
                                  <span class="drag-handle" title="Drag to reorder">⋮⋮</span>
                                  <span class="hierarchy-icon">{{ getItemIcon() }}</span>
                                  <a 
                                    href="#" 
                                    @click.prevent="navigateToEntity(itemId, hierarchySection === 'Factions' ? 'units' : 'cards')" 
                                    @mouseenter="showPreviewById(itemId, $event)"
                                    @mousemove="updatePreviewPosition($event)"
                                    @mouseleave="hidePreview"
                                    class="id-link"
                                  >
                                    {{ getEntityName(itemId) || itemId }}
                                  </a>
                                  <button @click="removeItemAtPath([level1Key, level2Key, level3Key], index)" class="remove-btn-small">✕</button>
                                </div>
                              </div>
                            </div>
                          </template>
                        </div>
                      </div>
                    </template>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>

      </div>
    </div>

    <!-- Entity Search Dialog -->
    <div v-if="showEntitySearchDialog" class="modal-overlay entity-search-overlay">
      <div class="modal-dialog">
        <div class="modal-header">
          <h3>Select Entity</h3>
          <button @click="closeEntitySearchDialog" class="close-btn">✕</button>
        </div>
        
        <!-- Type Filter -->
        <div v-if="entitySearchTypes.length > 1" class="type-filter">
          <button 
            @click="entitySearchTypeFilter = null"
            :class="['type-filter-btn', { 'active': entitySearchTypeFilter === null }]"
            title="Show all types"
          >
            All
          </button>
          <button 
            v-for="type in entitySearchTypes" 
            :key="type"
            @click="entitySearchTypeFilter = type"
            :class="['type-filter-btn', { 'active': entitySearchTypeFilter === type }]"
            :title="`Show only ${type}`"
          >
            {{ getTypeIcon(type) }}
          </button>
        </div>
        
        <div class="modal-body">
          <div class="search-header">
            <input 
              v-model="entitySearchText" 
              type="text" 
              class="search-input" 
              placeholder="Search by name or ID..." 
              autofocus
            />
            <button @click="createNewEntityFromSearch" class="btn-add-entity" title="Create new entity">
              + Add
            </button>
          </div>
          
          <div class="search-results">
            <div 
              v-for="entity in searchableEntities" 
              :key="entity.Id" 
              class="search-result-item"
              @click="selectEntityFromSearch(entity)"
              @mouseenter="showPreview(entity, $event)"
              @mousemove="updatePreviewPosition($event)"
              @mouseleave="hidePreview"
            >
              <div class="result-header">
                <span class="result-icon">{{ getTypeIcon(entity.entityType) }}</span>
                <div class="result-name">{{ entity.Name || entity.Id }}</div>
              </div>
            </div>
            
            <div v-if="searchableEntities.length === 0" class="no-results">
              No entities found
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Preview Tooltip -->
    <div 
      v-if="previewEntity" 
      class="preview-tooltip"
      :style="{ left: previewX + 'px', top: previewY + 'px' }"
    >
      <div class="preview-body">
        <div v-for="(value, key) in previewData" :key="key" class="preview-field">
          <span class="preview-key">{{ key }}:</span>
          <span class="preview-value">{{ value }}</span>
        </div>
      </div>
    </div>

    <!-- Input Dialog -->
    <div v-if="showInputDialog" class="modal-overlay">
      <div class="modal-dialog modal-dialog-small">
        <div class="modal-header">
          <h3>{{ inputDialogTitle }}</h3>
          <button @click="closeInputDialog" class="close-btn">✕</button>
        </div>
        
        <div class="modal-body">
          <input 
            v-model="inputDialogValue" 
            type="text" 
            class="search-input" 
            :placeholder="inputDialogPlaceholder"
            @keyup.enter="submitInputDialog"
            @keyup.esc="closeInputDialog"
            autofocus
          />
          <input 
            v-if="inputDialogPlaceholder2"
            v-model="inputDialogValue2" 
            type="text" 
            class="search-input" 
            :placeholder="inputDialogPlaceholder2"
            @keyup.enter="submitInputDialog"
            @keyup.esc="closeInputDialog"
            style="margin-top: 8px;"
          />
          
          <div class="dialog-actions">
            <button @click="closeInputDialog" class="btn-secondary">Cancel</button>
            <button @click="submitInputDialog" class="btn-primary">Add</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modified Unit/Profile Edit Dialog -->
    <div v-if="showModifiedDialog" class="modal-overlay">
      <div class="modal-dialog">
        <div class="modal-header">
          <h3>Edit {{ modifiedDialogType === 'unit' ? 'Modified Unit' : 'Modified Profile' }} - {{ getEntityName(modifiedDialogData?.OptionId) }}</h3>
          <button @click="closeModifiedDialog" class="close-btn">✕</button>
        </div>
        
        <div class="modal-body" v-if="modifiedDialogData">
          <div class="form-group">
            <label>Option</label>
            <a 
              href="#" 
              @click.prevent="closeModifiedDialog(); navigateToEntity(modifiedDialogData.OptionId, 'options')" 
              @mouseenter="showPreviewById(modifiedDialogData.OptionId, $event)"
              @mousemove="updatePreviewPosition($event)"
              @mouseleave="hidePreview"
              class="id-link block"
            >
              {{ getEntityName(modifiedDialogData.OptionId) }}
            </a>
          </div>

          <div class="form-group" v-if="modifiedDialogType === 'unit'">
            <label>Unit ID</label>
            <input v-model="modifiedDialogData.UnitId" class="input-field" readonly />
          </div>

          <div class="form-group" v-if="modifiedDialogType === 'unit'">
            <label>New Unit ID (optional)</label>
            <input v-model="modifiedDialogData.NewUnitId" class="input-field" placeholder="New Unit ID" />
          </div>

          <div class="form-group" v-if="modifiedDialogType === 'unit'">
            <label>New Profile ID (optional)</label>
            <input v-model="modifiedDialogData.NewProfileId" class="input-field" placeholder="New Profile ID" />
          </div>

          <div class="form-group" v-if="modifiedDialogType === 'profile'">
            <label>Profile ID</label>
            <input v-model="modifiedDialogData.ProfileId" class="input-field" readonly />
          </div>

          <div class="form-group" v-if="modifiedDialogType === 'profile'">
            <label>Unit ID</label>
            <input v-model="modifiedDialogData.UnitId" class="input-field" placeholder="Unit ID" />
          </div>

          <div class="form-group">
            <label>Referenced Items to Remove</label>
            <div v-if="!modifiedDialogData.ReferencedItemsToRemove || modifiedDialogData.ReferencedItemsToRemove.length === 0" class="mb-2">
              <button @click="$set(modifiedDialogData, 'ReferencedItemsToRemove', [])" class="small-btn">+ Add Remove List</button>
            </div>
            <div v-else class="displayable-editor">
              <div 
                v-for="(item, idx) in modifiedDialogData.ReferencedItemsToRemove" 
                :key="idx" 
                class="displayable-row"
              >
                <span class="displayable-icon">{{ getEntityIcon(item.Id) }}</span>
                <a 
                  href="#" 
                  @click.prevent="navigateToEntityById(item.Id)" 
                  @mouseenter="showPreviewById(item.Id, $event)"
                  @mousemove="updatePreviewPosition($event)"
                  @mouseleave="hidePreview"
                  class="id-link"
                  style="flex: 0 0 180px"
                >
                  {{ getEntityName(item.Id) }}
                </a>
                <span class="text-gray-400 mx-2" title="Nested refs">{{ (item.Ref && item.Ref.length) ? '🔗 ' + item.Ref.length : '∅' }}</span>
                <button @click="modifiedDialogData.ReferencedItemsToRemove.splice(idx, 1)" class="remove-btn-small">✕</button>
              </div>
              <button @click="openEntitySearchForModified('remove')" class="small-btn mt-2">+ Add Reference</button>
            </div>
          </div>

          <div class="form-group">
            <label>Referenced Items to Add</label>
            <div v-if="!modifiedDialogData.ReferencedItemsToAdd || modifiedDialogData.ReferencedItemsToAdd.length === 0" class="mb-2">
              <button @click="$set(modifiedDialogData, 'ReferencedItemsToAdd', [])" class="small-btn">+ Add Add List</button>
            </div>
            <div v-else class="displayable-editor">
              <div 
                v-for="(item, idx) in modifiedDialogData.ReferencedItemsToAdd" 
                :key="idx" 
                class="displayable-row"
              >
                <span class="displayable-icon">{{ getEntityIcon(item.Id) }}</span>
                <a 
                  href="#" 
                  @click.prevent="navigateToEntityById(item.Id)" 
                  @mouseenter="showPreviewById(item.Id, $event)"
                  @mousemove="updatePreviewPosition($event)"
                  @mouseleave="hidePreview"
                  class="id-link"
                  style="flex: 0 0 180px"
                >
                  {{ getEntityName(item.Id) }}
                </a>
                <span class="text-gray-400 mx-2" title="Nested refs">{{ (item.Ref && item.Ref.length) ? '🔗 ' + item.Ref.length : '∅' }}</span>
                <button @click="modifiedDialogData.ReferencedItemsToAdd.splice(idx, 1)" class="remove-btn-small">✕</button>
              </div>
              <button @click="openEntitySearchForModified('add')" class="small-btn mt-2">+ Add Reference</button>
            </div>
          </div>

          <div class="dialog-actions">
            <button @click="closeModifiedDialog" class="btn-primary">Done</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Custom Confirm/Alert Dialog -->
    <div v-if="showCustomDialog" class="custom-dialog-overlay">
      <div class="custom-dialog">
        <div class="custom-dialog-header">
          <span class="custom-dialog-icon">{{ customDialogType === 'alert' ? '⚠️' : '❓' }}</span>
          <h3 class="custom-dialog-title">{{ customDialogTitle }}</h3>
        </div>
        <div class="custom-dialog-body">
          <p v-for="(line, index) in customDialogMessageLines" :key="index" class="custom-dialog-message">
            {{ line }}
          </p>
        </div>
        <div class="custom-dialog-footer">
          <button 
            v-if="customDialogType === 'confirm'" 
            @click="cancelCustomDialog" 
            class="custom-dialog-btn custom-dialog-btn-cancel"
          >
            Cancel
          </button>
          <button 
            @click="confirmCustomDialog" 
            class="custom-dialog-btn custom-dialog-btn-confirm"
            :class="{ 'btn-single': customDialogType === 'alert' }"
          >
            {{ customDialogType === 'alert' ? 'OK' : customDialogConfirmText }}
          </button>
        </div>
      </div>
    </div>

    <!-- Duplicate Keys Warning Modal -->
    <div v-if="showDuplicateKeysModal" class="modal-overlay">
      <div class="modal-dialog duplicate-keys-dialog">
        <div class="modal-header">
          <h3>⚠️ Duplicate Keys Detected</h3>
          <button @click="showDuplicateKeysModal = false" class="close-btn">✕</button>
        </div>
        <div class="modal-body">
          <p class="duplicate-keys-warning-text">The following duplicate IDs were detected in the loaded data. This will cause rendering errors and data corruption. Please fix them before editing.</p>
          <div class="duplicate-keys-list">
            <div v-for="(item, index) in duplicateKeysList" :key="index" class="duplicate-key-item">
              <span class="duplicate-key-source">{{ item.source }}</span>
              <span class="duplicate-key-id">{{ item.key }}</span>
              <button @click="fixDuplicateId(item, index)" class="fix-duplicate-btn" title="Assign a new unique ID and save">Fix</button>
            </div>
          </div>
          <div class="dialog-actions">
            <button @click="fixAllDuplicateIds" class="btn-danger">Fix All &amp; Save</button>
            <button @click="showDuplicateKeysModal = false" class="btn-primary">Dismiss</button>
          </div>
        </div>
      </div>
    </div>

    <!-- ══════════════════════════════════════════════════════════
         Structure Validation Modal
         ════════════════════════════════════════════════════════== -->
    <div v-if="showStructureValidationModal" class="modal-overlay">
      <div class="modal-dialog structure-validation-dialog">
        <div class="modal-header">
          <h3>🔍 Structure Validation Issues</h3>
          <button @click="showStructureValidationModal = false" class="close-btn">✕</button>
        </div>
        <div class="modal-body">
          <p class="validation-warning-text">The following structure issues were detected. <strong style="color: #ef4444;">Errors</strong> should be fixed to ensure data consistency. <strong style="color: #ffc107;">Warnings</strong> are informational only and indicate potential naming conflicts that won't break functionality.</p>
          <div class="validation-issues-list">
            <div v-for="(issue, index) in structureValidationIssues" :key="index" :class="['validation-issue-item', issue.severity === 'warning' ? 'warning-item' : 'error-item']">
              <div class="validation-issue-header">
                <span class="validation-issue-icon">
                  {{ issue.severity === 'warning' ? '⚠️' : 
                     issue.type === 'duplicate' ? '❌' : 
                     issue.type === 'cross-faction' ? '📁' : 
                     issue.type === 'missing-required-files' ? '📄' : 
                     issue.type === 'schema-validation' ? '📋' :
                     '🔄' }}
                </span>
                <span class="validation-issue-type">
                  {{ issue.severity === 'warning' ? 'WARNING: ' : '' }}
                  {{ issue.type === 'duplicate' ? 'Duplicate ID' : 
                     issue.type === 'cross-faction' ? 'Cross-Faction Reference' : 
                     issue.type === 'missing-required-files' ? 'Missing Required Files' :
                     issue.type === 'schema-validation' ? 'Schema Validation Error' :
                     issue.type === 'duplicate-option-unit' ? 'Duplicate Unit Option Association' :
                     'Duplicate Profile Option Association' }}
                </span>
              </div>
              <div class="validation-issue-body">
                <!-- Schema validation errors -->
                <template v-if="issue.type === 'schema-validation'">
                  <div class="validation-issue-detail">
                    <strong>Entity:</strong> <code>{{ issue.entityId }}</code>
                    <span v-if="issue.dataType" class="data-type-badge">{{ issue.dataType }}</span>
                  </div>
                  <div class="validation-issue-detail">
                    <strong>Location:</strong> {{ issue.location }}
                  </div>
                  <div class="validation-issue-detail">
                    <strong>Problems:</strong>
                  </div>
                  <ul class="validation-issue-locations">
                    <li v-for="(problem, probIdx) in issue.problems" :key="probIdx">
                      {{ problem }}
                    </li>
                  </ul>
                  <div class="validation-issue-suggestion">
                    <strong>💡 Suggestion:</strong> These fields are required by the JSON schema specification. Please ensure all required fields are present and have the correct data types.
                  </div>
                </template>

                <!-- Missing required files issue -->
                <template v-if="issue.type === 'missing-required-files'">
                  <div class="validation-issue-detail">
                    <strong>⚠️ Critical:</strong> The following required files are missing from the root directory:
                  </div>
                  <ul class="validation-issue-locations">
                    <li v-for="(file, fileIdx) in issue.files" :key="fileIdx">
                      <code>{{ file }}</code>
                    </li>
                  </ul>
                  <div class="validation-issue-suggestion">
                    <strong>💡 Suggestion:</strong> These files are mandatory for the game to function properly. Please create them before continuing. You can use the Setup Wizard to initialize the basic structure.
                  </div>
                </template>

                <!-- Standard duplicate/cross-faction issues -->
                <template v-else-if="issue.type === 'duplicate' || issue.type === 'cross-faction'">
                  <div class="validation-issue-detail">
                    <strong>ID:</strong> <code>{{ issue.id }}</code>
                  </div>
                  <div v-if="issue.dataTypes" class="validation-issue-detail">
                    <strong>Data Types:</strong> <span v-for="(dt, dtIdx) in issue.dataTypes" :key="dtIdx" class="data-type-badge">{{ dt }}</span>
                  </div>
                  <div v-if="issue.reason" class="validation-issue-detail">
                    <strong>Reason:</strong> {{ issue.reason }}
                  </div>
                  <div class="validation-issue-detail">
                    <strong>{{ issue.type === 'duplicate' ? 'Found in:' : 'Referenced by:' }}</strong>
                  </div>
                  <ul class="validation-issue-locations">
                    <li v-for="(location, locIdx) in issue.locations" :key="locIdx">
                      {{ location }}
                    </li>
                  </ul>
                  <div v-if="issue.type === 'cross-faction'" class="validation-issue-suggestion">
                    <strong>💡 Suggestion:</strong> This {{ issue.dataType }} is referenced by multiple factions. Consider moving it to the root (game-level) {{ issue.dataType }}s folder to make it shared across all factions.
                  </div>
                  <div v-if="issue.severity === 'warning'" class="validation-issue-suggestion" style="background: rgba(255, 193, 7, 0.1); border-color: rgba(255, 193, 7, 0.3);">
                    <strong>ℹ️ Note:</strong> This is a warning only. The game will function correctly, but using the same ID for different independent entity types may cause confusion during development.
                  </div>
                </template>

                <!-- Duplicate option association issues -->
                <template v-else-if="issue.type === 'duplicate-option-unit' || issue.type === 'duplicate-option-profile'">
                  <div class="validation-issue-detail">
                    <strong>Faction:</strong> <code>{{ issue.factionName }}</code>
                  </div>
                  <div class="validation-issue-detail">
                    <strong>Duplicate Associations:</strong>
                  </div>
                  <ul class="validation-issue-locations">
                    <li v-for="(dup, dupIdx) in issue.duplicates" :key="dupIdx">
                      <template v-if="issue.type === 'duplicate-option-unit'">
                        Unit: <code>{{ dup.UnitId }}</code> + Option: <code>{{ dup.OptionId }}</code>
                      </template>
                      <template v-else>
                        Profile: <code>{{ dup.ProfileId }}</code> + Option: <code>{{ dup.OptionId }}</code>
                      </template>
                    </li>
                  </ul>
                  <div class="validation-issue-suggestion">
                    <strong>💡 Suggestion:</strong> The same {{ issue.type === 'duplicate-option-unit' ? 'unit' : 'profile' }} has the same option linked multiple times. Only one association per option should exist.
                  </div>
                </template>

                <div v-if="issue.type !== 'missing-required-files' && issue.type !== 'schema-validation' && issue.severity !== 'warning'" class="validation-issue-actions">
                  <button 
                    @click="fixStructureIssue(issue, index)" 
                    class="fix-structure-btn"
                    :title="issue.type === 'duplicate' ? 'Keep one in root file and remove duplicates' : 
                            issue.type === 'cross-faction' ? 'Move to root (game-level) file' : 
                            'Remove duplicate option associations'"
                  >
                    <span v-if="issue.type === 'duplicate'">🔧 Fix Duplicate</span>
                    <span v-else-if="issue.type === 'cross-faction'">📁 Move to Root</span>
                    <span v-else>🔧 Remove Duplicates</span>
                  </button>
                </div>
                <div v-else-if="issue.type === 'missing-required-files'" class="validation-issue-actions">
                  <p class="text-sm text-gray-300" style="font-style: italic;">Manual action required - these files must be created</p>
                </div>
                <div v-else-if="issue.type === 'schema-validation'" class="validation-issue-actions">
                  <p class="text-sm text-gray-300" style="font-style: italic;">Manual action required - edit the entity to fix schema violations</p>
                </div>
                <div v-else-if="issue.severity === 'warning'" class="validation-issue-actions">
                  <p class="text-sm text-gray-300" style="font-style: italic; color: #ffc107;">No automatic fix available - for informational purposes only</p>
                </div>
              </div>
            </div>
          </div>
          <div class="dialog-actions">
            <button @click="fixAllStructureIssues" class="btn-danger">Fix All Issues</button>
            <button @click="showStructureValidationModal = false" class="btn-primary">Dismiss</button>
          </div>
        </div>
      </div>
    </div>

    <!-- ══════════════════════════════════════════════════════════
         Advanced Duplicate Resolution Dialog
         ════════════════════════════════════════════════════════== -->
    <div v-if="showDuplicateResolutionDialog" class="modal-overlay">
      <div class="modal-dialog" style="max-width: 800px;">
        <div class="dialog-header">
          <h2>⚠️ Resolve Duplicate ID Conflict</h2>
          <button @click="showDuplicateResolutionDialog = false" class="close-btn">✕</button>
        </div>
        <div class="dialog-body" style="max-height: 600px; overflow-y: auto;">
          <div class="duplicate-resolution-info">
            <div class="info-block" style="background: rgba(255, 193, 7, 0.15); border-left: 4px solid #ffc107; padding: 15px; margin-bottom: 20px;">
              <strong>⚠️ Different Entities Found</strong>
              <p style="margin: 10px 0 0 0; color: #e0e0e0;">
                Multiple entities with ID <code style="background: rgba(255,255,255,0.1); padding: 2px 6px; border-radius: 3px;">{{ duplicateResolutionData.issue?.id }}</code> 
                were found, but they have <strong>different content</strong>.
              </p>
              <p style="margin: 10px 0 0 0; color: #e0e0e0;">
                Choose how you want to resolve this conflict:
              </p>
            </div>
            
            <!-- Resolution Strategy -->
            <div style="margin-bottom: 25px;">
              <label style="display: block; margin-bottom: 10px; font-weight: bold;">Resolution Strategy:</label>
              <div style="display: flex; gap: 10px;">
                <label style="flex: 1; cursor: pointer; padding: 15px; border: 2px solid #444; border-radius: 8px; background: rgba(255,255,255,0.05);"
                       :style="duplicateResolutionData.action === 'rename-others' ? 'border-color: #4CAF50; background: rgba(76, 175, 80, 0.15);' : ''">
                  <input type="radio" v-model="duplicateResolutionData.action" value="rename-others" style="margin-right: 8px;">
                  <strong>🔧 Rename Others</strong>
                  <p style="margin: 5px 0 0 24px; font-size: 0.9em; color: #aaa;">
                    Keep the selected entity with original ID, generate unique IDs for others, and update all references automatically.
                  </p>
                </label>
                
                <label style="flex: 1; cursor: pointer; padding: 15px; border: 2px solid #444; border-radius: 8px; background: rgba(255,255,255,0.05);"
                       :style="duplicateResolutionData.action === 'unify' ? 'border-color: #4CAF50; background: rgba(76, 175, 80, 0.15);' : ''">
                  <input type="radio" v-model="duplicateResolutionData.action" value="unify" style="margin-right: 8px;">
                  <strong>🔀 Unify</strong>
                  <p style="margin: 5px 0 0 24px; font-size: 0.9em; color: #aaa;">
                    Keep only the selected entity and delete all others. ⚠️ May cause data loss if entities are genuinely different.
                  </p>
                </label>
              </div>
            </div>
            
            <!-- Entity Selection -->
            <div>
              <label style="display: block; margin-bottom: 10px; font-weight: bold;">Select Entity to Keep:</label>
              <div style="display: flex; flex-direction: column; gap: 10px;">
                <div 
                  v-for="(entity, index) in duplicateResolutionData.entities" 
                  :key="index"
                  @click="duplicateResolutionData.selectedEntityIndex = index"
                  style="cursor: pointer; padding: 15px; border: 2px solid #444; border-radius: 8px; background: rgba(255,255,255,0.05); transition: all 0.2s;"
                  :style="duplicateResolutionData.selectedEntityIndex === index ? 'border-color: #4CAF50; background: rgba(76, 175, 80, 0.15);' : ''"
                >
                  <div style="display: flex; align-items: center; justify-content: space-between;">
                    <div style="flex: 1;">
                      <div style="display: flex; align-items: center; gap: 10px; margin-bottom: 8px;">
                        <span style="font-size: 1.3em;">{{ duplicateResolutionData.selectedEntityIndex === index ? '✅' : '⚪' }}</span>
                        <strong style="font-size: 1.1em;">{{ entity.Name || entity.Id }}</strong>
                        <span v-if="duplicateResolutionData.issue?.locations" class="data-type-badge" style="font-size: 0.85em;">
                          {{ duplicateResolutionData.issue.locations[index] }}
                        </span>
                      </div>
                      <div style="margin-left: 35px; color: #aaa; font-size: 0.9em;">
                        <div v-if="entity.Type"><strong>Type:</strong> {{ entity.Type }}</div>
                        <div v-if="entity.Attributes"><strong>Attributes:</strong> {{ Object.keys(entity.Attributes).join(', ') }}</div>
                        <div v-if="entity.Formation"><strong>Formation:</strong> {{ entity.Formation }}</div>
                        <div v-if="entity.BaseId"><strong>Base ID:</strong> {{ entity.BaseId }}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div v-if="duplicateResolutionData.action === 'rename-others'" class="info-block" style="background: rgba(33, 150, 243, 0.15); border-left: 4px solid #2196F3; padding: 15px; margin-top: 20px;">
              <strong>ℹ️ What will happen:</strong>
              <ul style="margin: 10px 0 0 20px; color: #e0e0e0;">
                <li>The selected entity will keep the original ID: <code style="background: rgba(255,255,255,0.1); padding: 2px 6px; border-radius: 3px;">{{ duplicateResolutionData.issue?.id }}</code></li>
                <li>Other entities will receive new unique IDs (e.g., <code style="background: rgba(255,255,255,0.1); padding: 2px 6px; border-radius: 3px;">{{ duplicateResolutionData.issue?.id }}_2</code>, <code style="background: rgba(255,255,255,0.1); padding: 2px 6px; border-radius: 3px;">{{ duplicateResolutionData.issue?.id }}_3</code>)</li>
                <li><strong>All references will be automatically updated</strong> in Units, Profiles, and other entities</li>
              </ul>
            </div>
            
            <div v-if="duplicateResolutionData.action === 'unify'" class="info-block" style="background: rgba(244, 67, 54, 0.15); border-left: 4px solid #f44336; padding: 15px; margin-top: 20px;">
              <strong>⚠️ Warning:</strong>
              <ul style="margin: 10px 0 0 20px; color: #e0e0e0;">
                <li>Only the selected entity will be kept</li>
                <li><strong>All other entities will be permanently deleted</strong></li>
                <li>If the entities have different data, information will be lost</li>
              </ul>
            </div>
          </div>
        </div>
        <div class="dialog-actions">
          <button @click="applyDuplicateResolution" class="btn-danger">
            {{ duplicateResolutionData.action === 'rename-others' ? '🔧 Apply Resolution' : '🔀 Unify Entities' }}
          </button>
          <button @click="showDuplicateResolutionDialog = false" class="btn-primary">Cancel</button>
        </div>
      </div>
    </div>

    <!-- ══════════════════════════════════════════════════════════
         Setup Wizard Modal
         ════════════════════════════════════════════════════════== -->
    <div v-if="showSetupWizard" class="wizard-overlay">
      <div class="wizard-dialog">

        <!-- Wizard Header -->
        <div class="wizard-header">
          <div class="wizard-header-left">
            <span class="wizard-icon">🧙</span>
            <div>
              <h2 class="wizard-title">Game Setup Wizard</h2>
              <p class="wizard-subtitle">Step {{ wizardStep }} of {{ wizardTotalSteps }}</p>
            </div>
          </div>
          <button @click="closeWizard" class="wizard-close-btn">✕</button>
        </div>

        <!-- Step Progress -->
        <div class="wizard-progress">
          <div
            v-for="n in wizardTotalSteps" :key="n"
            :class="['wizard-progress-dot', n < wizardStep ? 'done' : n === wizardStep ? 'active' : '']"
            :title="wizardStepLabel(n)"
          ></div>
        </div>

        <!-- ── Step 1: Base Sizes ── -->
        <div v-if="wizardStep === 1" class="wizard-body">
          <div class="wizard-step-header">
            <span class="step-icon">📐</span>
            <div>
              <h3 class="step-title">Base Sizes</h3>
              <p class="step-desc">Define the base sizes available in your game. Each base needs a name, a shape, and dimensions in mm.</p>
            </div>
          </div>

          <div class="wizard-list">
            <div v-for="(base, i) in wizardBases" :key="i" class="wizard-row">
              <input v-model="base.name" class="wiz-input" placeholder="e.g. 25mm Round" style="flex:2" />
              <select v-model="base.shape" class="wiz-select" style="flex:1">
                <option value="Round">Round</option>
                <option value="Square">Square</option>
              </select>
              <input v-model.number="base.width"  type="number" class="wiz-input-sm" placeholder="W (mm)" />
              <input v-model.number="base.height" type="number" class="wiz-input-sm" placeholder="H (mm)" />
              <button @click="wizardBases.splice(i, 1)" class="wiz-remove-btn">✕</button>
            </div>
          </div>
          <button @click="wizardAddBase" class="wiz-add-btn">+ Add Base Size</button>
        </div>

        <!-- ── Step 2: Factions ── -->
        <div v-if="wizardStep === 2" class="wizard-body">
          <div class="wizard-step-header">
            <span class="step-icon">🏰</span>
            <div>
              <h3 class="step-title">Factions</h3>
              <p class="step-desc">List all factions (armies) that will be available in the game. Each faction gets its own data folder.</p>
            </div>
          </div>

          <div class="wizard-list">
            <div v-for="(f, i) in wizardFactions" :key="i" class="wizard-row">
              <input v-model="f.name" class="wiz-input" placeholder="Faction name, e.g. Space Marines" />
              <button @click="wizardFactions.splice(i, 1)" class="wiz-remove-btn">✕</button>
            </div>
          </div>
          <button @click="wizardFactions.push({ name: '', subsections: [] })" class="wiz-add-btn">+ Add Faction</button>
        </div>

        <!-- ── Step 3: Subsections ── -->
        <div v-if="wizardStep === 3" class="wizard-body">
          <div class="wizard-step-header">
            <span class="step-icon">📁</span>
            <div>
              <h3 class="step-title">Subsections</h3>
              <p class="step-desc">Add categories shared across all factions (e.g. HQ, Troops, Elites). Leave empty to skip subsections.</p>
            </div>
          </div>

          <div class="wizard-list">
            <div v-for="(sub, si) in wizardSubsections" :key="si" class="wizard-row">
              <input v-model="sub.name" class="wiz-input" placeholder="Subsection name, e.g. HQ" />
              <button @click="wizardSubsections.splice(si, 1)" class="wiz-remove-btn">✕</button>
            </div>
          </div>
          <button @click="wizardSubsections.push({ name: '' })" class="wiz-add-btn">+ Add Subsection</button>
        </div>

        <!-- ── Step 4 (optional): Unit Names ── -->
        <div v-if="wizardStep === 4" class="wizard-body">
          <div class="wizard-step-header">
            <span class="step-icon">🧑‍🤝‍🧑</span>
            <div>
              <h3 class="step-title">Unit Names <span class="optional-tag">Optional</span></h3>
              <p class="step-desc">Pre-populate unit names for each faction / subsection. You can add more later in the editor.</p>
            </div>
          </div>

          <div v-for="(f, fi) in wizardFactions" :key="fi" class="wizard-faction-block">
            <h4 class="wizard-faction-name">{{ f.name || '(unnamed faction)' }}</h4>

            <!-- Faction-level units (no subsection) -->
            <div v-if="f.subsections.length === 0" class="wizard-subsection-block">
              <div class="wizard-list">
                <div v-for="(u, ui) in f.units" :key="ui" class="wizard-row">
                  <input v-model="u.name" class="wiz-input" placeholder="Unit name" />
                  <button @click="f.units.splice(ui, 1)" class="wiz-remove-btn">✕</button>
                </div>
              </div>
              <button @click="wizardEnsureUnits(f); f.units.push({ name: '' })" class="wiz-add-btn">+ Add Unit</button>
            </div>

            <!-- Units per subsection -->
            <div v-for="(sub, si) in f.subsections" :key="si" class="wizard-subsection-block">
              <span class="wizard-subsection-label">{{ sub.name || '(unnamed subsection)' }}</span>
              <div class="wizard-list">
                <div v-for="(u, ui) in sub.units" :key="ui" class="wizard-row">
                  <input v-model="u.name" class="wiz-input" placeholder="Unit name" />
                  <button @click="sub.units.splice(ui, 1)" class="wiz-remove-btn">✕</button>
                </div>
              </div>
              <button @click="sub.units.push({ name: '' })" class="wiz-add-btn">+ Add Unit</button>
            </div>
          </div>
        </div>

        <!-- ── Step 5: Statline Templates ── -->
        <div v-if="wizardStep === 5" class="wizard-body">
          <div class="wizard-step-header">
            <span class="step-icon">📊</span>
            <div>
              <h3 class="step-title">Statline Templates</h3>
              <p class="step-desc">Create multiple statline templates, each with a unique name and stat keys. When creating new statlines, you'll choose which template to use.</p>
            </div>
          </div>

          <div v-for="(template, tIdx) in wizardStatTemplates" :key="tIdx" class="wizard-template-group">
            <div class="wizard-template-header">
              <input v-model="template.name" class="wiz-input wiz-template-name" placeholder="Template name (e.g. Infantry, Cavalry)" />
              <button @click="wizardStatTemplates.splice(tIdx, 1)" class="wiz-remove-btn">✕ Remove Template</button>
            </div>
            
            <div class="wizard-list" style="margin-left: 20px;">
              <div v-for="(stat, sIdx) in template.stats" :key="sIdx" class="wizard-row">
                <input v-model="stat.key" class="wiz-input" placeholder="Stat key (e.g. Move)" style="flex: 1;" />
                <input v-model="stat.value" class="wiz-input" placeholder="Default value (optional)" style="flex: 1; margin-left: 8px;" />
                <button @click="template.stats.splice(sIdx, 1)" class="wiz-remove-btn">✕</button>
              </div>
            </div>
            <button @click="template.stats.push({ key: '', value: '' })" class="wiz-add-btn" style="margin-left: 20px;">+ Add Stat</button>
          </div>

          <button @click="wizardStatTemplates.push({ name: '', stats: [{ key: '', value: '' }] })" class="wiz-add-btn" style="margin-top: 16px;">+ Add Template</button>
        </div>

        <!-- Wizard Footer -->
        <div class="wizard-footer">
          <button v-if="wizardStep === 4" @click="wizardStep = 5" class="wiz-skip-btn">Skip this step →</button>
          <button v-if="wizardStep > 1" @click="wizardStep--" class="wiz-back-btn">← Back</button>
          <button
            v-if="wizardStep < wizardTotalSteps"
            @click="wizardNext"
            class="wiz-next-btn"
          >Next →</button>
          <button
            v-if="wizardStep === wizardTotalSteps"
            @click="applyWizard"
            class="wiz-apply-btn"
          >✅ Apply Setup</button>
        </div>

      </div>
    </div>

    <!-- Git Upload Modal -->
    <div v-if="showGitModal" class="wizard-overlay">
      <div class="git-modal-dialog">

        <!-- Header -->
        <div class="git-modal-header">
          <div class="git-modal-header-left">
            <svg class="git-modal-gh-icon" viewBox="0 0 16 16" fill="currentColor"><path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.013 8.013 0 0016 8c0-4.42-3.58-8-8-8z"/></svg>
            <div>
              <h2 class="git-modal-title">Upload to GitHub</h2>
              <p class="git-modal-subtitle">Push all in-memory data files to your repository</p>
            </div>
          </div>
          <button @click="showGitModal = false" class="wizard-close-btn">✕</button>
        </div>

        <!-- Body -->
        <div class="git-modal-body">
          <p class="git-modal-desc">
            You need a public GitHub repository to host your game files.
            <a href="https://docs.github.com/en/repositories/creating-and-managing-repositories/creating-a-new-repository" target="_blank" rel="noopener">How to create one</a>
          </p>

          <div class="git-modal-field">
            <label class="git-modal-label">GitHub Repository</label>
            <input v-model="gitRepo" type="text" class="git-modal-input" placeholder="owner/repo or https://github.com/owner/repo" />
            <p v-if="gitRepoError" class="git-modal-error">{{ gitRepoError }}</p>
          </div>

          <div class="git-modal-field">
            <div class="git-modal-label-row">
              <label class="git-modal-label">Personal Access Token</label>
              <button @click="showGitPatHelp = !showGitPatHelp" class="git-help-btn" type="button">?</button>
            </div>
            <div v-if="showGitPatHelp" class="git-help-box">
              <ol>
                <li>Go to <a href="https://github.com/settings/tokens" target="_blank" rel="noopener">github.com/settings/tokens</a></li>
                <li>Click <strong>"Generate new token (classic)"</strong></li>
                <li>Give it a name, set expiry, and tick <code>public_repo</code> (or <code>repo</code> for private repos)</li>
                <li>Click <strong>Generate token</strong> — copy it immediately</li>
              </ol>
            </div>
            <input v-model="gitToken" type="password" class="git-modal-input" placeholder="ghp_…" autocomplete="off" />
            <p v-if="gitTokenError" class="git-modal-error">{{ gitTokenError }}</p>
          </div>

          <div class="git-modal-field">
            <label class="git-modal-label">Subdirectory in repository <span class="git-modal-optional">(optional)</span></label>
            <input v-model="gitSubdir" type="text" class="git-modal-input" placeholder="e.g. MyGame  (leave blank for repo root)" />
            <p class="git-modal-hint">Files will be placed at <code>{{ gitSubdir.trim() ? gitSubdir.trim() + '/' : '' }}&lt;filename&gt;</code></p>
          </div>

          <!-- Progress -->
          <div v-if="gitUploading" class="git-progress-wrap">
            <div class="git-progress-track">
              <div class="git-progress-fill" :style="{ width: gitUploadTotal > 0 ? Math.round((gitUploadProgress / gitUploadTotal) * 100) + '%' : '0%' }"></div>
            </div>
            <span class="git-progress-label">{{ gitUploadProgress }} / {{ gitUploadTotal }} files</span>
          </div>

          <div v-if="gitSuccess" class="git-message git-message--success">
            {{ gitSuccess }}
            <a v-if="gitFolderUrl" :href="gitFolderUrl" target="_blank" rel="noopener" class="git-folder-link">View on GitHub →</a>
          </div>
          <div v-if="gitError" class="git-message git-message--error">{{ gitError }}</div>
        </div>

        <!-- Footer -->
        <div class="git-modal-footer">
          <button @click="showGitModal = false" class="git-cancel-btn">Cancel</button>
          <button @click="uploadToGitHub" :disabled="gitUploading" class="git-upload-btn">
            <span v-if="gitUploading" class="spinner-inline"></span>
            {{ gitUploading ? 'Uploading…' : 'Upload to GitHub' }}
          </button>
        </div>

      </div>
    </div>

    <!-- Git Sync Status Badge -->
    <div :class="['floating-git-status', hasUnsavedChanges ? 'has-changes' : 'no-changes']" @click="hasUnsavedChanges && openGitModal()">
      <span class="git-status-icon">{{ hasUnsavedChanges ? '⚠️' : '✓' }}</span>
      <div class="git-status-text">
        <span class="git-status-primary">{{ hasUnsavedChanges ? 'Unpublished changes' : 'In sync with Git' }}</span>
        <span class="git-status-version">based on v{{ gitBaseVersion }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { wds3Cache } from '@/utils/wds3Cache';
import TokensEditor from '@/components/TokensEditor.vue';
import TerrainEditor from '@/components/TerrainEditor.vue';
import DeploymentsEditor from '@/components/DeploymentsEditor.vue';
import ObjectivesEditor from '@/components/ObjectivesEditor.vue';
export default {
  props: ['gameName', 'factionName'],
  components: { TokensEditor, TerrainEditor, DeploymentsEditor, ObjectivesEditor },
  data() {
    return {
      editorMode: 'game', // 'game' or 'faction'
      selectedType: 'rules',
      selectedFaction: null,
      previousFaction: null, // Track previous faction for revert on cancel
      selectedEntity: null,
      searchText: '',
      draggedIndex: null,
      draggedKey: null,
      draggedFieldName: null,
      
      // Entity search dialog
      showEntitySearchDialog: false,
      entitySearchText: '',
      entitySearchCallback: null,
      entitySearchTypes: [],
      entitySearchTypeFilter: null, // null = show all types
      entitySearchFilterIds: null, // null = show all, or array of IDs to filter by
      entitySearchOriginalEntity: null, // Store original entity for linking when creating new entities
      
      // Preview tooltip
      previewEntity: null,
      previewX: 0,
      previewY: 0,
      
      // Input dialog
      showInputDialog: false,
      inputDialogTitle: '',
      inputDialogPlaceholder: '',
      inputDialogValue: '',
      inputDialogPlaceholder2: '',
      inputDialogValue2: '',
      inputDialogCallback: null,
      
      // Modified unit/profile dialog
      showModifiedDialog: false,
      modifiedDialogType: null, // 'unit' or 'profile'
      modifiedDialogData: null,
      
      // Custom confirm/alert dialog
      showCustomDialog: false,
      customDialogType: null, // 'confirm' or 'alert'
      customDialogTitle: '',
      customDialogMessage: '',
      customDialogConfirmText: 'OK',
      customDialogResolve: null,
      
      // Change tracking (unpublished to Git)
      saveDebounceTimer: null,
      hasUnsavedChanges: false,
      isInitialLoad: true, // Flag to prevent tracking changes during initial load
      dirtyGameFiles: {}, // Track which game-level files need saving
      dirtyFactionFiles: {}, // Track dirty files per faction: { "Necrons": { "units.json": true }, ... }
      gitBaseVersion: null, // Manifest version loaded from Git — changes are "unpublished" until next upload

      // ── Git Upload Modal ──
      showGitModal: false,
      gitRepo: '',
      gitRepoError: '',
      gitToken: '',
      gitTokenError: '',
      gitSubdir: '',
      gitUploading: false,
      gitUploadProgress: 0,
      gitUploadTotal: 0,
      gitSuccess: '',
      gitError: '',
      gitFolderUrl: '',
      showGitPatHelp: false,

      // ── Setup Wizard ──
      // Profile skin thumbnails
      profileThumbnailMap: {}, // profileId -> thumbnailUrl (null = no skin)
      
      showSetupWizard: false,
      wizardStep: 1,
      wizardTotalSteps: 5,
      wizardBases: [],          // [{ name, shape, width, height }]
      wizardFactions: [],       // [{ name, subsections: [{ name, units: [{ name }] }], units: [{ name }] }]
      wizardSubsections: [],    // [{ name }] — global, applied to all factions
      wizardStatTemplate: [],   // [{ key, value }] — legacy, kept for backward compatibility
      wizardStatTemplates: [],  // [{ name, stats: [{ key, value }] }] — multiple named templates
      wizardStatAttrsTemplate: null, // { Move: '', Attack: '', ... } — applied to new statlines

      factions: [],
      
      // Game Data
      rules: [],
      cards: [],
      bases: [],
      tokens: [],
      terrains: [],
      deployments: [],
      objectives: [],
      gameOptions: [],
      gameEquipment: [],
      gameStatlines: [],
      gameProfiles: [],
      gameUnits: [],
      gameFormations: [], // [{ Index, Type }] from gameconfig.json
      gameConfigWidth: 72,   // BattlegroundWidth from gameconfig.json
      gameConfigHeight: 48,  // BattlegroundHeight from gameconfig.json

      // Token skins catalogue for ObjectivesEditor (fetched on demand)
      objectiveTokenSkins: [],
      
      // Faction Data
      units: [],
      profiles: [],
      statlines: [],
      equipment: [],
      factionOptions: [],
      
      // Faction-specific rules
      factionRules: [],
      
      // Option mappings
      modifiedUnits: [],
      modifiedProfiles: [],
      
      // Hierarchy
      hierarchy: null,
      hierarchySection: 'Factions', // 'Factions' or 'Cards'
      selectedHierarchyFaction: null,
      selectedHierarchyCategory: null,
      draggedHierarchyItem: null,
      draggedHierarchyType: null, // 'faction', 'category', or 'unit'
      draggedHierarchyFaction: null,
      draggedHierarchyCategory: null,
      collapsedCategories: {}, // Track which categories are collapsed (key: path joined by ':')
      unitNamesCache: {}, // Cache unit names from all factions for hierarchy display
      selectedHierarchyPath: null, // Currently selected path in the unit editor hierarchy dropdown

      // Duplicate keys warning modal
      showDuplicateKeysModal: false,
      duplicateKeysList: [],
      
      // Structure validation modal
      showStructureValidationModal: false,
      structureValidationIssues: [],
      
      // Advanced duplicate resolution dialog
      showDuplicateResolutionDialog: false,
      duplicateResolutionData: {
        issue: null,
        entities: [],
        selectedEntityIndex: 0,
        action: 'rename-others' // 'rename-others', 'unify'
      },
      
      gameDataTypes: [
        { key: 'rules', label: 'Rules', icon: '📜' },
        { key: 'cards', label: 'Cards', icon: '🃏' },
        { key: 'bases', label: 'Bases', icon: '📐' },
        { key: 'tokens', label: 'Tokens', icon: '🟡' },
        { key: 'terrains', label: 'Terrain', icon: '🏔️' },
        { key: 'deployments', label: 'Deployments', icon: '🟩' },
        { key: 'objectives', label: 'Objectives', icon: '🎯' },
        { key: 'options', label: 'Options', icon: '⚙️' },
        { key: 'equipment', label: 'Equipment', icon: '⚔️' },
        { key: 'hierarchy', label: 'Hierarchy', icon: '🌳' },
      ],
      
      factionDataTypes: [
        { key: 'units', label: 'Units', icon: '🧑‍🤝‍🧑' },
        { key: 'profiles', label: 'Profiles', icon: '👤' },
        { key: 'statlines', label: 'Statlines', icon: '📊' },
        { key: 'equipment', label: 'Equipment', icon: '⚔️' },
        { key: 'options', label: 'Options', icon: '⚙️' },
      ],
      
      // Cache for statlines from all factions (loaded on demand)
      statlineCache: {}, // { factionName: [statlines] }
    };
  },
  
  computed: {
    /** Types that render a full-width editor component instead of the entity list + detail panel */
    isFullWidthType() {
      return ['hierarchy', 'tokens', 'terrains', 'deployments', 'objectives'].includes(this.selectedType);
    },

    panelTitle() {
      const titles = {
        hierarchy: 'Hierarchy Editor',
      };
      return titles[this.selectedType] || (this.selectedEntity ? this.selectedEntity.Name || this.selectedEntity.deploymentName || this.selectedEntity.Id : 'Select an item to edit');
    },

    listTitle() {
      const typeLabels = {
        rules: 'Rules',
        cards: 'Cards',
        bases: 'Bases',
        tokens: 'Tokens',
        terrains: 'Terrain',
        deployments: 'Deployments',
        objectives: 'Objectives',
        options: 'Options',
        units: 'Units',
        profiles: 'Profiles',
        statlines: 'Statlines',
        equipment: 'Equipment',
        hierarchy: 'Hierarchy',
      };
      return typeLabels[this.selectedType] || 'Entities';
    },
    
    currentTypeIcon() {
      const typeIcons = {
        rules: '📜',
        cards: '🃏',
        bases: '📐',
        tokens: '🟡',
        terrains: '🏔️',
        deployments: '🟩',
        objectives: '🎯',
        options: '⚙️',
        units: '🧑\u200d🤝\u200d🧑',
        profiles: '👤',
        statlines: '📊',
        equipment: '⚔️',
        hierarchy: '🌳',
      };
      return typeIcons[this.selectedType] || '📄';
    },
    
    currentEntities() {
      const dataMap = {
        rules: this.rules,
        cards: this.cards,
        bases: this.bases,
        tokens: this.tokens,
        terrains: this.terrains,
        deployments: this.deployments,
        objectives: this.objectives,
        options: this.editorMode === 'game' ? this.gameOptions : this.factionOptions,
        units: this.units,
        profiles: this.profiles,
        statlines: this.statlines,
        hierarchy: [], // Hierarchy uses custom UI
        equipment: this.editorMode === 'game' ? this.gameEquipment : this.equipment,
      };
      return dataMap[this.selectedType] || [];
    },
    
    filteredEntities() {
      let entities = this.currentEntities;
      
      // Filter by search text
      if (this.searchText) {
        const search = this.searchText.toLowerCase();
        entities = entities.filter(entity => 
          (entity.Name || entity.deploymentName || '').toLowerCase().includes(search) ||
          (entity.Id || '').toLowerCase().includes(search)
        );
      }
      
      // Sort alphabetically ascending (A to Z)
      return entities.slice().sort((a, b) => {
        const nameA = (a.Name || a.deploymentName || a.Id || '').toLowerCase();
        const nameB = (b.Name || b.deploymentName || b.Id || '').toLowerCase();
        return nameA.localeCompare(nameB); // Ascending order
      });
    },
    
    canAdd() {
      if (this.isFullWidthType) return false;
      if (this.editorMode === 'faction' && !this.selectedFaction) return false;
      return true;
    },

    shouldShowDefaultFront() {
      if (this.selectedType !== 'units' || !this.selectedEntity) return false;
      const formation = this.gameFormations.find(f => f.Index === this.selectedEntity.Formation);
      if (!formation) return true; // Show by default if no formation found
      const formationType = (formation.Type || '').toLowerCase();
      return formationType.includes('rank and file');
    },

    defaultFrontLabel() {
      if (this.selectedType !== 'units' || !this.selectedEntity) return 'Default Front';
      const formation = this.gameFormations.find(f => f.Index === this.selectedEntity.Formation);
      if (!formation) return 'Default Front';
      const formationType = (formation.Type || '').toLowerCase();
      if (formationType.includes('rank and file')) {
        return 'Default Front';
      }
      return 'Starting Model Count';
    },
    
    // Get all statlines: current, game, and cached from other factions
    allCachedStatlines() {
      const statlines = [...this.statlines, ...this.gameStatlines];
      // Add all cached statlines from other factions
      for (const factionName in this.statlineCache) {
        const factionStatlines = this.statlineCache[factionName];
        if (Array.isArray(factionStatlines)) {
          statlines.push(...factionStatlines);
        }
      }
      return statlines;
    },
    
    // Normalize Children to array format for consistent rendering
    normalizedChildren() {
      if (!this.selectedEntity || !this.selectedEntity.Children) {
        return [];
      }
      
      const children = this.selectedEntity.Children;
      
      // If already an array, return as-is
      if (Array.isArray(children)) {
        return children;
      }
      
      // If it's an object (old format: {id: info}), convert to array
      if (typeof children === 'object') {
        return Object.entries(children).map(([id, info]) => ({
          Id: id,
          Info: info || ''
        }));
      }
      
      return [];
    },
    
    // Normalize ItemsToAdd to array format for consistent rendering
    normalizedItemsToAdd() {
      if (!this.selectedEntity || !this.selectedEntity.ItemsToAdd) {
        return [];
      }
      
      const items = this.selectedEntity.ItemsToAdd;
      
      // If already an array, return as-is
      if (Array.isArray(items)) {
        return items;
      }
      
      // If it's an object format, convert to array
      if (typeof items === 'object') {
        return Object.entries(items).map(([id, info]) => ({
          Id: id,
          Info: info || ''
        }));
      }
      
      return [];
    },
    
    previewData() {
      if (!this.previewEntity) return {};
      const data = {};
      
      // Always show ID first if it exists
      if (this.previewEntity.Id) {
        data['Id'] = this.previewEntity.Id;
      }

      // For equipment, show a readable Type (name + id when possible)
      if (this.previewEntity.entityType === 'equipment' && this.previewEntity.Type) {
        const typeId = this.previewEntity.Type;
        const typeName = this.getEntityName(typeId);
        data['Type'] = typeName && typeName !== typeId ? `${typeName} (${typeId})` : typeId;
      }
      
      // For equipment, show inline Statlines data (Values and Rules)
      let hasInlineStatlines = false;
      if (this.previewEntity.entityType === 'equipment' && Array.isArray(this.previewEntity.Statlines)) {
        hasInlineStatlines = true;
        this.previewEntity.Statlines.forEach((statline, idx) => {
          if (!statline) return;
          const prefix = this.previewEntity.Statlines.length > 1 ? `[${idx + 1}] ` : '';
          
          // Show Values (attributes like Str, AP, D, etc.)
          if (statline.Values && typeof statline.Values === 'object') {
            for (const [key, value] of Object.entries(statline.Values)) {
              if (value !== null && value !== undefined && value !== '') {
                data[`${prefix}${key}`] = value;
              }
            }
          }
          
          // Show Rules array (inline rules in the statline)
          if (Array.isArray(statline.Rules) && statline.Rules.length > 0) {
            data[`${prefix}Rules`] = statline.Rules.join(', ');
          }
        });
        
        // Also check for Children with rule references (additional rules)
        if (this.previewEntity.Children) {
          const allRules = [...this.rules, ...this.factionRules];
          let ruleIds = [];
          
          // Handle both array and object formats for Children
          if (Array.isArray(this.previewEntity.Children)) {
            ruleIds = this.previewEntity.Children
              .map(child => this.getChildId(child))
              .filter(id => id && allRules.some(r => r.Id === id));
          } else if (typeof this.previewEntity.Children === 'object') {
            ruleIds = Object.keys(this.previewEntity.Children)
              .filter(id => id && allRules.some(r => r.Id === id));
          }
          
          if (ruleIds.length > 0) {
            const ruleNames = ruleIds.map(ruleId => {
              const rule = allRules.find(r => r.Id === ruleId);
              return rule ? rule.Name : ruleId;
            }).join(', ');
            // Append to existing Rules or create new entry
            if (data['Rules']) {
              data['Rules'] += ', ' + ruleNames;
            } else {
              data['Rules'] = ruleNames;
            }
          }
        }
        
        // Return if we found statline data
        if (Object.keys(data).length > 1) {
          return data;
        }
      }
      
      // Check if entity has Children with statline references
      if (this.previewEntity.Children) {
        const allStatlines = this.allCachedStatlines;
        const allRules = [...this.rules, ...this.factionRules];
        let statlineIds = [];
        
        // Handle both array and object formats for Children
        if (Array.isArray(this.previewEntity.Children)) {
          statlineIds = this.previewEntity.Children
            .map(child => this.getChildId(child))
            .filter(id => id && allStatlines.some(s => s.Id === id));
        } else if (typeof this.previewEntity.Children === 'object') {
          // Object format: {id: nameAdition}
          statlineIds = Object.keys(this.previewEntity.Children)
            .filter(id => id && allStatlines.some(s => s.Id === id));
        }
        
        if (statlineIds.length > 0) {
          
          // Look up and display statline data
          statlineIds.forEach((statlineId, idx) => {
            const statline = allStatlines.find(s => s.Id === statlineId);
            if (statline) {
              const prefix = statlineIds.length > 1 ? `[${idx + 1}] ` : '';
              if (statline.Attributes && typeof statline.Attributes === 'object') {
                for (const [statKey, statValue] of Object.entries(statline.Attributes)) {
                  if (statValue !== null && statValue !== undefined && statValue !== '') {
                    data[`${prefix}${statKey}`] = statValue;
                  }
                }
              }
              
              // Also show rules from the statline's Children (handle both array and object formats)
              if (statline.Children) {
                let ruleIds = [];
                
                if (Array.isArray(statline.Children)) {
                  ruleIds = statline.Children
                    .map(child => this.getChildId(child))
                    .filter(id => id && allRules.some(r => r.Id === id));
                } else if (typeof statline.Children === 'object') {
                  ruleIds = Object.keys(statline.Children)
                    .filter(id => id && allRules.some(r => r.Id === id));
                }
                
                if (ruleIds.length > 0) {
                  const ruleNames = ruleIds.map(ruleId => {
                    const rule = allRules.find(r => r.Id === ruleId);
                    return rule ? rule.Name : ruleId;
                  }).join(', ');
                  data[`${prefix}Rules`] = ruleNames;
                }
              }
            }
          });
          
          // Also check for rule references directly in the entity's Children
          // (not just in the statline's Children)
          let entityRuleIds = [];
          
          if (Array.isArray(this.previewEntity.Children)) {
            entityRuleIds = this.previewEntity.Children
              .map(child => this.getChildId(child))
              .filter(id => id && allRules.some(r => r.Id === id));
          } else if (typeof this.previewEntity.Children === 'object') {
            entityRuleIds = Object.keys(this.previewEntity.Children)
              .filter(id => id && allRules.some(r => r.Id === id));
          }
          
          if (entityRuleIds.length > 0) {
            const entityRuleNames = entityRuleIds.map(ruleId => {
              const rule = allRules.find(r => r.Id === ruleId);
              return rule ? rule.Name : ruleId;
            }).join(', ');
            
            // Append to existing Rules or create new entry
            if (data['Rules']) {
              data['Rules'] += ', ' + entityRuleNames;
            } else {
              data['Rules'] = entityRuleNames;
            }
          }
          
          // If we found statline data, return it (with the ID already added)
          if (Object.keys(data).length > 1) {
            return data;
          }
        }
      }
      
      // Otherwise show regular entity data (but skip Name, Type, and entityType)
      for (const [key, value] of Object.entries(this.previewEntity)) {
        if (key === 'entityType' || key === 'Name' || key === 'Type' || key === 'Id' || key === 'Children') continue;
        if (value === null || value === undefined) continue;
        if (typeof value === 'object' && !Array.isArray(value)) continue;
        if (Array.isArray(value)) {
          data[key] = `[${value.length} items]`;
        } else {
          const str = String(value);
          data[key] = str.length > 50 ? str.substring(0, 50) + '...' : str;
        }
      }
      
      // Show child item names if Children exists
      if (this.previewEntity.Children) {
        const childNames = this.getChildrenNames(this.previewEntity.Children);
        if (childNames.length > 0) {
          data['Children'] = childNames.join(', ');
        }
      }
      
      // Show ItemsToAdd names if it exists (for options)
      if (this.previewEntity.ItemsToAdd) {
        const itemNames = this.getItemsToAddNames(this.previewEntity.ItemsToAdd);
        if (itemNames.length > 0) {
          data['Items to Add'] = itemNames.join(', ');
        }
      }
      
      // Show BasicProfileIds or ProfileIds names if they exist (for units)
      const profileIds = this.previewEntity.BasicProfileIds || this.previewEntity.ProfileIds;
      if (Array.isArray(profileIds) && profileIds.length > 0) {
        const profileNames = profileIds.map(id => this.getEntityName(id)).filter(name => name);
        if (profileNames.length > 0) {
          data['Profiles'] = profileNames.join(', ');
        }
      }
      
      return data;
    },
    
    searchableEntities() {
      if (!this.entitySearchTypes.length) return [];
      
      let entities = [];
      this.entitySearchTypes.forEach(type => {
        const dataMap = {
          rules: this.rules,
          cards: this.cards,
          bases: this.bases,
          tokens: this.tokens,
          options: [...this.gameOptions, ...this.factionOptions],
          units: this.units,
          profiles: this.profiles,
          statlines: this.statlines,
          equipment: this.equipment,
        };
        const data = dataMap[type] || [];
        entities = [...entities, ...data.map(e => ({ ...e, entityType: type }))];
      });
      
      console.log('  - Initial entities count:', entities.length);
      
      // Apply ID filter if set (for filtering displayables from specific unit/profile)
      if (this.entitySearchFilterIds) {
        console.log('  - Applying ID filter:', this.entitySearchFilterIds);
        entities = entities.filter(e => this.entitySearchFilterIds.includes(e.Id));
        console.log('  - After ID filter:', entities.length);
      }
      
      // Apply type filter if set
      if (this.entitySearchTypeFilter) {
        console.log('  - Applying type filter:', this.entitySearchTypeFilter);
        entities = entities.filter(e => e.entityType === this.entitySearchTypeFilter);
        console.log('  - After type filter:', entities.length);
      }
      
      // Apply text search
      if (this.entitySearchText) {
        console.log('  - Applying text search:', this.entitySearchText);
        const search = this.entitySearchText.toLowerCase();
        entities = entities.filter(e => 
          (e.Name || '').toLowerCase().includes(search) ||
          (e.Id || '').toLowerCase().includes(search)
        );
      }
      
      return entities;
    },
    
    customDialogMessageLines() {
      if (!this.customDialogMessage) return [];
      return this.customDialogMessage.split('\n').filter(line => line.trim());
    }
  },
  
  watch: {
    // Track changes to mark specific files as dirty (per faction)
    units: {
      handler() {
        if (!this.isInitialLoad && this.selectedFaction) {
          this.hasUnsavedChanges = true;
          this.markFactionFileDirty('units.json');
        }
      },
      deep: true
    },
    profiles: {
      handler() {
        if (!this.isInitialLoad && this.selectedFaction) {
          this.hasUnsavedChanges = true;
          this.markFactionFileDirty('profiles.json');
        }
      },
      deep: true
    },
    statlines: {
      handler() {
        if (!this.isInitialLoad && this.selectedFaction) {
          this.hasUnsavedChanges = true;
          this.markFactionFileDirty('statlines.json');
        }
      },
      deep: true
    },
    equipment: {
      handler() {
        if (!this.isInitialLoad && this.selectedFaction) {
          this.hasUnsavedChanges = true;
          this.markFactionFileDirty('equipment.json');
        }
      },
      deep: true
    },
    factionOptions: {
      handler() {
        if (!this.isInitialLoad && this.selectedFaction) {
          this.hasUnsavedChanges = true;
          this.markFactionFileDirty('options.json');
        }
      },
      deep: true
    },
    modifiedUnits: {
      handler() {
        if (!this.isInitialLoad && this.selectedFaction) {
          this.hasUnsavedChanges = true;
          this.markFactionFileDirty('modifiedunit.json');
        }
      },
      deep: true
    },
    modifiedProfiles: {
      handler() {
        if (!this.isInitialLoad && this.selectedFaction) {
          this.hasUnsavedChanges = true;
          this.markFactionFileDirty('modifiedprofile.json');
        }
      },
      deep: true
    },
    hierarchy: {
      handler() {
        if (!this.isInitialLoad) {
          this.hasUnsavedChanges = true;
          this.$set(this.dirtyGameFiles, 'hierarchy.json', true);
        }
      },
      deep: true
    },
    rules: {
      handler() {
        if (!this.isInitialLoad) {
          this.hasUnsavedChanges = true;
          this.$set(this.dirtyGameFiles, 'rules.json', true);
        }
      },
      deep: true
    },
    cards: {
      handler() {
        if (!this.isInitialLoad) {
          this.hasUnsavedChanges = true;
          this.$set(this.dirtyGameFiles, 'cards.json', true);
        }
      },
      deep: true
    },
    bases: {
      handler() {
        if (!this.isInitialLoad) {
          this.hasUnsavedChanges = true;
          this.$set(this.dirtyGameFiles, 'bases.json', true);
        }
      },
      deep: true
    },
    tokens: {
      handler() {
        if (!this.isInitialLoad) {
          this.hasUnsavedChanges = true;
          this.$set(this.dirtyGameFiles, 'tokens.json', true);
        }
      },
      deep: true
    },
    terrains: {
      handler() {
        if (!this.isInitialLoad) {
          this.hasUnsavedChanges = true;
          this.$set(this.dirtyGameFiles, 'terrains.json', true);
        }
      },
      deep: true
    },
    deployments: {
      handler() {
        if (!this.isInitialLoad) {
          this.hasUnsavedChanges = true;
          this.$set(this.dirtyGameFiles, 'deployments.json', true);
        }
      },
      deep: true
    },
    objectives: {
      handler() {
        if (!this.isInitialLoad) {
          this.hasUnsavedChanges = true;
          this.$set(this.dirtyGameFiles, 'objectives.json', true);
        }
      },
      deep: true
    },
    gameOptions: {
      handler() {
        if (!this.isInitialLoad) {
          this.hasUnsavedChanges = true;
          this.$set(this.dirtyGameFiles, 'options.json', true);
        }
      },
      deep: true
    },
    
    // Load statlines on-demand when previewEntity changes
    async previewEntity() {
      if (this.previewEntity && this.previewEntity.Children) {
        const children = Array.isArray(this.previewEntity.Children) 
          ? this.previewEntity.Children 
          : this.previewEntity.Children.Children || [];
        
        for (const child of children) {
          const statlineId = child.StatlineId || (child.Children && child.Children.StatlineId);
          if (statlineId) {
            // Trigger async load for any statline that's not in current/game collections
            if (!this.statlines.find(s => s.Id === statlineId) && 
                !this.gameStatlines.find(s => s.Id === statlineId)) {
              // Load from other factions (will be cached)
              this.getStatlineFromAllFactions(statlineId).catch(err => {
                console.warn(`Failed to load statline ${statlineId}:`, err);
              });
            }
          }
        }
      }
    }
  },
  
  async mounted() {
    await this.$nextTick();

    // Build faction list from cache, using the files object as the "handle"
    this.factions = wds3Cache.factions.map(f => ({
      name: f.name,
      handle: f.files,   // plain object: { 'units.json': [...], ... }
    }));

    await this.loadGameData();

    if (this.factions.length > 0) {
      this.selectedFaction = this.factions[0];
      this.previousFaction = this.selectedFaction;
      await this.loadFactionData();
    }

    await this.$nextTick();
    this.isInitialLoad = false;
    this.gitBaseVersion = wds3Cache.baseVersion;
    console.log('[WDS2UnitEditor] Git base version set:', {
      gitBaseVersion: this.gitBaseVersion,
      wds3CacheBaseVersion: wds3Cache.baseVersion,
      manifest: wds3Cache.manifest
    });
  },
  
  methods: {
    async getSubdirectories() {
      // V2 filesystem method removed; factions are populated from wds3Cache in mounted()
      return [];
    },

    findDuplicateIds(label, arr) {
      const seen = new Set();
      const duplicates = [];
      for (const entity of arr) {
        if (entity.Id) {
          if (seen.has(entity.Id)) {
            duplicates.push({ key: entity.Id, source: label, entity });
          } else {
            seen.add(entity.Id);
          }
        }
      }
      return duplicates;
    },

    _buildDuplicateFixHelpers() {
      const allArrays = [
        this.rules, this.cards, this.bases, this.tokens, this.gameOptions, this.gameEquipment,
        this.units, this.profiles, this.statlines, this.equipment,
        this.factionOptions, this.factionRules,
      ];
      const usedIds = new Set();
      for (const arr of allArrays) {
        if (Array.isArray(arr)) {
          for (const e of arr) { if (e.Id) usedIds.add(e.Id); }
        }
      }
      const markDirty = {
        'rules':          () => { this.$set(this.dirtyGameFiles, 'rules.json', true); this.hasUnsavedChanges = true; },
        'cards':          () => { this.$set(this.dirtyGameFiles, 'cards.json', true); this.hasUnsavedChanges = true; },
        'bases':          () => { this.$set(this.dirtyGameFiles, 'bases.json', true); this.hasUnsavedChanges = true; },
        'tokens':         () => { this.$set(this.dirtyGameFiles, 'tokens.json', true); this.hasUnsavedChanges = true; },
        'game options':   () => { this.$set(this.dirtyGameFiles, 'options.json', true); this.hasUnsavedChanges = true; },
        'game equipment': () => { this.$set(this.dirtyGameFiles, 'equipment.json', true); this.hasUnsavedChanges = true; },
        'hierarchy':      () => { this.$set(this.dirtyGameFiles, 'hierarchy.json', true); this.hasUnsavedChanges = true; },
        'units':          () => this.markFactionFileDirty('units.json'),
        'profiles':       () => this.markFactionFileDirty('profiles.json'),
        'statlines':      () => this.markFactionFileDirty('statlines.json'),
        'equipment':      () => this.markFactionFileDirty('equipment.json'),
        'faction options':() => this.markFactionFileDirty('options.json'),
        'faction rules':  () => this.markFactionFileDirty('rules.json'),
        'modifiedunit':   () => this.markFactionFileDirty('modifiedunit.json'),
        'modifiedprofile':() => this.markFactionFileDirty('modifiedprofile.json'),
      };
      return { usedIds, markDirty };
    },

    /**
     * Update all references to an ID across all entities when an ID changes.
     * Searches in array properties (BasicProfileIds, ProfileIds) and Children properties.
     * @param {string} oldId - The ID to search for and replace
     * @param {string} newId - The new ID to use
     * @returns {Array<string>} - Array of affected file types that need to be marked dirty
     */
    _updateReferencesToId(oldId, newId) {
      const affectedFiles = new Set();

      // Helper to update array of IDs (simple strings)
      const updateIdArray = (arr, propName, fileType) => {
        if (!Array.isArray(arr)) return;
        const index = arr.indexOf(oldId);
        if (index !== -1) {
          arr[index] = newId;
          affectedFiles.add(fileType);
        }
      };

      // Helper to update Children property (array of objects { Id, Info })
      // Used by: Statlines, Tokens, Cards, Rules, Equipment
      // Can reference: Statlines, Rules, Equipment
      const updateChildren = (entity, fileType) => {
        if (!entity.Children || !Array.isArray(entity.Children)) return;
        
        for (const child of entity.Children) {
          if (child && child.Id === oldId) {
            child.Id = newId;
            affectedFiles.add(fileType);
          }
        }
      };
      
      // Helper to update array of objects with { Id, Info } structure
      const updateItemsArray = (arr, fileType) => {
        if (!Array.isArray(arr)) return;
        
        for (const item of arr) {
          if (item && item.Id === oldId) {
            item.Id = newId;
            affectedFiles.add(fileType);
          }
          // Check nested Ref array (for ModifiedProfile)
          if (item.Ref && Array.isArray(item.Ref)) {
            for (const ref of item.Ref) {
              if (ref && ref.Id === oldId) {
                ref.Id = newId;
                affectedFiles.add(fileType);
              }
            }
          }
        }
      };

      // Update Units: BasicProfileIds, ProfileIds, Children, and ExtraItems
      if (Array.isArray(this.units)) {
        for (const unit of this.units) {
          if (unit.BasicProfileIds) {
            updateIdArray(unit.BasicProfileIds, 'BasicProfileIds', 'units');
          }
          if (unit.ProfileIds) {
            updateIdArray(unit.ProfileIds, 'ProfileIds', 'units');
          }
          updateChildren(unit, 'units');
          
          // Update ExtraItems: array of objects with { Id, Type }
          updateItemsArray(unit.ExtraItems, 'units');
        }
      }

      // Update Profiles: Children, BaseId
      if (Array.isArray(this.profiles)) {
        for (const profile of this.profiles) {
          updateChildren(profile, 'profiles');
          if (profile.BaseId === oldId) {
            profile.BaseId = newId;
            affectedFiles.add('profiles');
          }
        }
      }

      // Update Statlines: Children
      if (Array.isArray(this.statlines)) {
        for (const statline of this.statlines) {
          updateChildren(statline, 'statlines');
        }
      }

      // Update Equipment: Children
      if (Array.isArray(this.equipment)) {
        for (const equip of this.equipment) {
          updateChildren(equip, 'equipment');
        }
      }

      // Update Game Equipment: Children
      if (Array.isArray(this.gameEquipment)) {
        for (const equip of this.gameEquipment) {
          updateChildren(equip, 'game equipment');
        }
      }

      // Update Faction Options: ItemsToAdd, ExtraItems
      if (Array.isArray(this.factionOptions)) {
        for (const option of this.factionOptions) {
          updateItemsArray(option.ItemsToAdd, 'faction options');
          updateItemsArray(option.ExtraItems, 'faction options');
        }
      }

      // Update Game Options: ItemsToAdd, ExtraItems
      if (Array.isArray(this.gameOptions)) {
        for (const option of this.gameOptions) {
          updateItemsArray(option.ItemsToAdd, 'game options');
          updateItemsArray(option.ExtraItems, 'game options');
        }
      }

      // Update Faction Rules: Children
      if (Array.isArray(this.factionRules)) {
        for (const rule of this.factionRules) {
          updateChildren(rule, 'faction rules');
        }
      }

      // Update Game Rules: Children
      if (Array.isArray(this.rules)) {
        for (const rule of this.rules) {
          updateChildren(rule, 'rules');
        }
      }

      // Update Cards: Children
      if (Array.isArray(this.cards)) {
        for (const card of this.cards) {
          updateChildren(card, 'cards');
        }
      }

      // Update Tokens: Children
      if (Array.isArray(this.tokens)) {
        for (const token of this.tokens) {
          updateChildren(token, 'tokens');
        }
      }

      // Update ModifiedUnit: UnitId, NewUnitId, NewProfileId, OptionId, ReferencedItemsToRemove, ReferencedItemsToAdd
      if (Array.isArray(this.modifiedUnits)) {
        for (const mod of this.modifiedUnits) {
          if (mod.UnitId === oldId) {
            mod.UnitId = newId;
            affectedFiles.add('modifiedunit');
          }
          if (mod.NewUnitId === oldId) {
            mod.NewUnitId = newId;
            affectedFiles.add('modifiedunit');
          }
          if (mod.NewProfileId === oldId) {
            mod.NewProfileId = newId;
            affectedFiles.add('modifiedunit');
          }
          if (mod.OptionId === oldId) {
            mod.OptionId = newId;
            affectedFiles.add('modifiedunit');
          }
          // Update arrays with nested Ref support
          updateItemsArray(mod.ReferencedItemsToRemove, 'modifiedunit');
          updateItemsArray(mod.ReferencedItemsToAdd, 'modifiedunit');
        }
      }

      // Update ModifiedProfile: ProfileId, UnitId, OptionId, ReferencedItemsToRemove, ReferencedItemsToAdd
      if (Array.isArray(this.modifiedProfiles)) {
        for (const mod of this.modifiedProfiles) {
          if (mod.ProfileId === oldId) {
            mod.ProfileId = newId;
            affectedFiles.add('modifiedprofile');
          }
          if (mod.UnitId === oldId) {
            mod.UnitId = newId;
            affectedFiles.add('modifiedprofile');
          }
          if (mod.OptionId === oldId) {
            mod.OptionId = newId;
            affectedFiles.add('modifiedprofile');
          }
          // Update arrays with nested Ref support
          updateItemsArray(mod.ReferencedItemsToRemove, 'modifiedprofile');
          updateItemsArray(mod.ReferencedItemsToAdd, 'modifiedprofile');
        }
      }

      // Update Hierarchy: Unit references in faction categories
      if (this.hierarchy && this.hierarchy.Factions) {
        for (const factionName in this.hierarchy.Factions) {
          const faction = this.hierarchy.Factions[factionName];
          if (!faction || typeof faction !== 'object') continue;
          
          // Check all categories in this faction (e.g., "Characters", "Core", "Special")
          for (const categoryName in faction) {
            const category = faction[categoryName];
            if (Array.isArray(category)) {
              const index = category.indexOf(oldId);
              if (index !== -1) {
                category[index] = newId;
                affectedFiles.add('hierarchy');
              }
            }
          }
        }
      }

      // Update Hierarchy: Card references in card categories
      if (this.hierarchy && this.hierarchy.Cards) {
        for (const categoryName in this.hierarchy.Cards) {
          const category = this.hierarchy.Cards[categoryName];
          if (Array.isArray(category)) {
            const index = category.indexOf(oldId);
            if (index !== -1) {
              category[index] = newId;
              affectedFiles.add('hierarchy');
            }
          }
        }
      }

      return Array.from(affectedFiles);
    },

    async fixDuplicateId(item, index) {
      const { usedIds, markDirty } = this._buildDuplicateFixHelpers();
      let newId;
      do {
        newId = `${item.source.replace(/\s+/g, '_')}_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
      } while (usedIds.has(newId));
      
      // Store old ID before changing
      const oldId = item.entity.Id;
      
      // Update all references to this ID
      const affectedFiles = this._updateReferencesToId(oldId, newId);
      
      // Update the entity ID
      item.entity.Id = newId;
      
      // Mark the source file as dirty
      if (markDirty[item.source]) markDirty[item.source]();
      
      // Mark all files with updated references as dirty
      for (const fileType of affectedFiles) {
        if (markDirty[fileType]) markDirty[fileType]();
      }
      
      this.duplicateKeysList.splice(index, 1);
      if (this.duplicateKeysList.length === 0) this.showDuplicateKeysModal = false;
      wds3Cache.saveToSession();
    },

    async fixAllDuplicateIds() {
      const { usedIds, markDirty } = this._buildDuplicateFixHelpers();
      const allAffectedFiles = new Set();
      
      for (const item of this.duplicateKeysList) {
        let newId;
        do {
          newId = `${item.source.replace(/\s+/g, '_')}_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
        } while (usedIds.has(newId));
        usedIds.add(newId);
        
        // Store old ID before changing
        const oldId = item.entity.Id;
        
        // Update all references to this ID
        const affectedFiles = this._updateReferencesToId(oldId, newId);
        affectedFiles.forEach(f => allAffectedFiles.add(f));
        
        // Update the entity ID
        item.entity.Id = newId;
        
        // Mark the source file as dirty
        if (markDirty[item.source]) markDirty[item.source]();
      }
      
      // Mark all files with updated references as dirty
      for (const fileType of allAffectedFiles) {
        if (markDirty[fileType]) markDirty[fileType]();
      }
      
      this.duplicateKeysList = [];
      this.showDuplicateKeysModal = false;
      wds3Cache.saveToSession();
    },

    /**
     * Validate all entities against their JSON schema requirements.
     * Returns an array of validation issues.
     */
    _validateSchemas() {
      const issues = [];

      // Helper to validate common required fields
      const validateEntity = (entity, entityType, location, requiredFields, validTypes) => {
        const problems = [];
        
        // Check required fields
        for (const field of requiredFields) {
          if (!entity[field] && entity[field] !== 0) {
            problems.push(`Missing required field: ${field}`);
          }
        }

        // Check Type field value
        if (validTypes && entity.Type && !validTypes.includes(entity.Type)) {
          problems.push(`Invalid Type value: "${entity.Type}". Expected one of: ${validTypes.join(', ')}`);
        }

        // Check Id is a string
        if (entity.Id && typeof entity.Id !== 'string') {
          problems.push(`Id must be a string, got: ${typeof entity.Id}`);
        }

        // Check Name is a string
        if (entity.Name && typeof entity.Name !== 'string') {
          problems.push(`Name must be a string, got: ${typeof entity.Name}`);
        }

        // Check Children structure if exists
        if (entity.Children) {
          if (!Array.isArray(entity.Children)) {
            problems.push(`Children must be an array, got: ${typeof entity.Children}`);
          } else {
            entity.Children.forEach((child, idx) => {
              if (!child.Id) {
                problems.push(`Children[${idx}] missing required field: Id`);
              }
              if (child.Id && typeof child.Id !== 'string') {
                problems.push(`Children[${idx}].Id must be a string`);
              }
            });
          }
        }

        // Check Attributes is an object if exists
        if (entity.Attributes && typeof entity.Attributes !== 'object') {
          problems.push(`Attributes must be an object, got: ${typeof entity.Attributes}`);
        }

        return problems;
      };

      // Validate Units
      const validateUnits = (units, location) => {
        if (!Array.isArray(units)) return;
        
        units.forEach((unit, idx) => {
          const problems = validateEntity(
            unit, 
            'unit', 
            location, 
            ['Id', 'Name', 'Type', 'DefaultFront', 'Formation', 'BasicProfileIds'],
            ['Unit']
          );

          // Unit-specific validation
          if (unit.DefaultFront !== undefined) {
            if (typeof unit.DefaultFront !== 'number' || unit.DefaultFront < 1) {
              problems.push(`DefaultFront must be a number >= 1, got: ${unit.DefaultFront}`);
            }
          }

          if (unit.Formation !== undefined) {
            if (typeof unit.Formation !== 'number' || ![0, 1, 2, 3, 4].includes(unit.Formation)) {
              problems.push(`Formation must be an integer 0-4, got: ${unit.Formation}`);
            }
          }

          if (unit.BasicProfileIds !== undefined) {
            if (!Array.isArray(unit.BasicProfileIds)) {
              problems.push(`BasicProfileIds must be an array, got: ${typeof unit.BasicProfileIds}`);
            } else if (unit.BasicProfileIds.some(id => typeof id !== 'string')) {
              problems.push(`BasicProfileIds must contain only strings`);
            }
          }

          if (unit.ExtraItems && !Array.isArray(unit.ExtraItems)) {
            problems.push(`ExtraItems must be an array, got: ${typeof unit.ExtraItems}`);
          }

          if (problems.length > 0) {
            issues.push({
              type: 'schema-validation',
              severity: 'error',
              dataType: 'unit',
              entityId: unit.Id || `index ${idx}`,
              location: location,
              problems: problems
            });
          }
        });
      };

      // Validate Profiles
      const validateProfiles = (profiles, location) => {
        if (!Array.isArray(profiles)) return;
        
        profiles.forEach((profile, idx) => {
          const problems = validateEntity(
            profile, 
            'profile', 
            location, 
            ['Id', 'Name', 'Type', 'BaseId'],
            ['Profile']
          );

          // Profile-specific validation
          if (profile.BaseId !== undefined && typeof profile.BaseId !== 'string') {
            problems.push(`BaseId must be a string, got: ${typeof profile.BaseId}`);
          }

          if (problems.length > 0) {
            issues.push({
              type: 'schema-validation',
              severity: 'error',
              dataType: 'profile',
              entityId: profile.Id || `index ${idx}`,
              location: location,
              problems: problems
            });
          }
        });
      };

      // Validate Statlines
      const validateStatlines = (statlines, location) => {
        if (!Array.isArray(statlines)) return;
        
        statlines.forEach((statline, idx) => {
          const problems = validateEntity(
            statline, 
            'statline', 
            location, 
            ['Id', 'Name', 'Type'],
            ['Statline']
          );

          if (problems.length > 0) {
            issues.push({
              type: 'schema-validation',
              severity: 'error',
              dataType: 'statline',
              entityId: statline.Id || `index ${idx}`,
              location: location,
              problems: problems
            });
          }
        });
      };

      // Validate Rules
      const validateRules = (rules, location) => {
        if (!Array.isArray(rules)) return;
        
        rules.forEach((rule, idx) => {
          const problems = validateEntity(
            rule, 
            'rule', 
            location, 
            ['Id', 'Name', 'Type'],
            ['Rule']
          );

          if (problems.length > 0) {
            issues.push({
              type: 'schema-validation',
              severity: 'error',
              dataType: 'rule',
              entityId: rule.Id || `index ${idx}`,
              location: location,
              problems: problems
            });
          }
        });
      };

      // Validate Equipment
      const validateEquipment = (equipment, location) => {
        if (!Array.isArray(equipment)) return;
        
        equipment.forEach((equip, idx) => {
          const problems = validateEntity(
            equip, 
            'equipment', 
            location, 
            ['Id', 'Name', 'Type'],
            ['Equipment']
          );

          if (problems.length > 0) {
            issues.push({
              type: 'schema-validation',
              severity: 'error',
              dataType: 'equipment',
              entityId: equip.Id || `index ${idx}`,
              location: location,
              problems: problems
            });
          }
        });
      };

      // Validate ModifiedUnit
      const validateModifiedUnits = (modifiedUnits, location) => {
        if (!Array.isArray(modifiedUnits)) return;
        
        modifiedUnits.forEach((mod, idx) => {
          const problems = [];
          
          if (!mod.OptionId) problems.push('Missing required field: OptionId');
          if (!mod.UnitId) problems.push('Missing required field: UnitId');
          
          if (mod.OptionId && typeof mod.OptionId !== 'string') {
            problems.push('OptionId must be a string');
          }
          if (mod.UnitId && typeof mod.UnitId !== 'string') {
            problems.push('UnitId must be a string');
          }

          if (problems.length > 0) {
            issues.push({
              type: 'schema-validation',
              severity: 'error',
              dataType: 'modifiedunit',
              entityId: `${mod.UnitId || '?'}+${mod.OptionId || '?'} [${idx}]`,
              location: location,
              problems: problems
            });
          }
        });
      };

      // Validate ModifiedProfile
      const validateModifiedProfiles = (modifiedProfiles, location) => {
        if (!Array.isArray(modifiedProfiles)) return;
        
        modifiedProfiles.forEach((mod, idx) => {
          const problems = [];
          
          if (!mod.OptionId) problems.push('Missing required field: OptionId');
          if (!mod.ProfileId) problems.push('Missing required field: ProfileId');
          
          if (mod.OptionId && typeof mod.OptionId !== 'string') {
            problems.push('OptionId must be a string');
          }
          if (mod.ProfileId && typeof mod.ProfileId !== 'string') {
            problems.push('ProfileId must be a string');
          }

          if (problems.length > 0) {
            issues.push({
              type: 'schema-validation',
              severity: 'error',
              dataType: 'modifiedprofile',
              entityId: `${mod.ProfileId || '?'}+${mod.OptionId || '?'} [${idx}]`,
              location: location,
              problems: problems
            });
          }
        });
      };

      // Run validations for game-level files
      validateRules(this.rules, 'Game Rules');
      validateEquipment(this.gameEquipment, 'Game Equipment');
      validateStatlines(this.gameStatlines, 'Game Statlines');

      // Run validations for all factions
      for (const faction of wds3Cache.factions) {
        const factionName = faction.name;
        const factionHandle = faction.handle || faction.files || {};

        let factionData = {};
        if (this.selectedFaction && this.selectedFaction.name === factionName) {
          factionData = {
            units: this.units,
            profiles: this.profiles,
            statlines: this.statlines,
            equipment: this.equipment,
            rules: this.factionRules,
            modifiedUnits: this.modifiedUnits,
            modifiedProfiles: this.modifiedProfiles,
          };
        } else {
          factionData = {
            units: factionHandle['units.json'] || [],
            profiles: factionHandle['profiles.json'] || [],
            statlines: factionHandle['statlines.json'] || [],
            equipment: factionHandle['equipment.json'] || [],
            rules: factionHandle['rules.json'] || [],
            modifiedUnits: factionHandle['modifiedunit.json'] || [],
            modifiedProfiles: factionHandle['modifiedprofile.json'] || [],
          };
        }

        validateUnits(factionData.units, `${factionName} / Units`);
        validateProfiles(factionData.profiles, `${factionName} / Profiles`);
        validateStatlines(factionData.statlines, `${factionName} / Statlines`);
        validateEquipment(factionData.equipment, `${factionName} / Equipment`);
        validateRules(factionData.rules, `${factionName} / Rules`);
        validateModifiedUnits(factionData.modifiedUnits, `${factionName} / ModifiedUnits`);
        validateModifiedProfiles(factionData.modifiedProfiles, `${factionName} / ModifiedProfiles`);
      }

      return issues;
    },

    checkForDuplicateIds() {
      const checks = [
        ['rules', this.rules],
        ['cards', this.cards],
        ['tokens', this.tokens],
        ['game options', this.gameOptions],
        ['game equipment', this.gameEquipment],
        ['units', this.units],
        ['profiles', this.profiles],
        ['statlines', this.statlines],
        ['equipment', this.equipment],
        ['faction options', this.factionOptions],
        ['faction rules', this.factionRules],
      ];
      const duplicates = [];
      for (const [label, arr] of checks) {
        duplicates.push(...this.findDuplicateIds(label, arr));
      }
      
      // Check bases only within their own file
      duplicates.push(...this.findDuplicateIds('bases', this.bases));
      
      if (duplicates.length > 0) {
        this.duplicateKeysList = duplicates;
        this.showDuplicateKeysModal = true;
      }
    },

    /** 
     * Comprehensive structure validation across all factions.
     * Checks for:
     * 1. File structure validation (required and optional files)
     * 2. ID duplicates across all factions (not just current)
     * 3. Cross-faction references (IDs referenced by multiple factions that should be moved to root)
     */
    async checkStructureValidation() {
      const issues = [];
      const idRegistry = {}; // { id: [{ location, entity, dataType }] }
      const referenceRegistry = {}; // { id: Set<factionName> } - tracks which factions reference which IDs

      // ========================================
      // 0. FILE STRUCTURE VALIDATION
      // ========================================
      
      // Define file requirements
      const rootRequiredFiles = [
        'manifest.json',
        'hierarchy.json',
        'gameconfig.json'
      ];

      const rootOptionalFiles = [
        'rules.json',
        'equipment.json',
        'option.json',
        'bases.json',
        'cards.json',
        'cardstack.json',
        'units.json',
        'profiles.json',
        'modifiedunit.json',
        'modifiedprofile.json',
        'tokens.json',
        'tokenpresets.json',
        'terrains.json',
        'objectives.json',
        'maps.json',
        'deployments.json',
        'dummylist.json'
      ];

      const factionOptionalFiles = [
        'profiles.json',
        'option.json',
        'modifiedunit.json',
        'modifiedprofile.json',
        'units.json',
        'rules.json',
        'equipment.json',
        'statlines.json'
      ];

      // Check required root files
      const missingRequiredFiles = [];
      for (const fileName of rootRequiredFiles) {
        if (!wds3Cache.gameFiles[fileName] || 
            (Array.isArray(wds3Cache.gameFiles[fileName]) && wds3Cache.gameFiles[fileName].length === 0 && fileName !== 'manifest.json' && fileName !== 'hierarchy.json' && fileName !== 'gameconfig.json') ||
            (typeof wds3Cache.gameFiles[fileName] === 'object' && !Array.isArray(wds3Cache.gameFiles[fileName]) && Object.keys(wds3Cache.gameFiles[fileName]).length === 0 && fileName === 'manifest.json')) {
          // For manifest, hierarchy, and gameconfig, they must exist as objects/arrays
          if (!wds3Cache.gameFiles[fileName]) {
            missingRequiredFiles.push(fileName);
          }
        }
      }

      if (missingRequiredFiles.length > 0) {
        issues.push({
          type: 'missing-required-files',
          files: missingRequiredFiles,
          locations: ['Root Directory'],
          description: `Required files are missing from the root directory: ${missingRequiredFiles.join(', ')}`
        });
      }

      // Check faction file structure
      for (const faction of wds3Cache.factions) {
        const factionName = faction.name;
        const factionFiles = faction.handle || faction.files || {};
        
        // We don't enforce that faction files must exist, but we can warn if a faction folder
        // exists but has unexpected structure. For now, just log available files.
        // (Optional: Add warnings for factions with no files at all)
        
        const hasAnyFile = factionOptionalFiles.some(f => factionFiles[f] && factionFiles[f].length > 0);
        if (!hasAnyFile) {
          // Faction exists but has no data files - this might be intentional
          console.log(`[Validation] Faction "${factionName}" has no data files`);
        }
      }

      // ========================================
      // 0.5 SCHEMA VALIDATION
      // ========================================
      
      // Validate entities against JSON schema requirements
      const schemaIssues = this._validateSchemas();
      issues.push(...schemaIssues);

      // Helper to register an ID
      const registerID = (id, location, entity, dataType) => {
        if (!id) return;
        if (!idRegistry[id]) {
          idRegistry[id] = [];
        }
        idRegistry[id].push({ location, entity, dataType });
      };

      // Helper to register a reference
      const registerReference = (id, factionName, dataType) => {
        if (!id) return;
        if (!referenceRegistry[id]) {
          referenceRegistry[id] = { factions: new Set(), dataType };
        }
        referenceRegistry[id].factions.add(factionName);
      };

      // 1. Collect IDs from game-level files
      const gameData = [
        { label: 'Game Rules', data: this.rules, dataType: 'rule' },
        { label: 'Game Cards', data: this.cards, dataType: 'card' },
        // Bases are excluded from validation as they are unique configuration elements
        { label: 'Game Tokens', data: this.tokens, dataType: 'token' },
        { label: 'Game Options', data: this.gameOptions, dataType: 'option' },
        { label: 'Game Equipment', data: this.gameEquipment, dataType: 'equipment' },
        { label: 'Game Statlines', data: this.gameStatlines, dataType: 'statline' },
        { label: 'Game Terrains', data: this.terrains, dataType: 'terrain' },
        { label: 'Game Objectives', data: this.objectives, dataType: 'objective' },
        { label: 'Game Deployments', data: this.deployments, dataType: 'deployment' },
      ];

      for (const { label, data, dataType } of gameData) {
        if (Array.isArray(data)) {
          for (const entity of data) {
            if (entity.Id) {
              registerID(entity.Id, label, entity, dataType);
            }
          }
        }
      }

      // 2. Collect IDs from all factions
      for (const faction of wds3Cache.factions) {
        const factionName = faction.name;
        const factionHandle = faction.handle || faction.files || {};

        // Load faction data if it's not the currently selected faction
        let factionData = {};
        if (this.selectedFaction && this.selectedFaction.name === factionName) {
          // Use currently loaded data
          factionData = {
            units: this.units,
            profiles: this.profiles,
            statlines: this.statlines,
            equipment: this.equipment,
            options: this.factionOptions,
            rules: this.factionRules,
          };
        } else {
          // Load from cache
          factionData = {
            units: factionHandle['units.json'] || [],
            profiles: factionHandle['profiles.json'] || [],
            statlines: factionHandle['statlines.json'] || [],
            equipment: factionHandle['equipment.json'] || [],
            options: factionHandle['options.json'] || [],
            rules: factionHandle['rules.json'] || [],
          };
        }

        // Register IDs
        const factionDataTypes = [
          { label: `${factionName} / Units`, data: factionData.units, dataType: 'unit' },
          { label: `${factionName} / Profiles`, data: factionData.profiles, dataType: 'profile' },
          { label: `${factionName} / Statlines`, data: factionData.statlines, dataType: 'statline' },
          { label: `${factionName} / Equipment`, data: factionData.equipment, dataType: 'equipment' },
          { label: `${factionName} / Options`, data: factionData.options, dataType: 'option' },
          { label: `${factionName} / Rules`, data: factionData.rules, dataType: 'rule' },
        ];

        for (const { label, data, dataType } of factionDataTypes) {
          if (Array.isArray(data)) {
            for (const entity of data) {
              if (entity.Id) {
                registerID(entity.Id, label, entity, dataType);
              }
            }
          }
        }

        // Track references from units to profiles, statlines, etc.
        if (Array.isArray(factionData.units)) {
          for (const unit of factionData.units) {
            // Track profile references
            const profileIds = unit.BasicProfileIds || unit.ProfileIds || [];
            for (const profileId of profileIds) {
              registerReference(profileId, factionName, 'profile');
            }
          }
        }

        // Track references from profiles to statlines, rules, equipment, etc.
        if (Array.isArray(factionData.profiles)) {
          for (const profile of factionData.profiles) {
            // Track children references (can be statlines, rules, equipment, etc.)
            if (profile.Children) {
              const children = Array.isArray(profile.Children) 
                ? profile.Children 
                : Object.keys(profile.Children);
              
              for (const child of children) {
                const childId = typeof child === 'string' ? child : child.Id;
                if (childId) {
                  // Determine type based on ID registry
                  const registered = idRegistry[childId];
                  if (registered && registered.length > 0) {
                    registerReference(childId, factionName, registered[0].dataType);
                  }
                }
              }
            }
          }
        }
      }

      // 3. Find duplicates (same ID defined in multiple places)
      // Types that share a common namespace - IDs MUST be unique across these types
      const sharedNamespaceTypes = new Set(['statline', 'token', 'card', 'profile', 'rule', 'equipment', 'terrain']);
      
      // Types that are independent - IDs can overlap with other types (warning only)
      const independentTypes = new Set(['unit', 'option', 'base', 'objective', 'deployment', 'map', 'cardstack', 'tokenpreset']);
      
      for (const [id, locations] of Object.entries(idRegistry)) {
        if (locations.length > 1) {
          // Get all data types involved in this duplicate
          const involvedTypes = [...new Set(locations.map(l => l.dataType))];
          
          // Check if this is a critical duplicate (same type OR mixed types within shared namespace)
          const isSameType = involvedTypes.length === 1;
          const allInSharedNamespace = involvedTypes.every(t => sharedNamespaceTypes.has(t));
          const anyInSharedNamespace = involvedTypes.some(t => sharedNamespaceTypes.has(t));
          const allIndependent = involvedTypes.every(t => independentTypes.has(t));
          
          if (isSameType) {
            // CRITICAL: Same type with duplicate IDs (always an error)
            issues.push({
              type: 'duplicate',
              severity: 'error',
              id: id,
              dataTypes: involvedTypes,
              locations: locations.map(l => l.location),
              entities: locations.map(l => l.entity),
              reason: `Duplicate ID within the same type (${involvedTypes[0]})`
            });
          } else if (allInSharedNamespace) {
            // CRITICAL: Different types but all within shared namespace (error)
            issues.push({
              type: 'duplicate',
              severity: 'error',
              id: id,
              dataTypes: involvedTypes,
              locations: locations.map(l => l.location),
              entities: locations.map(l => l.entity),
              reason: `ID used across multiple types in shared namespace: ${involvedTypes.join(', ')}`
            });
          } else if (!allIndependent && anyInSharedNamespace) {
            // MIXED: Some in shared namespace, some independent
            const sharedTypes = involvedTypes.filter(t => sharedNamespaceTypes.has(t));
            const independentTypesInvolved = involvedTypes.filter(t => independentTypes.has(t));
            
            if (sharedTypes.length > 1) {
              // ERROR: Multiple shared namespace types + independent types
              issues.push({
                type: 'duplicate',
                severity: 'error',
                id: id,
                dataTypes: involvedTypes,
                locations: locations.map(l => l.location),
                entities: locations.map(l => l.entity),
                reason: `ID conflicts across shared namespace types (${sharedTypes.join(', ')}) and independent types (${independentTypesInvolved.join(', ')})`
              });
            } else {
              // WARNING: One shared namespace type + independent types
              issues.push({
                type: 'duplicate',
                severity: 'warning',
                id: id,
                dataTypes: involvedTypes,
                locations: locations.map(l => l.location),
                entities: locations.map(l => l.entity),
                reason: `ID shared between independent types (${independentTypesInvolved.join(', ')}) and shared namespace type (${sharedTypes[0]}). This may cause confusion but won't break references.`
              });
            }
          } else if (allIndependent) {
            // WARNING: Only independent types (informational only)
            issues.push({
              type: 'duplicate',
              severity: 'warning',
              id: id,
              dataTypes: involvedTypes,
              locations: locations.map(l => l.location),
              entities: locations.map(l => l.entity),
              reason: `ID shared between independent types (${involvedTypes.join(', ')}). This is allowed but may cause confusion.`
            });
          }
        }
      }

      // 4. Find cross-faction references
      for (const [id, refData] of Object.entries(referenceRegistry)) {
        if (refData.factions.size > 1) {
          // This ID is referenced by multiple factions
          const registered = idRegistry[id];
          if (registered && registered.length > 0) {
            const location = registered[0].location;
            // Check if it's defined in a faction-specific location
            if (!location.startsWith('Game ')) {
              issues.push({
                type: 'cross-faction',
                id: id,
                dataType: refData.dataType,
                locations: Array.from(refData.factions).map(f => `Faction: ${f}`),
                definedIn: location,
              });
            }
          }
        }
      }

      // 5. Check for duplicate option associations (modifiedunit.json and modifiedprofile.json)
      for (const faction of wds3Cache.factions) {
        const factionName = faction.name;
        const factionHandle = faction.handle || faction.files || {};

        // Check modifiedunit.json for duplicate UnitId + OptionId combinations
        let modifiedUnits = [];
        if (this.selectedFaction && this.selectedFaction.name === factionName) {
          modifiedUnits = this.modifiedUnits;
        } else {
          modifiedUnits = factionHandle['modifiedunit.json'] || [];
        }

        if (Array.isArray(modifiedUnits) && modifiedUnits.length > 0) {
          const seenCombos = new Set();
          const duplicates = [];

          for (const mod of modifiedUnits) {
            const combo = `${mod.UnitId}|${mod.OptionId}`;
            if (seenCombos.has(combo)) {
              duplicates.push({ UnitId: mod.UnitId, OptionId: mod.OptionId });
            } else {
              seenCombos.add(combo);
            }
          }

          if (duplicates.length > 0) {
            issues.push({
              type: 'duplicate-option-unit',
              factionName: factionName,
              duplicates: duplicates,
              locations: [`${factionName} / modifiedunit.json`],
            });
          }
        }

        // Check modifiedprofile.json for duplicate ProfileId + OptionId combinations
        let modifiedProfiles = [];
        if (this.selectedFaction && this.selectedFaction.name === factionName) {
          modifiedProfiles = this.modifiedProfiles;
        } else {
          modifiedProfiles = factionHandle['modifiedprofile.json'] || [];
        }

        if (Array.isArray(modifiedProfiles) && modifiedProfiles.length > 0) {
          const seenCombos = new Set();
          const duplicates = [];

          for (const mod of modifiedProfiles) {
            const combo = `${mod.ProfileId}|${mod.OptionId}`;
            if (seenCombos.has(combo)) {
              duplicates.push({ ProfileId: mod.ProfileId, OptionId: mod.OptionId });
            } else {
              seenCombos.add(combo);
            }
          }

          if (duplicates.length > 0) {
            issues.push({
              type: 'duplicate-option-profile',
              factionName: factionName,
              duplicates: duplicates,
              locations: [`${factionName} / modifiedprofile.json`],
            });
          }
        }
      }

      // 6. Show results
      if (issues.length > 0) {
        this.structureValidationIssues = issues;
        this.showStructureValidationModal = true;
        
        // Count errors vs warnings vs schema errors
        const errorCount = issues.filter(i => i.severity !== 'warning' && i.type !== 'missing-required-files' && i.type !== 'schema-validation').length;
        const warningCount = issues.filter(i => i.severity === 'warning').length;
        const manualCount = issues.filter(i => i.type === 'missing-required-files').length;
        const schemaCount = issues.filter(i => i.type === 'schema-validation').length;
        
        // Log summary for developer
        console.log(`[Validation] Found ${errorCount} fixable error(s), ${schemaCount} schema error(s), ${warningCount} warning(s), ${manualCount} file issue(s)`);
      } else {
        await this.showAlert('✓ No structure validation issues found!', 'Validation Complete');
      }
    },

    /**
     * Fix a single structure validation issue
     */
    async fixStructureIssue(issue, issueIndex) {
      // Skip issues that require manual action
      if (issue.type === 'missing-required-files') {
        await this.showAlert('⚠️ This issue requires manual action. Please create the missing files.', 'Manual Action Required');
        return;
      }
      
      // Skip schema validation errors (require manual editing)
      if (issue.type === 'schema-validation') {
        await this.showAlert('📋 This entity has schema validation errors. Please edit the entity directly to fix the missing or incorrect fields.', 'Manual Editing Required');
        return;
      }
      
      // Skip warnings (informational only)
      if (issue.severity === 'warning') {
        await this.showAlert('ℹ️ This is a warning only. No automatic fix is available or needed. This is for informational purposes to help you identify potential naming conflicts.', 'Informational Warning');
        return;
      }
      
      if (issue.type === 'duplicate') {
        await this.fixDuplicateIssue(issue);
      } else if (issue.type === 'cross-faction') {
        await this.fixCrossFactionIssue(issue);
      } else if (issue.type === 'duplicate-option-unit') {
        await this.fixDuplicateOptionUnit(issue);
      } else if (issue.type === 'duplicate-option-profile') {
        await this.fixDuplicateOptionProfile(issue);
      }
      
      // Remove the fixed issue from the list
      this.structureValidationIssues.splice(issueIndex, 1);
      
      // Close modal if all issues are fixed
      if (this.structureValidationIssues.length === 0) {
        this.showStructureValidationModal = false;
        await this.showAlert('✓ All issues fixed successfully!', 'Fix Complete');
      }
      
      // Save to session and update unsaved changes flag
      wds3Cache.saveToSession();
      this.updateUnsavedChangesFlag();
    },

    /**
     * Fix all structure validation issues at once
     */
    async fixAllStructureIssues() {
      let fixedCount = 0;
      const manualIssues = [];
      const warningIssues = [];
      const schemaIssues = [];
      
      for (const issue of [...this.structureValidationIssues]) {
        // Skip issues that require manual action
        if (issue.type === 'missing-required-files') {
          manualIssues.push(issue);
          continue;
        }
        
        // Skip schema validation errors (require manual editing)
        if (issue.type === 'schema-validation') {
          schemaIssues.push(issue);
          continue;
        }
        
        // Skip warnings (informational only)
        if (issue.severity === 'warning') {
          warningIssues.push(issue);
          continue;
        }
        
        if (issue.type === 'duplicate') {
          await this.fixDuplicateIssue(issue);
          fixedCount++;
        } else if (issue.type === 'cross-faction') {
          await this.fixCrossFactionIssue(issue);
          fixedCount++;
        } else if (issue.type === 'duplicate-option-unit') {
          await this.fixDuplicateOptionUnit(issue);
          fixedCount++;
        } else if (issue.type === 'duplicate-option-profile') {
          await this.fixDuplicateOptionProfile(issue);
          fixedCount++;
        }
      }
      
      // Keep only manual issues, schema issues and warnings in the list
      this.structureValidationIssues = [...manualIssues, ...schemaIssues, ...warningIssues];
      
      if (this.structureValidationIssues.length === 0) {
        this.showStructureValidationModal = false;
      }
      
      // Show appropriate message
      const remainingCount = manualIssues.length + schemaIssues.length + warningIssues.length;
      
      if (fixedCount > 0 && remainingCount > 0) {
        const details = [];
        if (manualIssues.length > 0) {
          details.push(`${manualIssues.length} file issue${manualIssues.length !== 1 ? 's' : ''}`);
        }
        if (schemaIssues.length > 0) {
          details.push(`${schemaIssues.length} schema error${schemaIssues.length !== 1 ? 's' : ''}`);
        }
        if (warningIssues.length > 0) {
          details.push(`${warningIssues.length} warning${warningIssues.length !== 1 ? 's' : ''}`);
        }
        await this.showAlert(`✓ Fixed ${fixedCount} error${fixedCount !== 1 ? 's' : ''} successfully!\n\n⚠️ Remaining: ${details.join(', ')} require manual action.`, 'Fix Complete');
      } else if (fixedCount > 0) {
        await this.showAlert(`✓ Fixed ${fixedCount} error${fixedCount !== 1 ? 's' : ''} successfully!`, 'Fix Complete');
      } else if (remainingCount > 0) {
        const details = [];
        if (manualIssues.length > 0) {
          details.push(`${manualIssues.length} file issue${manualIssues.length !== 1 ? 's' : ''}`);
        }
        if (schemaIssues.length > 0) {
          details.push(`${schemaIssues.length} schema error${schemaIssues.length !== 1 ? 's' : ''}`);
        }
        if (warningIssues.length > 0) {
          details.push(`${warningIssues.length} informational warning${warningIssues.length !== 1 ? 's' : ''}`);
        }
        await this.showAlert(`⚠️ All issues require manual action:\n\n${details.join('\n')}`, 'Manual Action Required');
      }
      
      // Save to session and update unsaved changes flag
      wds3Cache.saveToSession();
      this.updateUnsavedChangesFlag();
    },

    /**
     * Fix a duplicate ID issue by keeping one in root and removing from factions
     */
    async fixDuplicateIssue(issue) {
      const { id, entities } = issue;
      
      if (!entities || entities.length === 0) return;
      
      // Check if all entities are identical
      const areAllIdentical = this._areAllEntitiesIdentical(entities);
      
      if (areAllIdentical) {
        // Auto-fix: Unify identical entities
        await this._unifyDuplicateEntities(issue);
        await this.showAlert(`✅ Unified ${entities.length} identical entities with ID "${id}"`, 'Success');
        // Save to session and update unsaved changes flag
        wds3Cache.saveToSession();
        this.updateUnsavedChangesFlag();
      } else {
        // Show advanced resolution dialog for different entities
        this.duplicateResolutionData = {
          issue: issue,
          entities: entities,
          selectedEntityIndex: 0,
          action: 'rename-others'
        };
        this.showDuplicateResolutionDialog = true;
      }
    },
    
    /**
     * Check if all entities in an array are identical (deep comparison)
     */
    _areAllEntitiesIdentical(entities) {
      if (!entities || entities.length <= 1) return true;
      
      const first = entities[0];
      for (let i = 1; i < entities.length; i++) {
        if (!this._areEntitiesEqual(first, entities[i])) {
          return false;
        }
      }
      return true;
    },
    
    /**
     * Normalize entity to WDS3 format when moving to game level.
     * Converts WDS2 profiles/units/equipment/statlines to WDS3 structure.
     */
    _normalizeEntityToWDS3(entity, dataType) {
      if (!entity) return entity;
      
      // Only normalize profiles and units that have WDS2 structure
      if (dataType === 'profile' && (entity.Statlines || entity.RuleIds || entity.EquipmentIds)) {
        // This is WDS2 profile format - convert to WDS3
        const wds3Profile = {
          Id: entity.Id,
          Name: entity.Name || '',
          Type: 'Profile',
        };
        
        // Preserve BaseId - try multiple sources
        if (entity.BaseId !== undefined && entity.BaseId !== null) {
          wds3Profile.BaseId = entity.BaseId;
        } else if (entity.Base && entity.Base.Id) {
          wds3Profile.BaseId = entity.Base.Id;
        } else {
          wds3Profile.BaseId = '';
        }
        
        // Add optional Description if present
        if (entity.Description) {
          wds3Profile.Description = entity.Description;
        }
        
        // Add optional Attributes if present and not empty
        if (entity.Attributes && Object.keys(entity.Attributes).length > 0) {
          wds3Profile.Attributes = entity.Attributes;
        }
        
        // Build Children array from various WDS2 sources
        const children = [];
        
        // Add statlines as children
        if (Array.isArray(entity.Statlines)) {
          for (const statline of entity.Statlines) {
            if (statline.Id) {
              const child = { Id: statline.Id };
              if (statline.Name && statline.Name !== entity.Name) {
                child.Info = statline.Name;
              }
              children.push(child);
            }
          }
        }
        
        // Add rules from RuleIds (object format)
        if (entity.RuleIds && typeof entity.RuleIds === 'object') {
          for (const [ruleId, ruleName] of Object.entries(entity.RuleIds)) {
            const child = { Id: ruleId };
            if (ruleName && typeof ruleName === 'string') {
              child.Info = ruleName;
            }
            children.push(child);
          }
        }
        
        // Add equipment from EquipmentIds
        if (Array.isArray(entity.EquipmentIds)) {
          for (const equipId of entity.EquipmentIds) {
            if (equipId) {
              children.push({ Id: equipId });
            }
          }
        }
        
        // Add children array if not empty
        if (children.length > 0) {
          wds3Profile.Children = children;
        }
        
        console.log('[DEBUG] _normalizeEntityToWDS3: Converted WDS2 profile to WDS3:', { 
          from: entity.Name, 
          childrenCount: children.length,
          baseId: wds3Profile.BaseId
        });
        
        return wds3Profile;
      }
      
      if (dataType === 'unit' && entity.Profiles) {
        // This is WDS2 unit format - convert to WDS3
        const wds3Unit = {
          Id: entity.Id,
          Name: entity.Name || '',
          Type: 'Unit',
          DefaultFront: entity.DefaultFront || 1,
          Formation: entity.Formation || 0,
          BasicProfileIds: [],
        };
        
        // Add optional Description if present
        if (entity.Description) {
          wds3Unit.Description = entity.Description;
        }
        
        // Add optional Attributes if present and not empty
        if (entity.Attributes && Object.keys(entity.Attributes).length > 0) {
          wds3Unit.Attributes = entity.Attributes;
        }
        
        // Convert Profiles array to BasicProfileIds
        if (Array.isArray(entity.Profiles)) {
          wds3Unit.BasicProfileIds = entity.Profiles.map(p => p.Id || p).filter(Boolean);
        }
        
        console.log('[DEBUG] _normalizeEntityToWDS3: Converted WDS2 unit to WDS3:', { 
          from: entity.Name, 
          profilesCount: wds3Unit.BasicProfileIds.length 
        });
        
        return wds3Unit;
      }
      
      // For other types (equipment, statlines, rules, etc.), ensure Children is in array format
      const normalized = { ...entity };
      
      // Add Type field if missing
      if (!normalized.Type && dataType) {
        const typeMap = {
          equipment: 'Equipment',
          statline: 'Statline',
          rule: 'Rule',
          card: 'Card',
          token: 'Token',
          option: 'Option',
        };
        if (typeMap[dataType]) {
          normalized.Type = typeMap[dataType];
        }
      }
      
      // Convert Children from object format {id: info} to array format [{Id, Info}]
      if (normalized.Children && typeof normalized.Children === 'object' && !Array.isArray(normalized.Children)) {
        console.log('[DEBUG] _normalizeEntityToWDS3: Converting Children from object to array for', dataType);
        normalized.Children = Object.entries(normalized.Children).map(([id, info]) => ({
          Id: id,
          Info: info || ''
        }));
      }
      
      // For statlines, convert Values to Attributes if present
      if (dataType === 'statline' && normalized.Values && !normalized.Attributes) {
        console.log('[DEBUG] _normalizeEntityToWDS3: Converting Values to Attributes for statline');
        normalized.Attributes = normalized.Values;
        delete normalized.Values;
      }
      
      return normalized;
    },
    
    /**
     * Deep comparison of two entities (ignoring Id field)
     */
    _areEntitiesEqual(entity1, entity2) {
      if (!entity1 || !entity2) return false;
      
      // Create copies without Id field
      const copy1 = { ...entity1 };
      const copy2 = { ...entity2 };
      delete copy1.Id;
      delete copy2.Id;
      
      // Deep compare using JSON (simple but effective for data objects)
      return JSON.stringify(copy1) === JSON.stringify(copy2);
    },
    
    /**
     * Unify duplicate entities (all identical) - keep first, remove others
     */
    async _unifyDuplicateEntities(issue) {
      const { id, entities } = issue;
      const entityToKeep = entities[0];
      const dataType = this._inferDataTypeFromEntity(entityToKeep);
      
      // Get the appropriate game-level array
      const gameArray = this._getGameArrayByDataType(dataType);
      
      if (!gameArray) {
        console.error('Could not determine game array for data type:', dataType);
        return;
      }
      
      // Check if already exists in game array
      const existsInGame = gameArray.some(e => e.Id === id);
      
      if (!existsInGame) {
        // Normalize entity to WDS3 format before adding to game level
        const normalizedEntity = this._normalizeEntityToWDS3(entityToKeep, dataType);
        
        // Add to game array (local)
        gameArray.push({ ...normalizedEntity });
        this._markGameFileDirty(dataType);
        
        // Sync to wds3Cache.gameFiles (create file entry if it doesn't exist)
        const gameFileName = this._getFileNameByDataType(dataType);
        if (gameFileName) {
          if (!wds3Cache.gameFiles[gameFileName]) {
            wds3Cache.gameFiles[gameFileName] = [];
          }
          wds3Cache.gameFiles[gameFileName] = gameArray;
        }
      }
      
      // Remove from all faction arrays
      for (let i = 1; i < entities.length; i++) {
        await this._removeEntityFromFaction(id, dataType);
      }
      
      // Save to session
      wds3Cache.saveToSession();
    },

    /**
     * Fix a cross-faction reference by moving entity to root
     */
    async fixCrossFactionIssue(issue) {
      console.log('[DEBUG] fixCrossFactionIssue START:', { issue });
      const { id, dataType, definedIn } = issue;
      
      if (!dataType || !definedIn) {
        console.log('[DEBUG] fixCrossFactionIssue EARLY EXIT: missing dataType or definedIn');
        return;
      }
      
      // Extract faction name from definedIn (e.g., "Necrons / Statlines")
      const factionMatch = definedIn.match(/^([^/]+)\s*\/\s*/);
      if (!factionMatch) {
        console.log('[DEBUG] fixCrossFactionIssue EARLY EXIT: could not extract faction from definedIn:', definedIn);
        return;
      }
      
      const factionName = factionMatch[1].trim();
      console.log('[DEBUG] fixCrossFactionIssue: factionName =', factionName);
      
      // Find the entity in the faction (use wds3Cache.factions, not this.factions)
      console.log('[DEBUG] fixCrossFactionIssue: searching in wds3Cache.factions, count =', wds3Cache.factions?.length || 0);
      const faction = wds3Cache.factions.find(f => f.name === factionName);
      if (!faction) {
        console.log('[DEBUG] fixCrossFactionIssue EARLY EXIT: faction not found:', factionName);
        console.log('[DEBUG] fixCrossFactionIssue: available factions:', wds3Cache.factions.map(f => f.name));
        return;
      }
      console.log('[DEBUG] fixCrossFactionIssue: found faction');
      
      const factionArray = this._getFactionArrayByDataType(faction, dataType);
      if (!factionArray) {
        console.log('[DEBUG] fixCrossFactionIssue EARLY EXIT: factionArray not found for dataType:', dataType);
        return;
      }
      console.log('[DEBUG] fixCrossFactionIssue: found factionArray, length =', factionArray.length);
      
      const entityIndex = factionArray.findIndex(e => e.Id === id);
      if (entityIndex === -1) {
        console.log('[DEBUG] fixCrossFactionIssue EARLY EXIT: entity not found in faction array, id:', id);
        return;
      }
      console.log('[DEBUG] fixCrossFactionIssue: found entity at index', entityIndex);
      
      const entity = factionArray[entityIndex];
      
      // Get the appropriate game-level array
      const gameArray = this._getGameArrayByDataType(dataType);
      if (!gameArray) {
        console.log('[DEBUG] fixCrossFactionIssue EARLY EXIT: gameArray not found for dataType:', dataType);
        return;
      }
      console.log('[DEBUG] fixCrossFactionIssue: found gameArray, length =', gameArray.length);
      
      // Check if already exists in game array
      const existsInGame = gameArray.some(e => e.Id === id);
      console.log('[DEBUG] fixCrossFactionIssue: existsInGame =', existsInGame);
      
      if (!existsInGame) {
        // Normalize entity to WDS3 format before adding to game level
        console.log('[DEBUG] fixCrossFactionIssue: normalizing entity to WDS3');
        const normalizedEntity = this._normalizeEntityToWDS3(entity, dataType);
        console.log('[DEBUG] fixCrossFactionIssue: normalized entity:', normalizedEntity);
        
        // Add to game array (local)
        console.log('[DEBUG] fixCrossFactionIssue: adding entity to game array');
        gameArray.push({ ...normalizedEntity });
        console.log('[DEBUG] fixCrossFactionIssue: calling _markGameFileDirty with dataType:', dataType);
        this._markGameFileDirty(dataType);
        
        // Sync to wds3Cache.gameFiles (create file entry if it doesn't exist)
        const gameFileName = this._getFileNameByDataType(dataType);
        console.log('[DEBUG] fixCrossFactionIssue: gameFileName =', gameFileName);
        if (gameFileName) {
          if (!wds3Cache.gameFiles[gameFileName]) {
            wds3Cache.gameFiles[gameFileName] = [];
          }
          wds3Cache.gameFiles[gameFileName] = gameArray;
        }
      }
      
      // Remove from faction array (local)
      console.log('[DEBUG] fixCrossFactionIssue: removing entity from faction array');
      factionArray.splice(entityIndex, 1);
      console.log('[DEBUG] fixCrossFactionIssue: calling _markFactionFileDirtyByName with factionName:', factionName, 'dataType:', dataType);
      this._markFactionFileDirtyByName(factionName, dataType);
      
      // Sync to wds3Cache faction files
      const factionKey = this._getFileNameByDataType(dataType);
      if (factionKey && faction.handle) {
        console.log('[DEBUG] fixCrossFactionIssue: updating faction.handle and faction.files with key:', factionKey);
        faction.handle[factionKey] = factionArray;
        
        // Also update faction.files (wds3Cache uses both handle and files)
        if (faction.files) {
          faction.files[factionKey] = factionArray;
        }
      }
      
      // If this is the currently selected faction, update local arrays
      if (this.selectedFaction && this.selectedFaction.name === factionName) {
        if (dataType === 'statline') {
          this.statlines = factionArray;
        } else if (dataType === 'equipment') {
          this.equipment = factionArray;
        } else if (dataType === 'option') {
          this.factionOptions = factionArray;
        } else if (dataType === 'rule') {
          this.factionRules = factionArray;
        }
      }
      
      // Save to session
      console.log('[DEBUG] fixCrossFactionIssue: calling wds3Cache.saveToSession()');
      wds3Cache.saveToSession();
      console.log('[DEBUG] fixCrossFactionIssue COMPLETE');
    },

    /**
     * Helper: Infer data type from entity structure
     */
    _inferDataTypeFromEntity(entity) {
      if (!entity) return null;
      
      // Check for specific fields to determine type
      if (entity.Formation !== undefined || entity.BasicProfileIds !== undefined || entity.ProfileIds !== undefined) {
        return 'unit';
      }
      if (entity.Children !== undefined || entity.BaseId !== undefined) {
        return 'profile';
      }
      if (entity.Attributes !== undefined && !entity.Children) {
        return 'statline';
      }
      if (entity.RuleText !== undefined) {
        return 'rule';
      }
      if (entity.Type === 'Card' || entity.CardType !== undefined) {
        return 'card';
      }
      if (entity.BaseShape !== undefined || entity.BaseWidth !== undefined) {
        return 'base';
      }
      if (entity.SkinId !== undefined && entity.Type === 'Token') {
        return 'token';
      }
      
      // Default fallback
      return 'option';
    },

    /**
     * Helper: Generate a unique ID by appending a suffix
     */
    _generateUniqueId(baseId, dataType) {
      // Collect all existing IDs of this type
      const existingIds = new Set();
      
      // Collect from game-level arrays
      const gameArray = this._getGameArrayByDataType(dataType);
      if (gameArray && Array.isArray(gameArray)) {
        for (const entity of gameArray) {
          if (entity.Id) existingIds.add(entity.Id);
        }
      }
      
      // Collect from all factions
      for (const faction of wds3Cache.factions) {
        const factionArray = this._getFactionArrayByDataType(faction, dataType);
        if (factionArray && Array.isArray(factionArray)) {
          for (const entity of factionArray) {
            if (entity.Id) existingIds.add(entity.Id);
          }
        }
      }
      
      // Generate unique ID with suffix
      let suffix = 2;
      let newId = `${baseId}_${suffix}`;
      
      while (existingIds.has(newId)) {
        suffix++;
        newId = `${baseId}_${suffix}`;
      }
      
      return newId;
    },

    /**
     * Helper: Get game-level array by data type
     */
    _getGameArrayByDataType(dataType) {
      const mapping = {
        rule: this.rules,
        card: this.cards,
        base: this.bases,
        token: this.tokens,
        option: this.gameOptions,
        equipment: this.gameEquipment,
        statline: this.gameStatlines,
        unit: this.gameUnits,
        profile: this.gameProfiles,
      };
      return mapping[dataType] || null;
    },

    /**
     * Helper: Get faction-level array by data type
     * Works with both faction.handle (this.factions) and faction.files (wds3Cache.factions)
     */
    _getFactionArrayByDataType(faction, dataType) {
      if (!faction) return null;
      
      const fileName = this._getFileNameByDataType(dataType);
      if (!fileName) return null;
      
      // Try faction.handle first (used by this.factions), then faction.files (used by wds3Cache.factions)
      if (faction.handle && faction.handle[fileName]) {
        return faction.handle[fileName];
      } else if (faction.files && faction.files[fileName]) {
        return faction.files[fileName];
      }
      
      return null;
    },

    /**
     * Helper: Get file name by data type
     */
    _getFileNameByDataType(dataType) {
      const mapping = {
        unit: 'units.json',
        profile: 'profiles.json',
        statline: 'statlines.json',
        equipment: 'equipment.json',
        option: 'options.json',
        rule: 'rules.json',
      };
      return mapping[dataType] || null;
    },

    /**
     * Helper: Mark game file as dirty by data type
     */
    _markGameFileDirty(dataType) {
      const mapping = {
        rule: 'rules.json',
        card: 'cards.json',
        base: 'bases.json',
        token: 'tokens.json',
        option: 'options.json',
        equipment: 'equipment.json',
        statline: 'statlines.json',
        profile: 'profiles.json',
        unit: 'units.json',
        hierarchy: 'hierarchy.json',
      };
      
      const fileName = mapping[dataType];
      if (fileName) {
        console.log('[DEBUG] _markGameFileDirty:', { dataType, fileName, before: {...this.dirtyGameFiles} });
        this.$set(this.dirtyGameFiles, fileName, true);
        this.hasUnsavedChanges = true;
        console.log('[DEBUG] _markGameFileDirty after:', { dirtyGameFiles: {...this.dirtyGameFiles}, hasUnsavedChanges: this.hasUnsavedChanges });
      }
    },

    /**
     * Helper: Mark faction file as dirty by faction name and data type
     */
    _markFactionFileDirtyByName(factionName, dataType) {
      let fileName = this._getFileNameByDataType(dataType);
      
      // Handle special cases
      if (dataType === 'modifiedunit') {
        fileName = 'modifiedunit.json';
      } else if (dataType === 'modifiedprofile') {
        fileName = 'modifiedprofile.json';
      }
      
      if (!fileName) return;
      
      console.log('[DEBUG] _markFactionFileDirtyByName:', { factionName, dataType, fileName, before: {...this.dirtyFactionFiles} });
      if (!this.dirtyFactionFiles[factionName]) {
        this.$set(this.dirtyFactionFiles, factionName, {});
      }
      this.$set(this.dirtyFactionFiles[factionName], fileName, true);
      this.hasUnsavedChanges = true;
      console.log('[DEBUG] _markFactionFileDirtyByName after:', { dirtyFactionFiles: {...this.dirtyFactionFiles}, hasUnsavedChanges: this.hasUnsavedChanges });
    },

    /**
     * Apply the selected duplicate resolution strategy
     */
    async applyDuplicateResolution() {
      const { issue, entities, selectedEntityIndex, action } = this.duplicateResolutionData;
      
      if (action === 'unify') {
        // Unify all (keep selected, remove others)
        await this._unifyDuplicateEntities(issue);
        await this.showAlert(`✅ Unified entities with ID "${issue.id}"`, 'Success');
      } else if (action === 'rename-others') {
        // Keep selected, rename others
        await this._renameDuplicateEntities(issue, selectedEntityIndex);
        await this.showAlert(`✅ Resolved duplicates for ID "${issue.id}" by renaming`, 'Success');
      }
      
      // Save to session and update unsaved changes flag
      wds3Cache.saveToSession();
      this.updateUnsavedChangesFlag();
      
      this.showDuplicateResolutionDialog = false;
      
      // Re-run validation to update issues list
      await this.checkStructureValidation();
    },
    
    /**
     * Rename duplicate entities (keep selected, generate unique IDs for others)
     */
    async _renameDuplicateEntities(issue, keepIndex) {
      const { id, entities } = issue;
      const dataType = this._inferDataTypeFromEntity(entities[0]);
      
      // Process each entity except the one to keep
      for (let i = 0; i < entities.length; i++) {
        if (i === keepIndex) continue; // Skip the one we're keeping
        
        const entity = entities[i];
        const oldId = entity.Id;
        
        // Generate unique new ID
        const newId = this._generateUniqueId(oldId, dataType);
        
        // Update all references to this entity
        const affectedFiles = this._updateReferencesToId(oldId, newId);
        
        // Update entity ID
        entity.Id = newId;
        
        // Mark appropriate files as dirty
        const location = issue.locations[i];
        if (location.startsWith('Game ')) {
          this._markGameFileDirty(dataType);
        } else {
          // Extract faction name
          const factionMatch = location.match(/^([^/]+)/);
          if (factionMatch) {
            const factionName = factionMatch[1];
            this._markFactionFileDirtyByName(factionName, dataType);
          }
        }
        
        // Mark affected files as dirty
        for (const fileType of affectedFiles) {
          // hierarchy is a game-level file
          if (fileType === 'hierarchy') {
            this._markGameFileDirty('hierarchy');
          }
          // modifiedunit and modifiedprofile are faction-level
          else if (fileType === 'modifiedunit' || fileType === 'modifiedprofile') {
            if (this.selectedFaction) {
              this._markFactionFileDirtyByName(this.selectedFaction.name, fileType);
            }
          }
          // Other faction files
          else if (this.selectedFaction) {
            this._markFactionFileDirtyByName(this.selectedFaction.name, fileType);
          }
        }
      }
      
      // Save changes to session
      wds3Cache.saveToSession();
    },
    
    /**
     * Helper: Remove an entity from all factions by ID and data type
     */
    async _removeEntityFromFaction(id, dataType) {
      for (const faction of wds3Cache.factions) {
        const factionArray = this._getFactionArrayByDataType(faction, dataType);
        if (!factionArray) continue;
        
        const entityIndex = factionArray.findIndex(e => e.Id === id);
        if (entityIndex !== -1) {
          factionArray.splice(entityIndex, 1);
          this._markFactionFileDirtyByName(faction.name, dataType);
          
          // Update cache - both faction.handle and faction.files
          const factionKey = this._getFileNameByDataType(dataType);
          if (factionKey && faction.handle) {
            faction.handle[factionKey] = factionArray;
            
            // Also update faction.files (wds3Cache uses both handle and files)
            if (faction.files) {
              faction.files[factionKey] = factionArray;
            }
          }
        }
      }
      
      // If currently viewing this faction, update the local arrays
      if (this.selectedFaction && dataType === 'unit') {
        const index = this.units.findIndex(e => e.Id === id);
        if (index !== -1) this.units.splice(index, 1);
      }
      if (this.selectedFaction && dataType === 'profile') {
        const index = this.profiles.findIndex(e => e.Id === id);
        if (index !== -1) this.profiles.splice(index, 1);
      }
      if (this.selectedFaction && dataType === 'statline') {
        const index = this.statlines.findIndex(e => e.Id === id);
        if (index !== -1) this.statlines.splice(index, 1);
      }
      if (this.selectedFaction && dataType === 'equipment') {
        const index = this.equipment.findIndex(e => e.Id === id);
        if (index !== -1) this.equipment.splice(index, 1);
      }
      if (this.selectedFaction && dataType === 'option') {
        const index = this.factionOptions.findIndex(e => e.Id === id);
        if (index !== -1) this.factionOptions.splice(index, 1);
      }
      if (this.selectedFaction && dataType === 'rule') {
        const index = this.factionRules.findIndex(e => e.Id === id);
        if (index !== -1) this.factionRules.splice(index, 1);
      }
    },

    /**
     * Fix duplicate option-unit associations
     */
    async fixDuplicateOptionUnit(issue) {
      const { factionName, duplicates } = issue;
      
      if (!factionName || !duplicates || duplicates.length === 0) return;
      
      const faction = this.factions.find(f => f.name === factionName);
      if (!faction) return;
      
      // Get modifiedUnits array
      let modifiedUnits;
      if (this.selectedFaction && this.selectedFaction.name === factionName) {
        modifiedUnits = this.modifiedUnits;
      } else {
        modifiedUnits = faction.handle['modifiedunit.json'] || [];
      }
      
      if (!Array.isArray(modifiedUnits)) return;
      
      // For each duplicate combination, keep only the first occurrence
      for (const dup of duplicates) {
        const combo = `${dup.UnitId}|${dup.OptionId}`;
        let foundFirst = false;
        
        // Remove all but the first occurrence
        for (let i = modifiedUnits.length - 1; i >= 0; i--) {
          const mod = modifiedUnits[i];
          const modCombo = `${mod.UnitId}|${mod.OptionId}`;
          
          if (modCombo === combo) {
            if (!foundFirst) {
              foundFirst = true; // Keep this one
            } else {
              modifiedUnits.splice(i, 1); // Remove duplicate
            }
          }
        }
      }
      
      // Update cache - both faction.handle and wds3Cache.factions
      faction.handle['modifiedunit.json'] = modifiedUnits;
      
      const cacheFaction = wds3Cache.factions.find(f => f.name === factionName);
      if (cacheFaction && cacheFaction.files) {
        cacheFaction.files['modifiedunit.json'] = modifiedUnits;
      }
      
      // Mark as dirty
      this._markFactionFileDirtyByName(factionName, 'modifiedunit');
      
      // Update local array if this is the current faction
      if (this.selectedFaction && this.selectedFaction.name === factionName) {
        this.modifiedUnits = modifiedUnits;
      }
      
      // Save to session
      wds3Cache.saveToSession();
    },

    /**
     * Fix duplicate option-profile associations
     */
    async fixDuplicateOptionProfile(issue) {
      const { factionName, duplicates } = issue;
      
      if (!factionName || !duplicates || duplicates.length === 0) return;
      
      const faction = this.factions.find(f => f.name === factionName);
      if (!faction) return;
      
      // Get modifiedProfiles array
      let modifiedProfiles;
      if (this.selectedFaction && this.selectedFaction.name === factionName) {
        modifiedProfiles = this.modifiedProfiles;
      } else {
        modifiedProfiles = faction.handle['modifiedprofile.json'] || [];
      }
      
      if (!Array.isArray(modifiedProfiles)) return;
      
      // For each duplicate combination, keep only the first occurrence
      for (const dup of duplicates) {
        const combo = `${dup.ProfileId}|${dup.OptionId}`;
        let foundFirst = false;
        
        // Remove all but the first occurrence
        for (let i = modifiedProfiles.length - 1; i >= 0; i--) {
          const mod = modifiedProfiles[i];
          const modCombo = `${mod.ProfileId}|${mod.OptionId}`;
          
          if (modCombo === combo) {
            if (!foundFirst) {
              foundFirst = true; // Keep this one
            } else {
              modifiedProfiles.splice(i, 1); // Remove duplicate
            }
          }
        }
      }
      
      // Update cache - both faction.handle and wds3Cache.factions
      faction.handle['modifiedprofile.json'] = modifiedProfiles;
      
      const cacheFaction = wds3Cache.factions.find(f => f.name === factionName);
      if (cacheFaction && cacheFaction.files) {
        cacheFaction.files['modifiedprofile.json'] = modifiedProfiles;
      }
      
      // Mark as dirty
      this._markFactionFileDirtyByName(factionName, 'modifiedprofile');
      
      // Update local array if this is the current faction
      if (this.selectedFaction && this.selectedFaction.name === factionName) {
        this.modifiedProfiles = modifiedProfiles;
      }
      
      // Save to session
      wds3Cache.saveToSession();
    },

    isUnitInHierarchy(unitId) {
      if (!this.hierarchy || !this.hierarchy.Factions) return false;
      // Check all factions in the hierarchy
      for (const factionName in this.hierarchy.Factions) {
        const faction = this.hierarchy.Factions[factionName];
        if (!faction || typeof faction !== 'object') continue;
        // Check all categories in this faction
        for (const categoryName in faction) {
          const category = faction[categoryName];
          if (Array.isArray(category) && category.includes(unitId)) {
            return true;
          }
        }
      }
      return false;
    },
    
    // Custom dialog methods
    showConfirm(message, title = 'Confirm', confirmText = 'OK') {
      return new Promise((resolve) => {
        this.customDialogType = 'confirm';
        this.customDialogTitle = title;
        this.customDialogMessage = message;
        this.customDialogConfirmText = confirmText;
        this.customDialogResolve = resolve;
        this.showCustomDialog = true;
      });
    },
    
    showAlert(message, title = 'Notice') {
      return new Promise((resolve) => {
        this.customDialogType = 'alert';
        this.customDialogTitle = title;
        this.customDialogMessage = message;
        this.customDialogConfirmText = 'OK';
        this.customDialogResolve = resolve;
        this.showCustomDialog = true;
      });
    },
    
    confirmCustomDialog() {
      if (this.customDialogResolve) {
        this.customDialogResolve(true);
      }
      this.showCustomDialog = false;
      this.customDialogResolve = null;
    },
    
    cancelCustomDialog() {
      if (this.customDialogResolve) {
        this.customDialogResolve(false);
      }
      this.showCustomDialog = false;
      this.customDialogResolve = null;
    },
    
    // Helper to mark a faction file as dirty
    markFactionFileDirty(fileName) {
      if (!this.selectedFaction) return;
      const factionName = this.selectedFaction.name;
      if (!this.dirtyFactionFiles[factionName]) {
        this.$set(this.dirtyFactionFiles, factionName, {});
      }
      this.$set(this.dirtyFactionFiles[factionName], fileName, true);
      this.hasUnsavedChanges = true; // Ensure the save button updates
    },
    
    async loadJsonFrom(folderHandle, fileName) {
      if (folderHandle && typeof folderHandle === 'object') {
        // Faction-level file: folderHandle is the faction's files map
        const data = folderHandle[fileName];
        return Array.isArray(data) ? data : (data || []);
      }
      // Game-level file
      const data = wds3Cache.gameFiles[fileName];
      return Array.isArray(data) ? data : (data || []);
    },
    
    async loadGameData() {
      try {
        const [rules, cards, bases, tokens, options, equipment, statlines, profiles, units, hierarchy, gameconfig, terrains, deployments, objectives] = await Promise.all([
          this.loadJsonFrom(null, 'rules.json'),
          this.loadJsonFrom(null, 'cards.json'),
          this.loadJsonFrom(null, 'bases.json'),
          this.loadJsonFrom(null, 'tokens.json'),
          this.loadJsonFrom(null, 'options.json'),
          this.loadJsonFrom(null, 'equipment.json'),
          this.loadJsonFrom(null, 'statlines.json'),
          this.loadJsonFrom(null, 'profiles.json').catch(() => []),
          this.loadJsonFrom(null, 'units.json').catch(() => []),
          this.loadJsonFrom(null, 'hierarchy.json').catch(() => ({ Factions: {} })),
          this.loadJsonFrom(null, 'gameconfig.json').catch(() => null),
          this.loadJsonFrom(null, 'terrains.json').catch(() => []),
          this.loadJsonFrom(null, 'deployments.json').catch(() => []),
          this.loadJsonFrom(null, 'objectives.json').catch(() => []),
        ]);
        
        this.rules = rules;
        this.cards = cards;
        this.bases = bases;
        this.tokens = tokens;
        this.terrains = Array.isArray(terrains) ? terrains : [];
        this.deployments = Array.isArray(deployments) ? deployments : [];
        this.objectives = Array.isArray(objectives) ? objectives : [];
        // Ensure these files are registered in the cache (even if empty) so the upload manifest includes them
        if (!wds3Cache.gameFiles['terrains.json']) wds3Cache.gameFiles['terrains.json'] = this.terrains;
        if (!wds3Cache.gameFiles['deployments.json']) wds3Cache.gameFiles['deployments.json'] = this.deployments;
        if (!wds3Cache.gameFiles['objectives.json']) wds3Cache.gameFiles['objectives.json'] = this.objectives;
        // Only register profiles.json and units.json if they actually exist in the game
        // (they will be created automatically when content is moved to game level)
        this.gameOptions = options;
        this.gameEquipment = equipment;
        this.gameStatlines = statlines;
        this.gameProfiles = profiles;
        this.gameUnits = units;
        // Sync loaded game-level profiles and units back to cache if they exist
        if (profiles && profiles.length > 0 && !wds3Cache.gameFiles['profiles.json']) {
          wds3Cache.gameFiles['profiles.json'] = profiles;
        }
        if (units && units.length > 0 && !wds3Cache.gameFiles['units.json']) {
          wds3Cache.gameFiles['units.json'] = units;
        }
        this.hierarchy = hierarchy;
        this.gameFormations = (gameconfig && Array.isArray(gameconfig.Formations))
          ? gameconfig.Formations
          : [];
        this.gameConfigWidth = (gameconfig && gameconfig.BattlegroundWidth) || 72;
        this.gameConfigHeight = (gameconfig && gameconfig.BattlegroundHeight) || 48;
        
        // Collapse all nodes by default when hierarchy is loaded
        if (hierarchy) {
          this.collapseAllFactions();
        }
        // Load unit and card names in the background
        if (hierarchy) {
          this.loadHierarchyItemNames();
        }
        
        console.log('Game data loaded:', { 
          rules: rules.length, 
          cards: cards.length,
          equipment: equipment.length,
          statlines: statlines.length
        });
        
        // Clear dirty game files after loading
        this.dirtyGameFiles = {};
        this.updateUnsavedChangesFlag();
        this.checkForDuplicateIds();
        
        // Initialize statline template from existing statlines if not already set
        if (!this.wizardStatAttrsTemplate && statlines.length > 0 && statlines[0].Attributes) {
          this.wizardStatAttrsTemplate = { ...statlines[0].Attributes };
        }
      } catch (err) {
        console.error('Failed to load game data:', err);
      }
    },
    
    switchEditorMode(newMode) {
      if (this.editorMode === newMode) return;
      this.editorMode = newMode;
    },

    async onFactionChange() {
      const newFaction = this.selectedFaction;
      if (!newFaction || newFaction === this.previousFaction) return;
      this.selectedFaction = newFaction;
      await this.loadFactionData();
      this.previousFaction = newFaction;
    },

    async addNewFaction() {
      // Prompt user for faction name
      const factionName = prompt('Enter new faction name (e.g. Space Marines):');
      
      if (!factionName || !factionName.trim()) {
        return; // User cancelled or entered empty name
      }

      const fName = factionName.trim();

      // Check if faction already exists
      if (this.factions.find(f => f.name === fName)) {
        await this.showAlert(`Faction "${fName}" already exists.`, 'Duplicate Faction');
        return;
      }

      // Initialize hierarchy if needed
      if (!this.hierarchy) {
        this.$set(this, 'hierarchy', { Factions: {}, Cards: {} });
      }
      if (!this.hierarchy.Factions) {
        this.$set(this.hierarchy, 'Factions', {});
      }

      // Copy subsections from an existing faction (if any)
      let newFactionStructure = {};
      const existingFactions = Object.keys(this.hierarchy.Factions);
      if (existingFactions.length > 0) {
        const templateFaction = this.hierarchy.Factions[existingFactions[0]];
        if (templateFaction && typeof templateFaction === 'object' && !Array.isArray(templateFaction)) {
          // Copy the subsection structure from the first faction
          for (const subsection in templateFaction) {
            newFactionStructure[subsection] = [];
          }
        }
      }

      // Add to hierarchy as a branch node (object) so it shows castle icon and can contain subsections
      this.$set(this.hierarchy.Factions, fName, newFactionStructure);

      // Add to factions array
      const newHandle = {};
      const newFactionEntry = { name: fName, handle: newHandle };
      this.factions.push(newFactionEntry);

      // Keep wds3Cache in sync
      wds3Cache.factions.push({ name: fName, files: newHandle });

      // Mark hierarchy as dirty
      this.$set(this.dirtyGameFiles, 'hierarchy.json', true);

      // Auto-select the new faction
      this.selectedFaction = newFactionEntry;
      await this.loadFactionData();
      this.previousFaction = newFactionEntry;
    },

    async loadFactionData() {
      if (!this.selectedFaction) return;
      
      // Temporarily disable change tracking while loading new faction data
      this.isInitialLoad = true;
      
      try {
        const [units, profiles, statlines, equipment, options, factionRules, modifiedUnits, modifiedProfiles] = await Promise.all([
          this.loadJsonFrom(this.selectedFaction.handle, 'units.json'),
          this.loadJsonFrom(this.selectedFaction.handle, 'profiles.json'),
          this.loadJsonFrom(this.selectedFaction.handle, 'statlines.json'),
          this.loadJsonFrom(this.selectedFaction.handle, 'equipment.json'),
          this.loadJsonFrom(this.selectedFaction.handle, 'options.json'),
          this.loadJsonFrom(this.selectedFaction.handle, 'rules.json').catch(() => []),
          this.loadJsonFrom(this.selectedFaction.handle, 'modifiedunit.json').catch(() => []),
          this.loadJsonFrom(this.selectedFaction.handle, 'modifiedprofile.json').catch(() => []),
        ]);
        
        this.units = units;
        this.profiles = profiles;
        this.statlines = statlines;
        this.equipment = equipment;
        this.factionOptions = options;
        this.factionRules = factionRules;
        
        // Fetch skin thumbnails for profiles
        this.profileThumbnailMap = {};
        this.fetchProfileThumbnails();
        
        // Normalize modifiedUnits and modifiedProfiles to ensure array format for ReferencedItems
        this.modifiedUnits = this.normalizeModifiedItems(modifiedUnits);
        this.modifiedProfiles = this.normalizeModifiedItems(modifiedProfiles);
        
        console.log('Faction data loaded:', { 
          units: units.length, 
          profiles: profiles.length,
          statlines: statlines.length,
          equipment: equipment.length,
          factionRules: factionRules.length
        });
        
        // Initialize statline template from existing faction statlines if not already set
        if (!this.wizardStatAttrsTemplate && statlines.length > 0 && statlines[0].Attributes) {
          this.wizardStatAttrsTemplate = { ...statlines[0].Attributes };
        }
        
        this.checkForDuplicateIds();
      } catch (err) {
        console.error('Failed to load faction data:', err);
      }
      
      // Re-enable change tracking after a tick
      await this.$nextTick();
      this.isInitialLoad = false;
    },
    
    selectType(type) {
      this.selectedType = type;
      this.selectedEntity = null;
      
      // Collapse all factions by default when switching to hierarchy
      if (type === 'hierarchy' && this.hierarchy) {
        this.collapseAllFactions();
        // Load unit and card names for all items in the background
        this.loadHierarchyItemNames();
      }

      // Fetch token skins for ObjectivesEditor on demand
      if (type === 'objectives' && this.objectiveTokenSkins.length === 0) {
        this.fetchObjectiveTokenSkins();
      }
    },


    
    /** Returns the display name for an entity, handling non-standard fields like deploymentName */
    entityDisplayName(entity) {
      return entity.Name || entity.deploymentName || entity.Id || '';
    },

    /** Fetch token skin thumbnails for the tokens currently in tokens.json (for ObjectivesEditor). */
    async fetchObjectiveTokenSkins() {
      const skinIds = this.tokens.map(t => t.SkinId).filter(id => id != null);
      if (skinIds.length === 0) return;
      try {
        const res = await fetch(
          `/GetCatalogueSkins?viewMode=public&search=token&perPage=200&page=1&sortOrder=newest`,
          { credentials: 'include' }
        );
        const data = await res.json();
        if (data.error || !data.result) return;
        const allSkins = data.result.skins || [];
        this.objectiveTokenSkins = allSkins.filter(s =>
          skinIds.some(id => String(id) === String(s.id))
        );
      } catch (_) {
        // silent — objective token palette will just be empty
      }
    },

    /** Handler: TokensEditor emitted new tokens array — write back to cache */
    onTokensEditorInput(newVal) {
      this.tokens = newVal;
      wds3Cache.gameFiles['tokens.json'] = newVal;
    },

    /** Handler: TerrainEditor emitted new terrains array — write back to cache */
    onTerrainsEditorInput(newVal) {
      this.terrains = newVal;
      wds3Cache.gameFiles['terrains.json'] = newVal;
    },

    /** Handler: DeploymentsEditor emitted new deployments array — write back to cache */
    onDeploymentsEditorInput(newVal) {
      this.deployments = newVal;
      wds3Cache.gameFiles['deployments.json'] = newVal;
    },

    /** Handler: ObjectivesEditor emitted new objectives array — write back to cache */
    onObjectivesEditorInput(newVal) {
      this.objectives = newVal;
      wds3Cache.gameFiles['objectives.json'] = newVal;
    },

    /** Add a new item placement to the selected objective */
    addObjectiveItem() {
      if (!this.selectedEntity) return;
      if (!this.selectedEntity.Items) this.$set(this.selectedEntity, 'Items', []);
      this.selectedEntity.Items.push({ Id: '', Type: 'Token', PosX: '0', PosY: '0' });
      this.markAsChanged();
    },

    selectEntity(entity) {
      this.selectedEntity = entity;
      
      // Support both BasicProfileIds and ProfileIds for backwards compatibility
      if (entity && this.selectedType === 'units' && entity.ProfileIds && !entity.BasicProfileIds) {
        this.$set(entity, 'BasicProfileIds', entity.ProfileIds);
      }
    },
    
    onFormationChange() {
      // Update DefaultFront based on new formation type
      if (this.selectedEntity && this.selectedType === 'units') {
        const formation = this.gameFormations.find(f => f.Index === this.selectedEntity.Formation);
        if (formation) {
          const formationType = (formation.Type || '').toLowerCase();
          const isRankAndFile = formationType.includes('rank and file');
          // Only update if DefaultFront is not already set
          if (this.selectedEntity.DefaultFront === undefined || this.selectedEntity.DefaultFront === 0) {
            this.$set(this.selectedEntity, 'DefaultFront', isRankAndFile ? 5 : 1);
          }
        }
      }
      this.markAsChanged();
    },
    
    markAsChanged() {
      if (!this.isInitialLoad) {
        this.hasUnsavedChanges = true;
        
        // Mark the appropriate file as dirty based on current editing context
        if (this.editorMode === 'game') {
          // Game-level data
          if (this.selectedType === 'rules') {
            this.$set(this.dirtyGameFiles, 'rules.json', true);
          } else if (this.selectedType === 'cards') {
            this.$set(this.dirtyGameFiles, 'cards.json', true);
          } else if (this.selectedType === 'bases') {
            this.$set(this.dirtyGameFiles, 'bases.json', true);
          } else if (this.selectedType === 'tokens') {
            this.$set(this.dirtyGameFiles, 'tokens.json', true);
          } else if (this.selectedType === 'terrains') {
            this.$set(this.dirtyGameFiles, 'terrains.json', true);
          } else if (this.selectedType === 'deployments') {
            this.$set(this.dirtyGameFiles, 'deployments.json', true);
          } else if (this.selectedType === 'objectives') {
            this.$set(this.dirtyGameFiles, 'objectives.json', true);
          } else if (this.selectedType === 'options') {
            this.$set(this.dirtyGameFiles, 'options.json', true);
          } else if (this.selectedType === 'equipment') {
            this.$set(this.dirtyGameFiles, 'equipment.json', true);
          } else if (this.selectedType === 'hierarchy') {
            this.$set(this.dirtyGameFiles, 'hierarchy.json', true);
          }
        } else if (this.editorMode === 'faction' && this.selectedFaction) {
          // Faction-level data
          if (this.selectedType === 'units') {
            this.markFactionFileDirty('units.json');
          } else if (this.selectedType === 'profiles') {
            this.markFactionFileDirty('profiles.json');
          } else if (this.selectedType === 'statlines') {
            this.markFactionFileDirty('statlines.json');
          } else if (this.selectedType === 'equipment') {
            this.markFactionFileDirty('equipment.json');
          } else if (this.selectedType === 'options') {
            this.markFactionFileDirty('options.json');
          } else if (this.selectedType === 'modifiedunits') {
            this.markFactionFileDirty('modifiedunit.json');
          } else if (this.selectedType === 'modifiedprofiles') {
            this.markFactionFileDirty('modifiedprofile.json');
          }
        }
      }
    },
    
    addNewEntity() {
      const typeLabelMap = {
        equipment: 'Equipment',
        statlines: 'Statline',
        profiles: 'Profile',
        options: 0, // Options start with numeric type (user will choose)
        rules: 'Rule',
        cards: 'Card',
        bases: null, // Bases don't have Type field
        tokens: 'Token',
        terrains: 'Terrain',
        units: 'Unit'
      };
      
      const newEntity = {
        Id: `${this.selectedType}_${Date.now()}`,
        Name: `New ${this.selectedType}`,
      };
      
      // Add Type field for all types except bases
      const typeValue = typeLabelMap[this.selectedType];
      if (typeValue !== null && typeValue !== undefined) {
        newEntity.Type = typeValue;
      }
      
      // Add type-specific default fields
      if (this.selectedType === 'bases') {
        newEntity.Shape = 0; // 0 = square
        newEntity.Width = 25;
        newEntity.Length = 25;
        delete newEntity.Type; // Bases don't have a Type field
      } else if (this.selectedType === 'terrains') {
        newEntity.Width = 10;
        newEntity.Length = 10;
        newEntity.Height = 1;
        newEntity.Shape = 0;
      } else if (this.selectedType === 'deployments') {
        // Deployments use non-standard field names
        const newDeployment = { deploymentID: 0, deploymentName: 'New Deployment' };
        this.currentEntities.push(newDeployment);
        this.selectedEntity = newDeployment;
        this.markAsChanged();
        return;
      } else if (this.selectedType === 'objectives') {
        const newObjective = { Name: 'New Objective', Items: [] };
        this.currentEntities.push(newObjective);
        this.selectedEntity = newObjective;
        this.markAsChanged();
        return;
      } else if (this.selectedType === 'units') {
        // Set default based on formation type
        const firstFormation = this.gameFormations.length > 0 ? this.gameFormations[0] : null;
        const isRankAndFile = firstFormation && (firstFormation.Type || '').toLowerCase().includes('rank and file');
        newEntity.DefaultFront = isRankAndFile ? 5 : 1;
        newEntity.Formation = firstFormation ? firstFormation.Index : 0;
        newEntity.BasicProfileIds = [];
      } else if (this.selectedType === 'statlines') {
        // Check if we have multiple templates to choose from
        const validTemplates = this.wizardStatTemplates.filter(t => t.name.trim() && t.stats.some(s => s.key.trim()));
        
        if (validTemplates.length > 0) {
          // Show template selection dialog
          this.showTemplateSelectionDialog(newEntity);
          return; // Don't add entity yet - will be added after template selection
        } else if (this.wizardStatAttrsTemplate) {
          // Fallback to legacy single template
          newEntity.Attributes = { ...this.wizardStatAttrsTemplate };
        }
      }
      
      this.currentEntities.push(newEntity);
      this.selectedEntity = newEntity;
      this.markAsChanged();
    },
    
    showTemplateSelectionDialog(newEntity) {
      const validTemplates = this.wizardStatTemplates.filter(t => t.name.trim() && t.stats.some(s => s.key.trim()));
      
      // Build options for selection
      const options = validTemplates.map((t, idx) => ({
        label: t.name,
        value: idx
      }));
      
      // Add "No template" option
      options.push({ label: 'No template (blank statline)', value: -1 });
      
      // For simplicity, use a prompt - in a real implementation you might want a custom dialog
      const templateNames = validTemplates.map((t, idx) => `${idx + 1}. ${t.name}`).join('\n');
      const choice = prompt(`Select a statline template:\n${templateNames}\n\nEnter template number (or 0 for no template):`);
      
      if (choice === null) {
        // User cancelled
        return;
      }
      
      const selectedIdx = parseInt(choice) - 1;
      
      if (selectedIdx >= 0 && selectedIdx < validTemplates.length) {
        // Apply selected template
        const template = validTemplates[selectedIdx];
        const attrs = {};
        for (const stat of template.stats) {
          if (stat.key.trim()) {
            attrs[stat.key.trim()] = stat.value || '';
          }
        }
        newEntity.Attributes = attrs;
      }
      // else: no template selected, leave attributes undefined
      
      this.currentEntities.push(newEntity);
      this.selectedEntity = newEntity;
      this.markAsChanged();
    },
    
    async removeEntity(entity) {
      const confirmed = await this.showConfirm(`Remove ${entity.Name || entity.Id}?`, 'Confirm Removal', 'Remove');
      if (!confirmed) return;
      
      const index = this.currentEntities.indexOf(entity);
      if (index !== -1) {
        this.currentEntities.splice(index, 1);
        if (this.selectedEntity === entity) {
          this.selectedEntity = null;
        }
        this.markAsChanged();
      }
    },
    
    _parseOwnerRepo(input) {
      const clean = input.trim().replace(/\.git$/, '').replace(/\/$/, '');
      const m = clean.match(/(?:https?:\/\/github\.com\/)?([A-Za-z0-9_.\-]+\/[A-Za-z0-9_.\-]+)/);
      return m ? m[1] : null;
    },

    _toBase64(str) {
      const bytes = new TextEncoder().encode(str);
      const binary = Array.from(bytes, (b) => String.fromCodePoint(b)).join('');
      return btoa(binary);
    },

    async changeGame() {
      const confirmed = await this.showConfirm(
        'Return to game selection? Any unpublished changes will be lost.',
        'Change Game',
        'Continue'
      );
      if (!confirmed) return;
      sessionStorage.removeItem('wds3CacheSession');
      wds3Cache.gameId = null;
      wds3Cache.gameName = null;
      wds3Cache.dataBasePath = null;
      wds3Cache.manifest = null;
      wds3Cache.gameFiles = {};
      wds3Cache.factions = [];
      wds3Cache.deletedFiles = [];
      wds3Cache.baseVersion = null;
      await this.$router.push('/wds3-editor');
    },

    async forceSyncWithGit() {
      const confirmed = await this.showConfirm(
        'This will discard all local changes and re-download fresh data from GitHub. Continue?',
        'Force Sync with Git',
        'Re-download'
      );
      if (!confirmed) return;
      
      // Clear session storage
      sessionStorage.removeItem('wds3CacheSession');
      
      // Reset cache to force fresh download
      wds3Cache.gameId = null;
      wds3Cache.gameName = null;
      wds3Cache.dataBasePath = null;
      wds3Cache.manifest = null;
      wds3Cache.gameFiles = {};
      wds3Cache.factions = [];
      wds3Cache.deletedFiles = [];
      wds3Cache.baseVersion = null;
      
      // Navigate back to game selector to trigger fresh download
      await this.$router.push('/wds3-editor');
    },

    async clearSampleArmy() {
      const SAMPLE_NAME = 'Sample Army';

      const idx = this.factions.findIndex(f => f.name === SAMPLE_NAME);
      if (idx === -1) {
        await this.showAlert(`No faction named "${SAMPLE_NAME}" was found.`, 'Not Found');
        return;
      }

      const confirmed = await this.showConfirm(
        `This will remove the "${SAMPLE_NAME}" faction and all its data (units, profiles, statlines, equipment, options, rules) from memory.\n\nThis cannot be undone. Continue?`,
        'Remove Sample Army',
        'Remove'
      );
      if (!confirmed) return;

      // 1. Remove from hierarchy
      if (this.hierarchy && this.hierarchy.Factions && SAMPLE_NAME in this.hierarchy.Factions) {
        this.$delete(this.hierarchy.Factions, SAMPLE_NAME);
        this.$set(this.dirtyGameFiles, 'hierarchy.json', true);
      }

      // 2. If currently selected, deselect and clear faction data
      if (this.selectedFaction && this.selectedFaction.name === SAMPLE_NAME) {
        this.selectedFaction = null;
        this.previousFaction = null;
        this.units = [];
        this.profiles = [];
        this.profileThumbnailMap = {};
        this.statlines = [];
        this.equipment = [];
        this.factionOptions = [];
        this.factionRules = [];
        this.modifiedUnits = [];
        this.modifiedProfiles = [];
      }

      // 3. Remove from this.factions
      this.factions.splice(idx, 1);

      // 4. Remove from wds3Cache (factions list + manifest file entries)
      {
        const cacheIdx = wds3Cache.factions.findIndex(f => f.name === SAMPLE_NAME);
        if (cacheIdx !== -1) {
          // Record all faction file paths for deletion on next Git upload
          const removedFaction = wds3Cache.factions[cacheIdx];
          for (const fileName of Object.keys(removedFaction.files)) {
            const relPath = `${SAMPLE_NAME}/${fileName}`;
            if (!wds3Cache.deletedFiles.includes(relPath)) {
              wds3Cache.deletedFiles.push(relPath);
            }
          }
          wds3Cache.factions.splice(cacheIdx, 1);
        }

        if (wds3Cache.manifest && Array.isArray(wds3Cache.manifest.files)) {
          const prefix = SAMPLE_NAME + '/';
          wds3Cache.manifest.files = wds3Cache.manifest.files.filter(
            f => f !== SAMPLE_NAME && !f.startsWith(prefix)
          );
        }
      }

      // 5. Clean up dirty tracking for this faction
      delete this.dirtyFactionFiles[SAMPLE_NAME];

      // 6. Auto-select first remaining faction
      if (this.factions.length > 0 && !this.selectedFaction) {
        this.selectedFaction = this.factions[0];
        this.previousFaction = this.selectedFaction;
        await this.loadFactionData();
      }

      this.hasUnsavedChanges = true;
      this.updateUnsavedChangesFlag();

      // 7. Persist updated cache to session
      wds3Cache.saveToSession();
    },

    openGitModal() {
      // Pre-fill repo + subdir from the data base path if possible
      if (wds3Cache.dataBasePath) {
        const url = wds3Cache.dataBasePath;
        // e.g. https://raw.githubusercontent.com/owner/repo/refs/heads/main/GameName/
        const m = url.match(/raw\.githubusercontent\.com\/([A-Za-z0-9_.\-]+\/[A-Za-z0-9_.\-]+)\/refs\/heads\/[^/]+\/(.*)/);
        if (m) {
          this.gitRepo = m[1];
          this.gitSubdir = m[2].replace(/\/$/, '');
        }
      }
      this.gitRepoError = '';
      this.gitTokenError = '';
      this.gitSuccess = '';
      this.gitError = '';
      this.gitFolderUrl = '';
      this.gitUploading = false;
      this.gitUploadProgress = 0;
      this.gitUploadTotal = 0;
      this.showGitPatHelp = false;
      this.showGitModal = true;
    },

    async _githubPutFile(apiBase, relPath, content, headers, commitMsg) {
      const encodedPath = relPath.split('/').map(encodeURIComponent).join('/');
      const url = `${apiBase}/${encodedPath}`;
      // Fetch existing SHA (needed to update an existing file)
      let sha;
      const getRes = await fetch(url, { headers });
      if (getRes.ok) {
        sha = (await getRes.json()).sha;
      } else if (getRes.status !== 404) {
        const err = await getRes.json().catch(() => ({}));
        throw new Error(err.message || `GitHub API error ${getRes.status} on GET ${relPath}`);
      }
      const putRes = await fetch(url, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          message: commitMsg,
          content,
          ...(sha ? { sha } : {}),
        }),
      });
      if (!putRes.ok) {
        const err = await putRes.json().catch(() => ({}));
        throw new Error(err.message || `GitHub API error ${putRes.status} on PUT ${relPath}`);
      }
    },

    async _githubDeleteFile(apiBase, relPath, headers, commitMsg) {
      const encodedPath = relPath.split('/').map(encodeURIComponent).join('/');
      const url = `${apiBase}/${encodedPath}`;
      // Need the current SHA to delete
      const getRes = await fetch(url, { headers });
      if (getRes.status === 404) return; // Already gone
      if (!getRes.ok) {
        const err = await getRes.json().catch(() => ({}));
        throw new Error(err.message || `GitHub API error ${getRes.status} on GET ${relPath}`);
      }
      const sha = (await getRes.json()).sha;
      const delRes = await fetch(url, {
        method: 'DELETE',
        headers,
        body: JSON.stringify({ message: commitMsg, sha }),
      });
      if (!delRes.ok && delRes.status !== 404) {
        const err = await delRes.json().catch(() => ({}));
        throw new Error(err.message || `GitHub API error ${delRes.status} on DELETE ${relPath}`);
      }
    },

    async uploadToGitHub() {
      this.gitRepoError = '';
      this.gitTokenError = '';
      this.gitSuccess = '';
      this.gitError = '';
      this.gitFolderUrl = '';

      const ownerRepo = this._parseOwnerRepo(this.gitRepo);
      if (!ownerRepo) {
        this.gitRepoError = 'Enter a valid repository, e.g. owner/repo or https://github.com/owner/repo';
        return;
      }
      if (!this.gitToken.trim()) {
        this.gitTokenError = 'A Personal Access Token is required.';
        return;
      }

      const headers = {
        Authorization: `Bearer ${this.gitToken.trim()}`,
        'Content-Type': 'application/json',
        Accept: 'application/vnd.github+json',
        'X-GitHub-Api-Version': '2022-11-28',
      };

      const subdir = this.gitSubdir.trim();
      const apiBase = subdir
        ? `https://api.github.com/repos/${ownerRepo}/contents/${encodeURIComponent(subdir)}`
        : `https://api.github.com/repos/${ownerRepo}/contents`;

      // Build list of CHANGED files only (optimization)
      const filesToUpload = []; // [{ relPath, content }]
      
      // Game-level files (only upload dirty ones)
      for (const [fileName, data] of Object.entries(wds3Cache.gameFiles)) {
        if (this.dirtyGameFiles[fileName]) {
          filesToUpload.push({ relPath: fileName, content: JSON.stringify(data, null, 2) });
        }
      }
      
      // Faction files (only upload dirty ones) - normalize to WDS3 format
      for (const faction of wds3Cache.factions) {
        const dirtyFiles = this.dirtyFactionFiles[faction.name] || {};
        for (const [fileName, data] of Object.entries(faction.files)) {
          if (dirtyFiles[fileName]) {
            // Normalize array data to WDS3 format before saving
            let normalizedData = data;
            if (Array.isArray(data)) {
              normalizedData = data.map(entity => {
                // Determine data type from filename
                const dataTypeMap = {
                  'profiles.json': 'profile',
                  'units.json': 'unit',
                  'statlines.json': 'statline',
                  'equipment.json': 'equipment',
                  'rules.json': 'rule',
                  'options.json': 'option',
                };
                const dataType = dataTypeMap[fileName];
                if (dataType) {
                  return this._normalizeEntityToWDS3(entity, dataType);
                }
                return entity;
              });
            }
            filesToUpload.push({ relPath: `${faction.name}/${fileName}`, content: JSON.stringify(normalizedData, null, 2) });
          }
        }
      }

      // Update manifest: start with existing files, add new ones, remove deleted ones
      let manifestFiles = [];
      
      if (wds3Cache.manifest && Array.isArray(wds3Cache.manifest.files)) {
        // Start with existing manifest files
        manifestFiles = [...wds3Cache.manifest.files];
      }
      
      // Add files from cache that aren't in manifest yet
      const manifestFilesSet = new Set(manifestFiles);
      for (const fileName of Object.keys(wds3Cache.gameFiles)) {
        if (!manifestFilesSet.has(fileName)) {
          manifestFiles.push(fileName);
          manifestFilesSet.add(fileName);
        }
      }
      for (const faction of wds3Cache.factions) {
        for (const fileName of Object.keys(faction.files)) {
          const relPath = `${faction.name}/${fileName}`;
          if (!manifestFilesSet.has(relPath)) {
            manifestFiles.push(relPath);
            manifestFilesSet.add(relPath);
          }
        }
      }
      
      // Remove deleted files from manifest
      if (wds3Cache.deletedFiles && wds3Cache.deletedFiles.length > 0) {
        const deletedSet = new Set(wds3Cache.deletedFiles);
        manifestFiles = manifestFiles.filter(f => !deletedSet.has(f));
      }
      
      const prevVersion = (wds3Cache.manifest && wds3Cache.manifest.version) || 0;
      const updatedManifest = { ...(wds3Cache.manifest || {}), version: prevVersion + 1, files: manifestFiles };
      // Keep the in-memory cache (and sessionStorage) in sync with the new version
      wds3Cache.manifest = updatedManifest;
      wds3Cache.saveToSession();
      
      // Always upload manifest.json
      filesToUpload.unshift({ relPath: 'manifest.json', content: JSON.stringify(updatedManifest, null, 2) });

      this.gitUploading = true;
      this.gitUploadProgress = 0;
      this.gitUploadTotal = filesToUpload.length;
      const gameName = wds3Cache.gameName || 'game';

      try {
        for (const file of filesToUpload) {
          const b64 = this._toBase64(file.content);
          await this._githubPutFile(apiBase, file.relPath, b64, headers, `Update ${file.relPath} for ${gameName}`);
          this.gitUploadProgress++;
        }

        const folderPath = subdir ? `${subdir}` : '';
        this.gitFolderUrl = `https://github.com/${ownerRepo}/tree/main/${folderPath}`;

        // Delete files that were removed from the cache (e.g. cleared factions)
        const toDelete = wds3Cache.deletedFiles ? [...wds3Cache.deletedFiles] : [];
        if (toDelete.length > 0) {
          this.gitUploadTotal += toDelete.length;
          for (const relPath of toDelete) {
            try {
              await this._githubDeleteFile(apiBase, relPath, headers, `Remove ${relPath} for ${gameName}`);
            } catch (e) {
              // File may not exist in repo; treat as success
              console.warn(`Could not delete ${relPath}:`, e.message);
            }
            this.gitUploadProgress++;
          }
          // Clear the pending deletes now that they've been processed
          wds3Cache.deletedFiles = [];
          wds3Cache.saveToSession();
        }

        this.gitSuccess = `Upload complete! ${filesToUpload.length} file(s) pushed${toDelete.length ? `, ${toDelete.length} file(s) deleted` : ''}.`;

        // Mark all in-memory changes as published to Git
        this.dirtyGameFiles = {};
        this.dirtyFactionFiles = {};
        this.updateUnsavedChangesFlag();
        this.gitBaseVersion = updatedManifest.version;
      } catch (err) {
        this.gitError = 'Upload failed: ' + err.message;
      } finally {
        this.gitUploading = false;
        this.gitToken = '';
      }
    },

    _crc32(data) {
      const table = new Uint32Array(256);
      for (let i = 0; i < 256; i++) {
        let c = i;
        for (let j = 0; j < 8; j++) c = (c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1);
        table[i] = c;
      }
      let crc = 0xFFFFFFFF;
      for (let i = 0; i < data.length; i++) crc = table[(crc ^ data[i]) & 0xFF] ^ (crc >>> 8);
      return (crc ^ 0xFFFFFFFF) >>> 0;
    },

    _buildZip(entries) {
      // entries: [{ path: string, content: string }]
      const enc = new TextEncoder();
      const parts = [];
      const centralDir = [];
      let offset = 0;

      for (const entry of entries) {
        const nameBytes = enc.encode(entry.path);
        const dataBytes = enc.encode(entry.content);
        const crc = this._crc32(dataBytes);

        // Local file header
        const lh = new Uint8Array(30 + nameBytes.length);
        const lv = new DataView(lh.buffer);
        lv.setUint32(0, 0x04034B50, true);
        lv.setUint16(4, 20, true);
        lv.setUint16(6, 0, true);
        lv.setUint16(8, 0, true); // compression: stored
        lv.setUint16(10, 0, true);
        lv.setUint16(12, 0, true);
        lv.setUint32(14, crc, true);
        lv.setUint32(18, dataBytes.length, true);
        lv.setUint32(22, dataBytes.length, true);
        lv.setUint16(26, nameBytes.length, true);
        lv.setUint16(28, 0, true);
        lh.set(nameBytes, 30);
        parts.push(lh, dataBytes);

        // Central directory entry
        const cd = new Uint8Array(46 + nameBytes.length);
        const cv = new DataView(cd.buffer);
        cv.setUint32(0, 0x02014B50, true);
        cv.setUint16(4, 20, true);
        cv.setUint16(6, 20, true);
        cv.setUint16(8, 0, true);
        cv.setUint16(10, 0, true);
        cv.setUint16(12, 0, true);
        cv.setUint16(14, 0, true);
        cv.setUint32(16, crc, true);
        cv.setUint32(20, dataBytes.length, true);
        cv.setUint32(24, dataBytes.length, true);
        cv.setUint16(28, nameBytes.length, true);
        cv.setUint16(30, 0, true);
        cv.setUint16(32, 0, true);
        cv.setUint16(34, 0, true);
        cv.setUint16(36, 0, true);
        cv.setUint32(38, 0, true);
        cv.setUint32(42, offset, true);
        cd.set(nameBytes, 46);
        centralDir.push(cd);

        offset += lh.length + dataBytes.length;
      }

      const cdSize = centralDir.reduce((s, c) => s + c.length, 0);
      const eocd = new Uint8Array(22);
      const ev = new DataView(eocd.buffer);
      ev.setUint32(0, 0x06054B50, true);
      ev.setUint16(4, 0, true);
      ev.setUint16(6, 0, true);
      ev.setUint16(8, entries.length, true);
      ev.setUint16(10, entries.length, true);
      ev.setUint32(12, cdSize, true);
      ev.setUint32(16, offset, true);
      ev.setUint16(20, 0, true);

      return new Blob([...parts, ...centralDir, eocd], { type: 'application/zip' });
    },

    downloadAllFiles() {
      const entries = [];

      // Game-level files
      for (const [fileName, data] of Object.entries(wds3Cache.gameFiles)) {
        entries.push({ path: fileName, content: JSON.stringify(data, null, 2) });
      }

      // Faction files - normalize to WDS3 format
      for (const faction of wds3Cache.factions) {
        for (const [fileName, data] of Object.entries(faction.files)) {
          // Normalize array data to WDS3 format before downloading
          let normalizedData = data;
          if (Array.isArray(data)) {
            normalizedData = data.map(entity => {
              // Determine data type from filename
              const dataTypeMap = {
                'profiles.json': 'profile',
                'units.json': 'unit',
                'statlines.json': 'statline',
                'equipment.json': 'equipment',
                'rules.json': 'rule',
                'options.json': 'option',
              };
              const dataType = dataTypeMap[fileName];
              if (dataType) {
                return this._normalizeEntityToWDS3(entity, dataType);
              }
              return entity;
            });
          }
          entries.push({ path: `${faction.name}/${fileName}`, content: JSON.stringify(normalizedData, null, 2) });
        }
      }

      if (entries.length === 0) {
        this.showAlert('No files to download.', 'Download');
        return;
      }

      const zipBlob = this._buildZip(entries);
      const safeName = (wds3Cache.gameName || 'game').replace(/[^a-z0-9_\-]/gi, '_');
      const url = URL.createObjectURL(zipBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${safeName}_data.zip`;
      a.click();
      URL.revokeObjectURL(url);
    },

    // Check if there are any unsaved changes across all factions
    updateUnsavedChangesFlag() {
      const hasGameChanges = Object.keys(this.dirtyGameFiles).length > 0;
      const hasFactionChanges = Object.keys(this.dirtyFactionFiles).length > 0;
      console.log('[DEBUG] updateUnsavedChangesFlag:', { 
        dirtyGameFiles: {...this.dirtyGameFiles}, 
        dirtyFactionFiles: {...this.dirtyFactionFiles},
        hasGameChanges, 
        hasFactionChanges,
        beforeUpdate: this.hasUnsavedChanges
      });
      this.hasUnsavedChanges = hasGameChanges || hasFactionChanges;
      console.log('[DEBUG] updateUnsavedChangesFlag after:', { hasUnsavedChanges: this.hasUnsavedChanges });
    },
    
    saveJsonTo(folderHandle, fileName, data) {
      // Write back to in-memory cache (faction files or game files)
      if (folderHandle && typeof folderHandle === 'object') {
        folderHandle[fileName] = data;
      } else {
        wds3Cache.gameFiles[fileName] = data;
      }
    },
    
    // ══════════════════════════════════════════════════════════
    // Setup Wizard
    // ══════════════════════════════════════════════════════════

    wizardStepLabel(n) {
      return ['Base Sizes', 'Factions', 'Subsections', 'Unit Names (optional)', 'Statline Template'][n - 1] || '';
    },

    openWizard() {
      // Pre-populate wizard from existing data
      this.wizardBases = this.bases.map(b => ({
        name: b.Name || '',
        shape: b.Shape === 0 ? 'Round' : 'Square',
        width: b.Width || 0,
        height: b.Length || 0,
      }));
      this.wizardFactions = this.factions.map(f => ({ name: f.name, subsections: [], units: [] }));
      this.wizardSubsections = [];
      this.wizardStatTemplate = this.statlines.length > 0
        ? Object.keys(this.statlines[0].Attributes || {}).map(k => ({ key: k }))
        : [];
      this.wizardStep = 1;
      this.showSetupWizard = true;
    },

    closeWizard() {
      this.showSetupWizard = false;
    },

    wizardAddBase() {
      this.wizardBases.push({ name: '', shape: 'Round', width: 25, height: 25 });
    },

    wizardEnsureUnits(faction) {
      if (!faction.units) this.$set(faction, 'units', []);
    },

    wizardNext() {
      // When leaving step 3, sync global subsections into every faction
      if (this.wizardStep === 3) {
        for (const f of this.wizardFactions) {
          this.$set(f, 'subsections', this.wizardSubsections
            .filter(s => s.name.trim())
            .map(s => ({ name: s.name.trim(), units: [] })));
        }
      }
      if (this.wizardStep < this.wizardTotalSteps) this.wizardStep++;
    },

    _generateId() {
      return Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
    },

    async applyWizard() {
      // ── 1. Bases ──
      if (this.wizardBases.length > 0) {
        const newBases = this.wizardBases
          .filter(b => b.name.trim())
          .map(b => ({
            Id: this._generateId(),
            Name: b.name.trim(),
            Shape: b.shape === 'Round' ? 1 : 0,
            Width: b.width || 25,
            Length: b.height || 25,
          }));
        // Merge: add new bases that don't already exist by name
        const existingNames = new Set(this.bases.map(b => b.Name));
        for (const base of newBases) {
          if (!existingNames.has(base.Name)) {
            this.bases.push(base);
            existingNames.add(base.Name);
          }
        }
        if (newBases.length > 0) this.$set(this.dirtyGameFiles, 'bases.json', true);
      }

      // ── 2 & 3. Factions + Subsections → Hierarchy ──
      const newFactionNames = this.wizardFactions.map(f => f.name.trim()).filter(Boolean);
      if (newFactionNames.length > 0) {
        if (!this.hierarchy) {
          this.$set(this, 'hierarchy', { Factions: {}, Cards: {} });
        }
        if (!this.hierarchy.Factions) this.$set(this.hierarchy, 'Factions', {});

        for (const wf of this.wizardFactions) {
          const fName = wf.name.trim();
          if (!fName) continue;
          if (!this.hierarchy.Factions[fName]) {
            const subs = wf.subsections.filter(s => s.name.trim());
            if (subs.length > 0) {
              // Create subsection keys each pointing to empty unit array
              const subsObj = {};
              for (const sub of subs) subsObj[sub.name.trim()] = [];
              this.$set(this.hierarchy.Factions, fName, subsObj);
            } else {
              this.$set(this.hierarchy.Factions, fName, []);
            }
          }

          // Add to this.factions if it doesn't already exist
          if (!this.factions.find(f => f.name === fName)) {
            const newHandle = {};
            const newFactionEntry = { name: fName, handle: newHandle };
            this.factions.push(newFactionEntry);
            // Keep wds3Cache in sync so the faction persists
            wds3Cache.factions.push({ name: fName, files: newHandle });
          }
        }
        this.$set(this.dirtyGameFiles, 'hierarchy.json', true);
      }

      // ── 4. Unit Names → units.json per faction ──
      for (const wf of this.wizardFactions) {
        const fName = wf.name.trim();
        if (!fName) continue;

        // Find matching loaded faction
        const faction = this.factions.find(f => f.name === fName);
        if (!faction) continue;

        // Collect all units to create
        const unitEntries = [];
        if (wf.subsections.length === 0) {
          for (const u of (wf.units || [])) {
            if (u.name.trim()) unitEntries.push({ name: u.name.trim(), subsection: null });
          }
        } else {
          for (const sub of wf.subsections) {
            for (const u of (sub.units || [])) {
              if (u.name.trim()) unitEntries.push({ name: u.name.trim(), subsection: sub.name.trim() });
            }
          }
        }

        if (unitEntries.length === 0) continue;

        // Load current units for this faction first
        const existingUnits = await this.loadJsonFrom(faction.handle, 'units.json');
        const existingNames = new Set(existingUnits.map(u => u.Name));

        for (const entry of unitEntries) {
          if (existingNames.has(entry.name)) continue;
          const statAttrs = {};
          for (const st of this.wizardStatTemplate) {
            if (st.key.trim()) statAttrs[st.key.trim()] = '';
          }
          const newUnit = {
            Id: this._generateId(),
            Name: entry.name,
            DefaultFront: 0,
            Formation: 0,
            BasicProfileIds: [],
          };
          existingUnits.push(newUnit);
          existingNames.add(entry.name);

          // Also add to hierarchy if subsection given
          if (entry.subsection && this.hierarchy && this.hierarchy.Factions[fName]) {
            const factionNode = this.hierarchy.Factions[fName];
            if (typeof factionNode === 'object' && !Array.isArray(factionNode)) {
              if (!factionNode[entry.subsection]) {
                this.$set(factionNode, entry.subsection, []);
              }
              factionNode[entry.subsection].push(newUnit.Id);
            }
          } else if (!entry.subsection && this.hierarchy && Array.isArray(this.hierarchy.Factions[fName])) {
            this.hierarchy.Factions[fName].push(newUnit.Id);
          }
        }

        await this.saveJsonTo(faction.handle, 'units.json', existingUnits);
      }

      // ── 5. Statline Templates → store all named templates for new statline creation ──
      // Legacy single template support (for backward compatibility)
      const tplStats = this.wizardStatTemplate.filter(s => s.key.trim());
      if (tplStats.length > 0) {
        const attrTemplate = {};
        for (const s of tplStats) attrTemplate[s.key.trim()] = s.value || '';
        // Store template in a data property for "add new statline" to use
        this.wizardStatAttrsTemplate = attrTemplate;
      }
      
      // New multi-template support - templates are already stored in wizardStatTemplates
      // They will be used when creating new statlines via the template selection dialog

      this.hasUnsavedChanges = true;
      this.updateUnsavedChangesFlag();
      this.closeWizard();
      await this.showAlert('Setup applied! Review the changes in the editor, then upload to Git when ready.', 'Wizard Complete');
    },

    async fetchProfileThumbnails() {
      if (!this.profiles.length) {
        console.log('[WDS2UnitEditor] No profiles to fetch thumbnails for');
        return;
      }
      console.log('[WDS2UnitEditor] Profiles array:', this.profiles);
      const itemNames = this.profiles.map(p => p.Id).filter(Boolean);
      if (!itemNames.length) {
        console.log('[WDS2UnitEditor] No profile IDs found after mapping');
        return;
      }
      console.log('[WDS2UnitEditor] Fetching thumbnails for profiles:', itemNames);
      try {
        const res = await fetch('/GetProfileThumbnails', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ itemNames }),
        });
        const data = await res.json();
        console.log('[WDS2UnitEditor] Thumbnail response:', data);
        if (data.error === 0 && data.result) {
          this.profileThumbnailMap = data.result; // { profileId: thumbnailUrl | null }
          console.log('[WDS2UnitEditor] Thumbnail map updated:', this.profileThumbnailMap);
        } else {
          console.warn('[WDS2UnitEditor] Thumbnail fetch failed:', data);
        }
      } catch (e) {
        console.error('[WDS2UnitEditor] Exception fetching thumbnails:', e);
        // Silently fail — thumbnails are a UI enhancement only
      }
    },
    
    // Normalize data to match new schema structure (Children and ItemsToAdd as arrays)
    normalizeDataForSave(data, fileName = '') {
      if (Array.isArray(data)) {
        return data.map(item => this.normalizeEntityForSave(item, fileName));
      } else if (typeof data === 'object' && data !== null) {
        return this.normalizeEntityForSave(data, fileName);
      }
      return data;
    },
    
    normalizeEntityForSave(entity, fileName = '') {
      if (!entity || typeof entity !== 'object') return entity;
      
      const normalized = { ...entity };
      
      // Determine entity type from filename or ID
      const fileTypeMap = {
        'equipment.json': 'Equipment',
        'statlines.json': 'Statline',
        'profiles.json': 'Profile',
        'rules.json': 'Rule',
        'cards.json': 'Card',
        'tokens.json': 'Token',
        'units.json': 'Unit',
        'bases.json': null, // Bases don't have Type field
        'options.json': null // Options use numeric Type (handled separately)
      };
      
      const idTypeMap = {
        'equipment_': 'Equipment',
        'statlines_': 'Statline',
        'profiles_': 'Profile',
        'rules_': 'Rule',
        'cards_': 'Card',
        'tokens_': 'Token',
        'units_': 'Unit',
        'options_': null // Options use numeric Type
      };
      
      // Ensure Type field is present for all entity types (except bases and options with numeric type)
      if (!normalized.Type && normalized.Type !== 0) {
        let inferredType = null;
        
        // First, try to infer from filename
        if (fileName && fileTypeMap[fileName] !== undefined) {
          inferredType = fileTypeMap[fileName];
        }
        
        // If not from filename, try from ID prefix
        if (!inferredType && normalized.Id) {
          for (const [prefix, type] of Object.entries(idTypeMap)) {
            if (normalized.Id.toString().startsWith(prefix)) {
              inferredType = type;
              break;
            }
          }
        }
        
        // Apply the inferred type (null means don't add Type field)
        if (inferredType) {
          normalized.Type = inferredType;
        }
      }
      
      // Ensure Children is in array format with {Id, Info}
      if (normalized.Children) {
        if (typeof normalized.Children === 'object' && !Array.isArray(normalized.Children)) {
          // Convert old object format {id: info} to new array format
          normalized.Children = Object.entries(normalized.Children).map(([id, info]) => ({
            Id: id,
            Info: info || ''
          }));
        } else if (Array.isArray(normalized.Children)) {
          // Normalize existing array to ensure {Id, Info} format
          normalized.Children = normalized.Children.map(child => {
            if (Array.isArray(child) && child.length >= 1) {
              return { Id: child[0], Info: child[1] || '' };
            } else if (child && typeof child === 'object' && child.Id) {
              return { Id: child.Id, Info: child.Info || child.NameAdition || '' };
            }
            return child;
          });
        }
      }
      
      // Ensure ItemsToAdd is in array format with {Id, Info}
      if (normalized.ItemsToAdd) {
        if (typeof normalized.ItemsToAdd === 'object' && !Array.isArray(normalized.ItemsToAdd)) {
          // Convert old object format {id: info} to new array format
          normalized.ItemsToAdd = Object.entries(normalized.ItemsToAdd).map(([id, info]) => ({
            Id: id,
            Info: info || ''
          }));
        } else if (Array.isArray(normalized.ItemsToAdd)) {
          // Normalize existing array to ensure {Id, Info} format
          normalized.ItemsToAdd = normalized.ItemsToAdd.map(item => {
            if (Array.isArray(item) && item.length >= 1) {
              return { Id: item[0], Info: item[1] || '' };
            } else if (item && typeof item === 'object' && item.Id) {
              return { Id: item.Id, Info: item.Info || item.NameAdition || '' };
            }
            return item;
          });
        }
      }
      
      // Normalize ReferencedItemsToAdd for modifiedunit/modifiedprofile
      if (normalized.ReferencedItemsToAdd) {
        if (typeof normalized.ReferencedItemsToAdd === 'object' && !Array.isArray(normalized.ReferencedItemsToAdd)) {
          // Convert old object format {id: nestedObj} to new array format
          normalized.ReferencedItemsToAdd = Object.entries(normalized.ReferencedItemsToAdd).map(([id, nested]) => {
            const item = { Id: id };
            // If nested is an object with items, convert to Ref array
            if (nested && typeof nested === 'object' && Object.keys(nested).length > 0) {
              item.Ref = Object.keys(nested).map(nestedId => ({ Id: nestedId }));
            }
            return item;
          });
        }
      }
      
      // Normalize ReferencedItemsToRemove for modifiedunit/modifiedprofile
      if (normalized.ReferencedItemsToRemove) {
        if (typeof normalized.ReferencedItemsToRemove === 'object' && !Array.isArray(normalized.ReferencedItemsToRemove)) {
          // Convert old object format {id: nestedObj} to new array format
          normalized.ReferencedItemsToRemove = Object.entries(normalized.ReferencedItemsToRemove).map(([id, nested]) => {
            const item = { Id: id };
            // If nested is an object with items, convert to Ref array
            if (nested && typeof nested === 'object' && Object.keys(nested).length > 0) {
              item.Ref = Object.keys(nested).map(nestedId => ({ Id: nestedId }));
            }
            return item;
          });
        }
      }
      
      // Remove empty/null/undefined fields
      return this.removeEmptyFields(normalized);
    },
    
    // Remove empty, null, or undefined fields from an object
    removeEmptyFields(obj) {
      if (Array.isArray(obj)) {
        return obj.map(item => this.removeEmptyFields(item)).filter(item => item != null);
      }
      
      if (obj && typeof obj === 'object') {
        const cleaned = {};
        
        for (const [key, value] of Object.entries(obj)) {
          // Always preserve the Type field (even if empty/null for some edge cases)
          if (key === 'Type') {
            cleaned[key] = value;
            continue;
          }
          
          // Always preserve BaseId field for profiles (required by schema)
          if (key === 'BaseId') {
            cleaned[key] = value || '';
            continue;
          }
          
          // Skip null or undefined
          if (value == null) continue;
          
          // Skip empty strings
          if (value === '') continue;
          
          // Skip empty arrays
          if (Array.isArray(value) && value.length === 0) continue;
          
          // Handle nested objects
          if (typeof value === 'object' && !Array.isArray(value)) {
            const cleanedNested = this.removeEmptyFields(value);
            // Only include if the cleaned object has properties
            if (Object.keys(cleanedNested).length > 0) {
              cleaned[key] = cleanedNested;
            }
            continue;
          }
          
          // Handle arrays with nested objects
          if (Array.isArray(value)) {
            const cleanedArray = value.map(item => this.removeEmptyFields(item)).filter(item => {
              if (item == null) return false;
              if (typeof item === 'object' && Object.keys(item).length === 0) return false;
              return true;
            });
            if (cleanedArray.length > 0) {
              cleaned[key] = cleanedArray;
            }
            continue;
          }
          
          // Keep all other values (including 0, false, etc.)
          cleaned[key] = value;
        }
        
        return cleaned;
      }
      
      return obj;
    },
    
    // Normalize modifiedUnits/modifiedProfiles ReferencedItems from object to array format
    normalizeModifiedItems(items) {
      if (!Array.isArray(items)) return [];
      
      return items.map(item => {
        const normalized = { ...item };
        
        // Convert ReferencedItemsToAdd from object to array if needed
        if (normalized.ReferencedItemsToAdd && typeof normalized.ReferencedItemsToAdd === 'object' && !Array.isArray(normalized.ReferencedItemsToAdd)) {
          normalized.ReferencedItemsToAdd = Object.entries(normalized.ReferencedItemsToAdd).map(([id, nested]) => {
            const refItem = { Id: id };
            if (nested && typeof nested === 'object' && Object.keys(nested).length > 0) {
              refItem.Ref = Object.keys(nested).map(nestedId => ({ Id: nestedId }));
            }
            return refItem;
          });
        }
        
        // Convert ReferencedItemsToRemove from object to array if needed
        if (normalized.ReferencedItemsToRemove && typeof normalized.ReferencedItemsToRemove === 'object' && !Array.isArray(normalized.ReferencedItemsToRemove)) {
          normalized.ReferencedItemsToRemove = Object.entries(normalized.ReferencedItemsToRemove).map(([id, nested]) => {
            const refItem = { Id: id };
            if (nested && typeof nested === 'object' && Object.keys(nested).length > 0) {
              refItem.Ref = Object.keys(nested).map(nestedId => ({ Id: nestedId }));
            }
            return refItem;
          });
        }
        
        return normalized;
      });
    },
    
    // Auto-save with debouncing
    // Auto-save removed - users must manually save changes
    
    // Attribute management
    addAttribute(fieldName) {
      this.openInputDialog(
        'Add Attribute',
        'Enter attribute key (e.g., M, WS, S, T, W, I, A, Ld)...',
        'Enter attribute value...',
        (key, value) => {
          if (key && key.trim()) {
            this.$set(this.selectedEntity[fieldName], key.trim(), value || '');
            this.markAsChanged();
          }
        }
      );
    },
    
    deleteAttribute(fieldName, key) {
      this.$delete(this.selectedEntity[fieldName], key);
      this.markAsChanged();
    },
    
    async renameAttribute(fieldName, oldKey, newKey) {
      if (!newKey || !newKey.trim() || oldKey === newKey) return;
      
      newKey = newKey.trim();
      
      // Check if new key already exists
      if (this.selectedEntity[fieldName][newKey] !== undefined) {
        await this.showAlert(`Attribute "${newKey}" already exists!`, 'Duplicate Attribute');
        return;
      }
      
      // Get the old value
      const value = this.selectedEntity[fieldName][oldKey];
      
      // Delete old key and create new one
      this.$delete(this.selectedEntity[fieldName], oldKey);
      this.$set(this.selectedEntity[fieldName], newKey, value);
      this.markAsChanged();
    },
    
    // Drag and drop for attributes
    dragStartAttribute(fieldName, key) {
      this.draggedKey = key;
      this.draggedFieldName = fieldName;
    },
    
    dragOverAttribute(event) {
      event.preventDefault();
    },
    
    dropAttribute(fieldName, targetKey) {
      if (!this.draggedKey || this.draggedKey === targetKey || this.draggedFieldName !== fieldName) {
        this.draggedKey = null;
        this.draggedFieldName = null;
        return;
      }
      
      const attributes = this.selectedEntity[fieldName];
      const keys = Object.keys(attributes);
      const draggedIndex = keys.indexOf(this.draggedKey);
      const targetIndex = keys.indexOf(targetKey);
      
      if (draggedIndex === -1 || targetIndex === -1) {
        this.draggedKey = null;
        this.draggedFieldName = null;
        return;
      }
      
      // Create new ordered object
      const newAttributes = {};
      const reorderedKeys = [...keys];
      
      // Remove dragged key and insert at target position
      reorderedKeys.splice(draggedIndex, 1);
      reorderedKeys.splice(targetIndex, 0, this.draggedKey);
      
      // Rebuild object in new order
      reorderedKeys.forEach(key => {
        newAttributes[key] = attributes[key];
      });
      
      // Replace the entire attributes object
      this.$set(this.selectedEntity, fieldName, newAttributes);
      this.markAsChanged();
      
      this.draggedKey = null;
      this.draggedFieldName = null;
    },
    
    // Children management (replaces DisplayableDataIds)
    getChildId(child) {
      // Support both object {Id, NameAdition} and tuple [id, nameAdition] formats
      if (!child) return '';
      return child.Id || child[0] || '';
    },
    
    addChild() {
      const searchTypes = this.editorMode === 'game' 
        ? ['rules', 'cards', 'tokens', 'options']
        : ['rules', 'cards', 'statlines', 'equipment', 'options'];
      
      this.openEntitySearchDialog(searchTypes, (selectedEntity) => {
        if (!this.selectedEntity.Children) {
          this.$set(this.selectedEntity, 'Children', []);
        }
        
        const children = this.selectedEntity.Children;
        
        // Handle both array and object formats
        if (Array.isArray(children)) {
          // Add as object {Id, Info}
          children.push({ Id: selectedEntity.Id, Info: '' });
        } else if (typeof children === 'object') {
          // Add to object format (for backward compatibility)
          this.$set(children, selectedEntity.Id, '');
        }
        
        this.markAsChanged();
      });
    },
    
    deleteChild(index) {
      const children = this.selectedEntity.Children;
      
      // If Children is an array, use splice
      if (Array.isArray(children)) {
        children.splice(index, 1);
      } else if (typeof children === 'object') {
        // If Children is an object, get the key at this index and delete it
        const keys = Object.keys(children);
        if (keys[index]) {
          this.$delete(children, keys[index]);
        }
      }
      
      this.markAsChanged();
    },
    
    // Option ItemsToAdd management
    addItemToOption() {
      const searchTypes = this.editorMode === 'game' 
        ? ['rules', 'cards', 'tokens', 'equipment', 'options']
        : ['rules', 'cards', 'statlines', 'equipment', 'options'];
      
      this.openEntitySearchDialog(searchTypes, (selectedEntity) => {
        if (!this.selectedEntity.ItemsToAdd) {
          this.$set(this.selectedEntity, 'ItemsToAdd', []);
        }
        
        const items = this.selectedEntity.ItemsToAdd;
        
        // Handle both array and object formats
        if (Array.isArray(items)) {
          // Add as object {Id, Info}
          items.push({ Id: selectedEntity.Id, Info: '' });
        } else if (typeof items === 'object') {
          // Add to object format (for backward compatibility)
          this.$set(items, selectedEntity.Id, '');
        }
        
        this.markAsChanged();
      });
    },
    
    deleteItemFromOption(index) {
      const items = this.selectedEntity.ItemsToAdd;
      
      // If ItemsToAdd is an array, use splice
      if (Array.isArray(items)) {
        items.splice(index, 1);
      } else if (typeof items === 'object') {
        // If ItemsToAdd is an object, get the key at this index and delete it
        const keys = Object.keys(items);
        if (keys[index]) {
          this.$delete(items, keys[index]);
        }
      }
      
      this.markAsChanged();
    },
    
    // DisplayableDataIds management (deprecated - keeping for compatibility)
    addDisplayableDataId() {
      // Now redirects to addChild for compatibility
      this.addChild();
    },
    
    deleteDisplayableDataId(id) {
      // For backward compatibility
      if (this.selectedEntity.Children) {
        const index = this.selectedEntity.Children.findIndex(c => this.getChildId(c) === id);
        if (index >= 0) {
          this.deleteChild(index);
        }
      }
    },
    
    // Find a statline by ID from current faction, game data, or other factions (on-demand)
    async getStatlineFromAllFactions(id) {
      // First search in current faction and game statlines (fast)
      let statline = this.statlines.find(s => s.Id === id) || this.gameStatlines.find(s => s.Id === id);
      if (statline) return statline;
      
      // Not found - search in other factions (under demand, with caching)
      for (const faction of wds3Cache.factions) {
        if (faction === this.selectedFaction) continue; // Skip currently selected faction
        
        // Check if already cached
        if (this.statlineCache[faction.name]) {
          statline = this.statlineCache[faction.name].find(s => s.Id === id);
          if (statline) return statline;
        } else {
          // Load and cache this faction's statlines
          try {
            const factionStatlines = await this.loadJsonFrom(faction.handle, 'statlines.json');
            this.$set(this.statlineCache, faction.name, factionStatlines);
            statline = factionStatlines.find(s => s.Id === id);
            if (statline) return statline;
          } catch (err) {
            console.warn(`Failed to load statlines from faction ${faction.name}:`, err);
          }
        }
      }
      
      return null;
    },
    
    // Get statline from cache (synchronous) - for use in computed properties
    getStatlineFromCacheSync(id) {
      // Search in current faction and game statlines
      let statline = this.statlines.find(s => s.Id === id) || this.gameStatlines.find(s => s.Id === id);
      if (statline) return statline;
      
      // Search in cached statlines from other factions
      for (const factionName in this.statlineCache) {
        statline = this.statlineCache[factionName].find(s => s.Id === id);
        if (statline) return statline;
      }
      
      return null;
    },
    
    // Get entity name by ID
    getEntityName(id, entityType = null) {
      if (!id) return id;
      
      // If entityType is specified, search only in that array
      if (entityType) {
        const typeMap = {
          rules: this.rules,
          cards: this.cards,
          bases: this.bases,
          tokens: this.tokens,
          options: this.editorMode === 'game' ? this.gameOptions : this.factionOptions,
          units: this.units,
          profiles: this.profiles,
          statlines: this.statlines,
          equipment: this.editorMode === 'game' ? this.gameEquipment : this.equipment,
        };
        
        const dataArray = typeMap[entityType];
        if (dataArray) {
          const entity = dataArray.find(e => e.Id === id);
          if (entity) {
            // Bases have no Name property, return ID
            return entity.Name || id;
          }
        }
        return id;
      }
      
      // Search in all data arrays (game + faction)
      const allData = [
        ...this.rules,
        ...this.cards,
        ...this.bases,
        ...this.tokens,
        ...this.gameOptions,
        ...this.gameEquipment,
        ...this.gameStatlines,
        ...this.units,
        ...this.profiles,
        ...this.statlines,
        ...this.equipment,
        ...this.factionOptions,
        ...this.factionRules,
      ];
      
      const entity = allData.find(e => e.Id === id);
      if (entity) {
        return entity.Name || id;
      }
      
      // Check unit names cache (for hierarchy view when units aren't loaded)
      if (this.unitNamesCache[id]) {
        return this.unitNamesCache[id];
      }
      
      return id; // Fallback to ID if not found
    },
    
    getEntityIcon(id) {
      // Search through all data to find the entity by actual lookup
      const allData = [
        { array: this.rules, icon: '📜' },
        { array: this.factionRules, icon: '📜' },
        { array: this.cards, icon: '🃏' },
        { array: this.bases, icon: '📐' },
        { array: this.tokens, icon: '🎲' },
        { array: this.gameOptions, icon: '⚙️' },
        { array: this.units, icon: '🧑\u200d🤝\u200d🧑' },
        { array: this.profiles, icon: '👤' },
        { array: this.statlines, icon: '📊' },
        { array: this.gameStatlines, icon: '📊' },
        { array: this.equipment, icon: '⚔️' },
        { array: this.factionOptions, icon: '⚙️' },
      ];
      
      for (const { array, icon } of allData) {
        if (array.find(e => e.Id === id)) {
          return icon;
        }
      }
      
      return '📄'; // Default icon if not found
    },
    
    // Get names of children from Children array/object
    getChildrenNames(children) {
      if (!children) return [];
      
      let childIds = [];
      
      // Handle array format [{Id, Info}, ...]
      if (Array.isArray(children)) {
        childIds = children.map(child => this.getChildId(child)).filter(id => id);
      } 
      // Handle object format {id: info, ...}
      else if (typeof children === 'object') {
        childIds = Object.keys(children).filter(id => id);
      }
      
      // Get names for each child ID
      return childIds.map(id => this.getEntityName(id)).filter(name => name);
    },
    
    // Get names of items from ItemsToAdd array/object
    getItemsToAddNames(itemsToAdd) {
      if (!itemsToAdd) return [];
      
      let itemIds = [];
      
      // Handle array format [{Id}, ...]
      if (Array.isArray(itemsToAdd)) {
        itemIds = itemsToAdd.map(item => item?.Id || item).filter(id => id);
      }
      // Handle object format {id: ..., ...}
      else if (typeof itemsToAdd === 'object') {
        itemIds = Object.keys(itemsToAdd).filter(id => id);
      }
      
      // Get names for each item ID
      return itemIds.map(id => this.getEntityName(id)).filter(name => name);
    },
    
    // Navigation methods
    async navigateToEntity(id, targetType) {
      // Determine the correct mode based on target type
      const targetMode = ['rules', 'cards', 'bases', 'tokens'].includes(targetType) ? 'game' : 'faction';
      
      // Switch mode if needed (with confirmation)
      if (this.editorMode !== targetMode) {
        await this.switchEditorMode(targetMode);
        // If user cancelled the mode switch, don't continue with navigation
        if (this.editorMode !== targetMode) return;
      }
      
      // Wait for editorMode watcher to complete, THEN set the correct type
      // (the watcher sets selectedType to 'rules' or 'units' by default)
      this.$nextTick(() => {
        this.selectType(targetType);
        
        // Wait one more tick for currentEntities to update
        this.$nextTick(() => {
          // Convert ID to string for comparison (bases use string IDs)
          const idStr = String(id);
          const entity = this.currentEntities.find(e => String(e.Id) === idStr);
          if (entity) {
            this.selectEntity(entity);
          } else {
            alert(`Entity with ID "${id}" not found in ${targetType}`);
          }
        });
      });
    },
    
    async navigateToEntityById(id) {
      // Search all data arrays to find which one contains this ID
      // Check game data first (rules, equipment, statlines, etc.)
      const gameArrays = [
        { array: this.rules, type: 'rules' },
        { array: this.gameEquipment, type: 'equipment' },
        { array: this.gameStatlines, type: 'statlines' },
        { array: this.cards, type: 'cards' },
        { array: this.bases, type: 'bases' },
        { array: this.tokens, type: 'tokens' },
        { array: this.gameOptions, type: 'options' },
      ];
      
      for (const { array, type } of gameArrays) {
        if (array.find(e => e.Id === id)) {
          this.navigateToEntity(id, type);
          return;
        }
      }
      
      // Then check faction data
      const factionArrays = [
        { array: this.factionRules, type: 'rules' },
        { array: this.units, type: 'units' },
        { array: this.profiles, type: 'profiles' },
        { array: this.statlines, type: 'statlines' },
        { array: this.equipment, type: 'equipment' },
        { array: this.factionOptions, type: 'options' },
      ];
      
      for (const { array, type } of factionArrays) {
        if (array.find(e => e.Id === id)) {
          this.navigateToEntity(id, type);
          return;
        }
      }
      
      // If not found anywhere
      await this.showAlert(`Entity with ID "${id}" not found in any data file`, 'Entity Not Found');
    },
    
    // Get options linked to this unit
    getLinkedOptionsForUnit(unitId) {
      return this.modifiedUnits
        .filter(mod => mod.UnitId === unitId)
        .map(mod => ({
          optionId: mod.OptionId,
          newUnitId: mod.NewUnitId,
          newProfileId: mod.NewProfileId
        }));
    },
    
    // Get options linked to this profile
    getLinkedOptionsForProfile(profileId) {
      return this.modifiedProfiles
        .filter(mod => mod.ProfileId === profileId)
        .map(mod => ({
          optionId: mod.OptionId,
          unitId: mod.UnitId
        }));
    },
    
    // Get units linked to this option
    getLinkedUnitsForOption(optionId) {
      return this.modifiedUnits
        .filter(mod => mod.OptionId === optionId)
        .map(mod => ({
          unitId: mod.UnitId,
          newUnitId: mod.NewUnitId,
          newProfileId: mod.NewProfileId
        }));
    },
    
    // Get profiles linked to this option
    getLinkedProfilesForOption(optionId) {
      return this.modifiedProfiles
        .filter(mod => mod.OptionId === optionId)
        .map(mod => ({
          profileId: mod.ProfileId,
          unitId: mod.UnitId
        }));
    },
    
    addBasicProfileId() {
      this.openEntitySearchDialog(['profiles'], (selectedEntity) => {
        // Support both BasicProfileIds and ProfileIds
        const profileArray = this.selectedEntity.BasicProfileIds || this.selectedEntity.ProfileIds;
        if (profileArray) {
          profileArray.push(selectedEntity.Id);
        } else {
          this.$set(this.selectedEntity, 'BasicProfileIds', [selectedEntity.Id]);
        }
        this.markAsChanged();
      });
    },
    
    // Add/remove option links for units
    addUnitOptionLink() {
      this.openEntitySearchDialog(['options'], (selectedEntity) => {
        this.modifiedUnits.push({
          UnitId: this.selectedEntity.Id,
          OptionId: selectedEntity.Id,
          NewUnitId: '',
          NewProfileId: ''
        });
        this.markFactionFileDirty('modifiedunit.json');
      });
    },
    
    removeUnitOptionLink(index) {
      const filteredLinks = this.modifiedUnits.filter(m => m.UnitId === this.selectedEntity.Id);
      const linkToRemove = filteredLinks[index];
      const globalIndex = this.modifiedUnits.indexOf(linkToRemove);
      if (globalIndex !== -1) {
        this.modifiedUnits.splice(globalIndex, 1);
        this.markFactionFileDirty('modifiedunit.json');
      }
    },
    
    // Add/remove option links for profiles
    addProfileOptionLink() {
      this.openEntitySearchDialog(['options'], (selectedEntity) => {
        this.modifiedProfiles.push({
          ProfileId: this.selectedEntity.Id,
          OptionId: selectedEntity.Id,
          UnitId: ''
        });
        this.markFactionFileDirty('modifiedprofile.json');
      });
    },
    
    removeProfileOptionLink(index) {
      const filteredLinks = this.modifiedProfiles.filter(m => m.ProfileId === this.selectedEntity.Id);
      const linkToRemove = filteredLinks[index];
      const globalIndex = this.modifiedProfiles.indexOf(linkToRemove);
      if (globalIndex !== -1) {
        this.modifiedProfiles.splice(globalIndex, 1);
        this.markFactionFileDirty('modifiedprofile.json');
      }
    },
    
    // Add/remove unit/profile links when viewing an option
    addOptionUnitLink() {
      this.openEntitySearchDialog(['units'], (selectedEntity) => {
        this.modifiedUnits.push({
          OptionId: this.selectedEntity.Id,
          UnitId: selectedEntity.Id,
          NewUnitId: '',
          NewProfileId: ''
        });
        this.markFactionFileDirty('modifiedunit.json');
      });
    },
    
    removeOptionUnitLink(index) {
      const filteredLinks = this.modifiedUnits.filter(m => m.OptionId === this.selectedEntity.Id);
      const linkToRemove = filteredLinks[index];
      const globalIndex = this.modifiedUnits.indexOf(linkToRemove);
      if (globalIndex !== -1) {
        this.modifiedUnits.splice(globalIndex, 1);
        this.markFactionFileDirty('modifiedunit.json');
      }
    },
    
    addOptionProfileLink() {
      this.openEntitySearchDialog(['profiles'], (selectedEntity) => {
        this.modifiedProfiles.push({
          OptionId: this.selectedEntity.Id,
          ProfileId: selectedEntity.Id,
          UnitId: ''
        });
        this.markFactionFileDirty('modifiedprofile.json');
      });
    },
    
    removeOptionProfileLink(index) {
      const filteredLinks = this.modifiedProfiles.filter(m => m.OptionId === this.selectedEntity.Id);
      const linkToRemove = filteredLinks[index];
      const globalIndex = this.modifiedProfiles.indexOf(linkToRemove);
      if (globalIndex !== -1) {
        this.modifiedProfiles.splice(globalIndex, 1);
        this.markFactionFileDirty('modifiedprofile.json');
      }
    },
    
    // Extra Items methods
    addExtraItem() {
      this.openEntitySearchDialog(['cards', 'units', 'rules', 'equipment', 'tokens'], (selectedEntity) => {
        if (!this.selectedEntity.ExtraItems) {
          this.$set(this.selectedEntity, 'ExtraItems', []);
        }
        // Determine type based on which collection the selected entity belongs to
        let type = 'Card'; // default
        if (this.units.some(u => u.Id === selectedEntity.Id)) {
          type = 'Unit';
        } else if (this.rules.some(r => r.Id === selectedEntity.Id)) {
          type = 'Rule';
        } else if (this.equipment.some(e => e.Id === selectedEntity.Id)) {
          type = 'Equipment';
        } else if (this.tokens.some(t => t.Id === selectedEntity.Id)) {
          type = 'Token';
        } else if (this.cards.some(c => c.Id === selectedEntity.Id)) {
          type = 'Card';
        }
        
        this.selectedEntity.ExtraItems.push({
          Id: selectedEntity.Id,
          Type: type
        });
        this.markAsChanged();
      });
    },
    
    getExtraItemIcon(type) {
      switch(type) {
        case 'Card': return '🃏';
        case 'Unit': return '🧑‍🤝‍🧑';
        case 'Rule': return '📜';
        case 'Equipment': return '⚔️';
        case 'Token': return '🎯';
        default: return '📦';
      }
    },
    
    navigateToExtraItem(item) {
      const typeMap = {
        'Card': 'cards',
        'Unit': 'units',
        'Rule': 'rules',
        'Equipment': 'equipment',
        'Token': 'tokens'
      };
      const entityType = typeMap[item.Type] || 'cards';
      this.navigateToEntity(item.Id, entityType);
    },
    
    onDropExtraItems(targetIndex) {
      if (this.draggedIndex === null || this.draggedIndex === targetIndex) return;
      
      const array = this.selectedEntity.ExtraItems;
      const item = array[this.draggedIndex];
      array.splice(this.draggedIndex, 1);
      array.splice(targetIndex, 0, item);
      
      this.draggedIndex = null;
      this.markAsChanged();
    },
    
    // Drag and drop for arrays
    onDragStart(index) {
      this.draggedIndex = index;
    },
    
    onDragOver(event) {
      event.preventDefault();
    },
    
    onDrop(targetIndex, arrayName) {
      if (this.draggedIndex === null || this.draggedIndex === targetIndex) return;
      
      const array = this.selectedEntity[arrayName];
      const item = array[this.draggedIndex];
      array.splice(this.draggedIndex, 1);
      array.splice(targetIndex, 0, item);
      
      this.draggedIndex = null;
      this.markAsChanged();
    },
    
    onDragEnd() {
      this.draggedIndex = null;
    },
    
    // Drag and drop for objects (DisplayableDataIds)
    onDragStartObject(key) {
      this.draggedKey = key;
    },
    
    onDropObject(targetKey, objectName) {
      if (this.draggedKey === null || this.draggedKey === targetKey) return;
      
      const obj = this.selectedEntity[objectName];
      const keys = Object.keys(obj);
      const draggedIdx = keys.indexOf(this.draggedKey);
      const targetIdx = keys.indexOf(targetKey);
      
      // Rebuild object with new order
      const newObj = {};
      const newKeys = [...keys];
      newKeys.splice(draggedIdx, 1);
      newKeys.splice(targetIdx, 0, this.draggedKey);
      
      newKeys.forEach(k => {
        newObj[k] = obj[k];
      });
      
      this.$set(this.selectedEntity, objectName, newObj);
      this.draggedKey = null;
      this.markAsChanged();
    },
    
    onDragEndObject() {
      this.draggedKey = null;
    },
    
    // Generic object field management
    addToObject(fieldName) {
      // Determine search types based on field name
      const searchTypes = ['rules', 'cards', 'statlines', 'bases', 'tokens', 'options', 'profiles', 'units', 'equipment'];
      
      this.openEntitySearchDialog(searchTypes, (selectedEntity) => {
        this.$set(this.selectedEntity[fieldName], selectedEntity.Id, '');
      });
    },
    
    deleteFromObject(fieldName, key) {
      this.$delete(this.selectedEntity[fieldName], key);
    },
    
    // Entity search dialog
    openEntitySearchDialog(types, callback) {
      this.entitySearchTypes = types;
      this.entitySearchText = '';
      this.entitySearchCallback = callback;
      this.entitySearchOriginalEntity = this.selectedEntity; // Store current entity
      this.showEntitySearchDialog = true;
    },
    
    selectEntityFromSearch(entity) {
      if (this.entitySearchCallback) {
        this.entitySearchCallback(entity);
      }
      this.closeEntitySearchDialog();
    },
    
    closeEntitySearchDialog() {
      this.showEntitySearchDialog = false;
      this.entitySearchText = '';
      this.entitySearchTypes = [];
      this.entitySearchTypeFilter = null;
      this.entitySearchFilterIds = null;
      this.entitySearchCallback = null;
      this.entitySearchOriginalEntity = null; // Clear stored entity
      this.hidePreview();
    },

    createNewEntityFromSearch() {
      // Determine which type to create based on filter or first available type
      const targetType = this.entitySearchTypeFilter || this.entitySearchTypes[0];
      if (!targetType) return;

      // Switch to the correct editor mode and type
      const gameTypes = ['rules', 'cards', 'bases', 'tokens'];
      const targetMode = gameTypes.includes(targetType) ? 'game' : 'faction';

      // Store callback and original entity before closing dialog
      const callback = this.entitySearchCallback;
      const originalEntity = this.entitySearchOriginalEntity;
      this.closeEntitySearchDialog();

      // Switch mode if needed, then create the entity
      if (this.editorMode !== targetMode) {
        this.switchEditorMode(targetMode).then(() => {
          this.selectedType = targetType;
          this.addNewEntity();
          // If a callback was provided, call it with the new entity
          if (callback && this.selectedEntity) {
            const newEntity = this.selectedEntity;
            // Temporarily restore original entity so callback operates on it
            this.selectedEntity = originalEntity;
            callback(newEntity);
            // Switch back to the newly created entity
            this.selectedEntity = newEntity;
          }
        });
      } else {
        this.selectedType = targetType;
        this.addNewEntity();
        // If a callback was provided, call it with the new entity
        if (callback && this.selectedEntity) {
          const newEntity = this.selectedEntity;
          // Temporarily restore original entity so callback operates on it
          this.selectedEntity = originalEntity;
          callback(newEntity);
          // Switch back to the newly created entity
          this.selectedEntity = newEntity;
        }
      }
    },
    
    showPreview(entity, event) {
      this.previewEntity = entity;
      this.updatePreviewPosition(event);
    },
    
    showPreviewById(entityId, event) {
      if (!entityId) return;
      
      // Search all data arrays to find the entity
      const allArrays = [
        ...this.rules.map(e => ({ ...e, entityType: 'rules' })),
        ...this.gameEquipment.map(e => ({ ...e, entityType: 'equipment' })),
        ...this.gameStatlines.map(e => ({ ...e, entityType: 'statlines' })),
        ...this.cards.map(e => ({ ...e, entityType: 'cards' })),
        ...this.bases.map(e => ({ ...e, entityType: 'bases' })),
        ...this.tokens.map(e => ({ ...e, entityType: 'tokens' })),
        ...this.gameOptions.map(e => ({ ...e, entityType: 'options' })),
        ...this.factionRules.map(e => ({ ...e, entityType: 'rules' })),
        ...this.units.map(e => ({ ...e, entityType: 'units' })),
        ...this.profiles.map(e => ({ ...e, entityType: 'profiles' })),
        ...this.statlines.map(e => ({ ...e, entityType: 'statlines' })),
        ...this.equipment.map(e => ({ ...e, entityType: 'equipment' })),
        ...this.factionOptions.map(e => ({ ...e, entityType: 'options' })),
      ];
      
      const entity = allArrays.find(e => e.Id === entityId);
      if (entity) {
        this.showPreview(entity, event);
      }
    },
    
    updatePreviewPosition(event) {
      if (!this.previewEntity) return;
      this.previewX = event.clientX + 20;
      this.previewY = event.clientY + 20;
    },
    
    hidePreview() {
      this.previewEntity = null;
    },
    
    getTypeIcon(type) {
      const typeIcons = {
        rules: '📜',
        cards: '🃏',
        bases: '📐',
        tokens: '🎲',
        options: '⚙️',
        units: '🧑\u200d🤝\u200d🧑',
        profiles: '👤',
        statlines: '📊',
        equipment: '⚔️',
      };
      return typeIcons[type] || '📄';
    },
    
    // Input dialog
    openInputDialog(title, placeholder, placeholder2OrCallback, callback) {
      this.inputDialogTitle = title;
      this.inputDialogPlaceholder = placeholder;
      this.inputDialogValue = '';
      
      // Support both single and double input modes
      if (typeof placeholder2OrCallback === 'function') {
        // Single input mode (backward compatible)
        this.inputDialogPlaceholder2 = '';
        this.inputDialogValue2 = '';
        this.inputDialogCallback = placeholder2OrCallback;
      } else {
        // Double input mode
        this.inputDialogPlaceholder2 = placeholder2OrCallback;
        this.inputDialogValue2 = '';
        this.inputDialogCallback = callback;
      }
      
      this.showInputDialog = true;
    },
    
    submitInputDialog() {
      if (this.inputDialogCallback) {
        if (this.inputDialogPlaceholder2) {
          // Two-field mode: pass both values
          if (this.inputDialogValue) {
            this.inputDialogCallback(this.inputDialogValue, this.inputDialogValue2);
          }
        } else {
          // Single-field mode: pass one value
          if (this.inputDialogValue) {
            this.inputDialogCallback(this.inputDialogValue);
          }
        }
      }
      this.closeInputDialog();
    },
    
    closeInputDialog() {
      this.showInputDialog = false;
      this.inputDialogTitle = '';
      this.inputDialogPlaceholder = '';
      this.inputDialogValue = '';
      this.inputDialogPlaceholder2 = '';
      this.inputDialogValue2 = '';
      this.inputDialogCallback = null;
    },
    
    // Modified unit/profile dialog
    openModifiedUnitDialog(modifiedUnit) {
      this.modifiedDialogType = 'unit';
      this.modifiedDialogData = modifiedUnit;
      this.showModifiedDialog = true;
    },
    
    openModifiedProfileDialog(modifiedProfile) {
      this.modifiedDialogType = 'profile';
      this.modifiedDialogData = modifiedProfile;
      this.showModifiedDialog = true;
    },
    
    closeModifiedDialog() {
      this.showModifiedDialog = false;
      this.modifiedDialogType = null;
      this.modifiedDialogData = null;
    },
    
    // Open entity search filtered to Children in current unit/profile
    openEntitySearchForModified(action = 'remove') {
      if (!this.modifiedDialogData) return;
      
      // Get the entity (unit or profile) that owns these items
      let entityId, entity;
      
      if (this.modifiedDialogType === 'unit') {
        entityId = this.modifiedDialogData.UnitId;
        entity = this.units.find(u => u.Id === entityId);
      } else if (this.modifiedDialogType === 'profile') {
        entityId = this.modifiedDialogData.ProfileId;
        entity = this.profiles.find(p => p.Id === entityId);
      }
      
      // Get all Child IDs from the entity
      const childIds = [];
      if (entity && entity.Children) {
        entity.Children.forEach(child => {
          const id = this.getChildId(child);
          if (id) childIds.push(id);
        });
      }
      
      // Set the filter and open dialog
      this.entitySearchFilterIds = childIds.length > 0 ? childIds : null;
      this.openEntitySearchDialog(['rules', 'equipment', 'statlines'], (selectedEntity) => {
        const targetField = action === 'remove' ? 'ReferencedItemsToRemove' : 'ReferencedItemsToAdd';
        if (!this.modifiedDialogData[targetField]) {
          this.$set(this.modifiedDialogData, targetField, []);
        }
        // Add as object with Id (array format)
        this.modifiedDialogData[targetField].push({ Id: selectedEntity.Id });
        this.markAsChanged();
      });
    },
    
    // Open entity search filtered to Children in linked unit (for inline option editing)
    openEntitySearchForModifiedUnit(modifiedUnit, action = 'remove') {
      if (!modifiedUnit) {
        console.warn('⚠️ modifiedUnit not provided');
        return;
      }
      
      // Get the unit that owns these items
      const unit = this.units.find(u => u.Id === modifiedUnit.UnitId);
      
      // Get all Child IDs from the unit
      const childIds = [];
      if (unit && unit.Children) {
        unit.Children.forEach(child => {
          const id = this.getChildId(child);
          if (id) childIds.push(id);
        });
      }
      
      // Also get Child IDs from all basic profiles of the unit
      if (unit) {
        const profileIds = unit.BasicProfileIds || unit.ProfileIds || [];
        profileIds.forEach(profileId => {
          const profile = this.profiles.find(p => p.Id === profileId);
          if (profile && profile.Children && Array.isArray(profile.Children)) {
            profile.Children.forEach(child => {
              const id = this.getChildId(child);
              if (id && !childIds.includes(id)) {
                childIds.push(id);
              }
            });
          }
        });
      }
      
      // Set the filter and open dialog
      console.log('  - childIds:', childIds);
      console.log('  - setting entitySearchFilterIds:', childIds.length > 0 ? childIds : null);
      this.entitySearchFilterIds = childIds.length > 0 ? childIds : null;
      console.log('  - calling openEntitySearchDialog');
      this.openEntitySearchDialog(['rules', 'equipment', 'statlines'], (selectedEntity) => {
        const targetField = action === 'remove' ? 'ReferencedItemsToRemove' : 'ReferencedItemsToAdd';
        if (!modifiedUnit[targetField]) {
          this.$set(modifiedUnit, targetField, []);
        }
        // Add as object with Id (array format)
        modifiedUnit[targetField].push({ Id: selectedEntity.Id });
        this.markFactionFileDirty('modifiedunit.json');
      });
    },
    
    // Open entity search filtered to Children in linked profile (for inline option editing)
    openEntitySearchForModifiedProfile(modifiedProfile, action = 'remove') {
      if (!modifiedProfile) {
        console.warn('⚠️ modifiedProfile not provided');
        return;
      }
      
      // Get the profile that owns these items
      const profile = this.profiles.find(p => p.Id === modifiedProfile.ProfileId);
      
      // Get all Child IDs from the profile
      const childIds = [];
      if (profile && profile.Children && Array.isArray(profile.Children)) {
        profile.Children.forEach(child => {
          const id = this.getChildId(child);
          if (id) childIds.push(id);
        });
      }
      
      // Set the filter and open dialog
      console.log('  - childIds:', childIds);
      console.log('  - setting entitySearchFilterIds:', childIds.length > 0 ? childIds : null);
      this.entitySearchFilterIds = childIds.length > 0 ? childIds : null;
      console.log('  - calling openEntitySearchDialog');
      this.openEntitySearchDialog(['rules', 'equipment', 'statlines'], (selectedEntity) => {
        const targetField = action === 'remove' ? 'ReferencedItemsToRemove' : 'ReferencedItemsToAdd';
        if (!modifiedProfile[targetField]) {
          this.$set(modifiedProfile, targetField, []);
        }
        // Add as object with Id (array format)
        modifiedProfile[targetField].push({ Id: selectedEntity.Id });
        this.markFactionFileDirty('modifiedprofile.json');
      });
    },
    
    // Hierarchy Management Methods
    getCategoriesForFaction(categories) {
      // Filter out internal properties like _name
      const result = {};
      for (const key in categories) {
        if (key !== '_name') {
          result[key] = categories[key];
        }
      }
      return result;
    },
    
    // Hierarchy Helpers
    isLeafNode(value) {
      return Array.isArray(value);
    },
    
    isBranchNode(value) {
      return value && typeof value === 'object' && !Array.isArray(value);
    },
    
    getNodeIcon(path, value) {
      if (this.isLeafNode(value)) {
        return '📄'; // Leaf - will show items inside
      }
      const level = path.length;
      if (this.hierarchySection === 'Factions') {
        if (level === 1) return '🏰'; // Faction
        return '📁'; // Category
      } else {
        return '🃏'; // Cards category
      }
    },
    
    getItemIcon() {
      return this.hierarchySection === 'Factions' ? '🧑‍🤝‍🧑' : '🎴';
    },
    
    getCollapsedKey(path) {
      return path.join(':');
    },
    
    isNodeCollapsed(path) {
      const key = this.getCollapsedKey(path);
      return this.collapsedCategories[key] || false;
    },
    
    toggleCategoryCollapse(factionName, categoryName) {
      // Support both old path-based calls and new path array calls
      let key;
      if (Array.isArray(factionName)) {
        // New: path array
        key = factionName.join(':');
      } else {
        // Old: individual parameters
        key = factionName ? (factionName + ':' + categoryName) : categoryName;
      }
      this.$set(this.collapsedCategories, key, !this.collapsedCategories[key]);
    },
    
    collapseAllFactions() {
      if (!this.hierarchy || !this.hierarchy[this.hierarchySection]) return;
      
      // Recursively collapse all nodes in the current hierarchy section
      const collapseNode = (node, path = []) => {
        if (typeof node === 'object' && !Array.isArray(node)) {
          Object.keys(node).forEach(key => {
            const newPath = [...path, key];
            const pathKey = newPath.join(':');
            this.$set(this.collapsedCategories, pathKey, true);
            
            // Recursively collapse children
            if (node[key] && typeof node[key] === 'object' && !Array.isArray(node[key])) {
              collapseNode(node[key], newPath);
            }
          });
        }
      };
      
      collapseNode(this.hierarchy[this.hierarchySection]);
    },
    
    expandAllFactions() {
      this.collapsedCategories = {};
    },
    
    async loadHierarchyItemNames() {
      // Load unit and card names for hierarchy display
      const promises = [];
      
      // Load unit names from all factions
      if (this.factions && this.factions.length > 0) {
        this.factions.forEach((faction) => {
          promises.push(
            this.loadJsonFrom(faction.handle, 'units.json')
              .then(units => {
                units.forEach(unit => {
                  if (unit.Id && unit.Name) {
                    this.unitNamesCache[unit.Id] = unit.Name;
                  }
                });
              })
              .catch(err => console.warn(`Failed to load units for faction ${faction.name}:`, err))
          );
        });
      }
      
      // Load card names from game data
      if (this.gameDataHandle) {
        promises.push(
          this.loadJsonFrom(this.gameDataHandle, 'cards.json')
            .then(cards => {
              cards.forEach(card => {
                if (card.Id && card.Name) {
                  this.unitNamesCache[card.Id] = card.Name;
                }
              });
            })
            .catch(err => console.warn('Failed to load cards:', err))
        );
      }
      
      await Promise.all(promises);
      console.log(`Loaded ${Object.keys(this.unitNamesCache).length} item names for hierarchy (units + cards)`);
    },
    
    initializeHierarchySection() {
      if (!this.hierarchy[this.hierarchySection]) {
        this.$set(this.hierarchy, this.hierarchySection, {});
      }
    },
    
    addHierarchyItem() {
      if (this.hierarchySection === 'Factions') {
        this.addHierarchyFaction();
      } else if (this.hierarchySection === 'Cards') {
        this.addHierarchyFaction(); // Same UI, different section
      }
    },
    
    async addHierarchyTopLevelNode() {
      if (this.hierarchySection === 'Factions') {
        // Use the full addNewFaction method for Factions (handles faction list + hierarchy)
        await this.addNewFaction();
      } else if (this.hierarchySection === 'Cards') {
        // Add a new top-level category in Cards hierarchy
        await this.addHierarchyFaction();
      }
    },

    async addHierarchyFaction() {
      const itemName = prompt(`Enter ${this.hierarchySection === 'Factions' ? 'faction' : 'category'} name:`);
      if (!itemName || !itemName.trim()) return;
      
      if (this.hierarchy[this.hierarchySection][itemName]) {
        await this.showAlert(`${this.hierarchySection === 'Factions' ? 'Faction' : 'Category'} already exists!`, 'Duplicate Name');
        return;
      }
      
      // For Factions: create object (contains categories), For Cards: create array (contains card IDs)
      const initialValue = this.hierarchySection === 'Factions' ? {} : [];
      this.$set(this.hierarchy[this.hierarchySection], itemName, initialValue);
      this.$set(this.dirtyGameFiles, 'hierarchy.json', true);
      // Note: Manually click Save All button to persist changes
    },
    
    async removeHierarchyFaction(factionName) {
      const confirmed = await this.showConfirm(`Remove "${factionName}" and all its categories?`, 'Confirm Removal', 'Remove');
      if (!confirmed) return;
      
      this.$delete(this.hierarchy[this.hierarchySection], factionName);
      // Note: Manually click Save All button to persist changes
    },
    
    async renameHierarchyFaction(oldName, newName) {
      newName = newName.trim();
      if (!newName || newName === oldName) {
        return;
      }
      
      if (this.hierarchy[this.hierarchySection][newName]) {
        await this.showAlert('Name already exists!', 'Duplicate Name');
        return;
      }
      
      // Get the data, remove old, add with new name
      const data = this.hierarchy[this.hierarchySection][oldName];
      this.$delete(this.hierarchy[this.hierarchySection], oldName);
      this.$set(this.hierarchy[this.hierarchySection], newName, data);
      // Note: Manually click Save All button to persist changes
    },
    
    async addHierarchyCategory(factionName) {
      const categoryName = prompt('Enter category name:');
      if (!categoryName || !categoryName.trim()) return;
      
      const targetObject = factionName ? this.hierarchy[this.hierarchySection][factionName] : this.hierarchy[this.hierarchySection];
      
      if (targetObject[categoryName]) {
        await this.showAlert('Category already exists!', 'Duplicate Name');
        return;
      }
      
      this.$set(targetObject, categoryName, []);
      // Note: Manually click Save All button to persist changes
    },
    
    async removeHierarchyCategory(factionName, categoryName) {
      const confirmed = await this.showConfirm(`Remove category "${categoryName}"?`, 'Confirm Removal', 'Remove');
      if (!confirmed) return;
      
      if (factionName) {
        // Factions: delete from faction object
        this.$delete(this.hierarchy[this.hierarchySection][factionName], categoryName);
      } else {
        // Cards: delete from root
        this.$delete(this.hierarchy[this.hierarchySection], categoryName);
      }
      // Note: Manually click Save All button to persist changes
    },
    
    async renameHierarchyCategory(factionName, oldName, newName) {
      newName = newName.trim();
      if (!newName || newName === oldName) {
        return;
      }
      
      const targetObject = factionName ? this.hierarchy[this.hierarchySection][factionName] : this.hierarchy[this.hierarchySection];
      
      if (targetObject[newName]) {
        await this.showAlert('Category name already exists!', 'Duplicate Name');
        return;
      }
      
      const data = targetObject[oldName];
      this.$delete(targetObject, oldName);
      this.$set(targetObject, newName, data);
      // Note: Manually click Save All button to persist changes
    },
    
    addHierarchyUnit(factionName, categoryName) {
      const searchTypes = this.hierarchySection === 'Factions' ? ['units'] : ['cards'];
      this.openEntitySearchDialog(searchTypes, (selectedEntity) => {
        const targetObject = factionName ? this.hierarchy[this.hierarchySection][factionName] : this.hierarchy[this.hierarchySection];
        const units = targetObject[categoryName];
        if (!Array.isArray(units)) {
          this.$set(targetObject, categoryName, []);
        }
        targetObject[categoryName].push(selectedEntity.Id);
        // Note: Manually click Save All button to persist changes
      });
    },
    
    removeHierarchyUnit(factionName, categoryName, index) {
      const targetObject = factionName ? this.hierarchy[this.hierarchySection][factionName] : this.hierarchy[this.hierarchySection];
      targetObject[categoryName].splice(index, 1);
      // Note: Manually click Save All button to persist changes
    },
    
    // Path-based hierarchy methods for flexible depth
    getNodeByPath(path) {
      let current = this.hierarchy[this.hierarchySection];
      for (const key of path) {
        if (!current) return null;
        current = current[key];
      }
      return current;
    },
    
    async addChildToPath(path) {
      const parent = this.getNodeByPath(path);
      if (!parent || !this.isBranchNode(parent)) return;
      
      const name = prompt(`Enter child category name:\n\nThis will create a new folder that can contain:\n• More sub-categories (folders)\n• Or items (${this.hierarchySection === 'Factions' ? 'units' : 'cards'})`);
      if (!name || !name.trim()) return;
      
      const trimmedName = name.trim();
      if (parent[trimmedName]) {
        await this.showAlert(`A category named "${trimmedName}" already exists at this level!`, 'Duplicate Name');
        return;
      }
      
      // Default: create an empty object (can add more children)
      // If user wants a leaf, they can add items to it
      this.$set(parent, trimmedName, {});
    },
    
    async removeNodeAtPath(path) {
      if (path.length === 0) return;
      
      const parentPath = path.slice(0, -1);
      const key = path[path.length - 1];
      const parent = parentPath.length > 0 ? this.getNodeByPath(parentPath) : this.hierarchy[this.hierarchySection];
      
      const confirmed = await this.showConfirm(`Remove "${key}"?`, 'Confirm Removal', 'Remove');
      if (!confirmed) return;
      
      this.$delete(parent, key);
    },
    
    async renameNodeAtPath(path, newName) {
      if (path.length === 0) return;
      if (!newName || !newName.trim()) return;
      
      const parentPath = path.slice(0, -1);
      const oldName = path[path.length - 1];
      if (newName === oldName) return;
      
      const parent = parentPath.length > 0 ? this.getNodeByPath(parentPath) : this.hierarchy[this.hierarchySection];
      
      if (parent[newName]) {
        await this.showAlert('Name already exists!', 'Duplicate Name');
        return;
      }
      
      const data = parent[oldName];
      this.$delete(parent, oldName);
      this.$set(parent, newName, data);
    },
    
    async addItemToPath(path) {
      const node = this.getNodeByPath(path);
      
      // Convert to array if it's an empty object
      if (this.isBranchNode(node)) {
        if (Object.keys(node).length > 0) {
          await this.showAlert(`This node has child categories and cannot contain items directly.\n\nYou can either:\n• Add items to one of its child categories\n• Or create a new child category to add items to`, 'Cannot Add Items');
          return;
        }
        // Empty object, convert to array
        const confirmed = await this.showConfirm(`This will convert this category to hold items (${this.hierarchySection === 'Factions' ? 'units' : 'cards'}) instead of sub-categories.\n\nContinue?`, 'Convert Category?', 'Convert');
        if (!confirmed) return;
        
        const parentPath = path.slice(0, -1);
        const key = path[path.length - 1];
        const parent = parentPath.length > 0 ? this.getNodeByPath(parentPath) : this.hierarchy[this.hierarchySection];
        this.$set(parent, key, []);
      }
      
      const targetNode = this.getNodeByPath(path);
      if (!this.isLeafNode(targetNode)) {
        await this.showAlert('Unable to add items to this node.', 'Cannot Add Items');
        return;
      }
      
      const searchTypes = this.hierarchySection === 'Factions' ? ['units'] : ['cards'];
      this.openEntitySearchDialog(searchTypes, (selectedEntity) => {
        targetNode.push(selectedEntity.Id);
      });
    },
    
    removeItemAtPath(path, index) {
      const node = this.getNodeByPath(path);
      if (!this.isLeafNode(node)) return;
      node.splice(index, 1);
    },
    
    // Get all hierarchy locations where a unit is currently placed
    getUnitHierarchyLocations(unitId) {
      if (!this.hierarchy || !this.hierarchy.Factions || !this.selectedFaction) return [];
      
      const locations = [];
      const factionName = this.selectedFaction.name;
      const factionData = this.hierarchy.Factions[factionName];
      
      if (!factionData) return locations;
      
      const findUnit = (obj, currentPath, displayPath) => {
        for (const key in obj) {
          const value = obj[key];
          const newPath = [...currentPath, key];
          const newDisplayPath = displayPath ? `${displayPath} > ${key}` : `${factionName} > ${key}`;
          
          if (Array.isArray(value)) {
            // This is a leaf node (contains unit IDs)
            if (value.includes(unitId)) {
              locations.push({
                path: newDisplayPath,
                pathArray: newPath,
                factionName: factionName,
                index: value.indexOf(unitId)
              });
            }
          } else if (typeof value === 'object' && value !== null) {
            // This is a branch node, recurse
            findUnit(value, newPath, newDisplayPath);
          }
        }
      };
      
      findUnit(factionData, [], '');
      return locations;
    },
    
    // Get all available hierarchy paths for the current faction (returns leaf nodes only)
    getAvailableHierarchyPaths() {
      if (!this.hierarchy || !this.hierarchy.Factions || !this.selectedFaction) return [];
      
      const paths = [];
      const factionName = this.selectedFaction.name;
      const factionData = this.hierarchy.Factions[factionName];
      
      if (!factionData) return paths;
      
      const collectPaths = (obj, currentPath, displayPath) => {
        for (const key in obj) {
          const value = obj[key];
          const newPath = [...currentPath, key];
          const newDisplayPath = displayPath ? `${displayPath} > ${key}` : `${factionName} > ${key}`;
          
          if (Array.isArray(value)) {
            // This is a leaf node (contains unit IDs)
            paths.push({
              path: newDisplayPath,
              pathArray: newPath,
              factionName: factionName
            });
          } else if (typeof value === 'object' && value !== null) {
            // This is a branch node, recurse
            collectPaths(value, newPath, newDisplayPath);
          }
        }
      };
      
      collectPaths(factionData, [], '');
      return paths;
    },
    
    // Add unit to a hierarchy path
    addUnitToHierarchyPath(unitId, pathObj) {
      if (!pathObj || !pathObj.pathArray) return;
      
      // Get the target array
      let current = this.hierarchy.Factions[pathObj.factionName];
      for (const key of pathObj.pathArray) {
        current = current[key];
      }
      
      if (!Array.isArray(current)) {
        console.error('Target is not an array:', current);
        return;
      }
      
      // Check if unit is already in this array
      if (current.includes(unitId)) {
        this.showAlert(`This unit is already in "${pathObj.path}"`, 'Already Added');
        return;
      }
      
      // Add the unit
      current.push(unitId);
      this.dirtyGameFiles['hierarchy.json'] = true;
      this.hasUnsavedChanges = true;
      this.selectedHierarchyPath = null; // Reset selection
    },
    
    // Remove unit from a hierarchy path
    removeUnitFromHierarchyPath(unitId, pathArray) {
      if (!pathArray || pathArray.length === 0) return;
      if (!this.selectedFaction) return;
      
      // Get the target array
      let current = this.hierarchy.Factions[this.selectedFaction.name];
      for (const key of pathArray) {
        current = current[key];
      }
      
      if (!Array.isArray(current)) {
        console.error('Target is not an array:', current);
        return;
      }
      
      // Find and remove the unit
      const index = current.indexOf(unitId);
      if (index !== -1) {
        current.splice(index, 1);
        this.dirtyGameFiles['hierarchy.json'] = true;
        this.hasUnsavedChanges = true;
      }
    },
    
    // Hierarchy Drag and Drop
    onDragStartHierarchy(item, type, factionName = null, categoryName = null) {
      this.draggedHierarchyItem = item;
      this.draggedHierarchyType = type;
      this.draggedHierarchyFaction = factionName || null; // Normalize undefined to null
      this.draggedHierarchyCategory = categoryName || null; // Normalize undefined to null
      console.log('Drag start:', { item, type, faction: this.draggedHierarchyFaction, category: this.draggedHierarchyCategory });
    },
    
    onDropHierarchy(targetItem, type, factionName = null, categoryName = null) {
      console.log('Drop attempt:', { 
        targetItem, 
        type, 
        factionName: factionName || null, 
        categoryName: categoryName || null,
        dragged: {
          item: this.draggedHierarchyItem,
          type: this.draggedHierarchyType,
          faction: this.draggedHierarchyFaction,
          category: this.draggedHierarchyCategory
        }
      });
      
      if (this.draggedHierarchyType !== type) {
        console.log('Type mismatch, ignoring drop');
        return;
      }
      
      if (type === 'faction') {
        // Reorder factions
        const factionNames = Object.keys(this.hierarchy[this.hierarchySection]);
        const fromIndex = factionNames.indexOf(this.draggedHierarchyItem);
        const toIndex = factionNames.indexOf(targetItem);
        
        if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) return;
        
        // Rebuild the object in new order
        const newFactions = {};
        const reorderedNames = [...factionNames];
        reorderedNames.splice(fromIndex, 1);
        reorderedNames.splice(toIndex, 0, this.draggedHierarchyItem);
        
        reorderedNames.forEach(name => {
          newFactions[name] = this.hierarchy[this.hierarchySection][name];
        });
        
        this.$set(this.hierarchy, this.hierarchySection, newFactions);
        console.log('Faction reordered successfully');
      } else if (type === 'category') {
        // Validate: must be dragging within the same parent (normalize null/undefined)
        const draggedFaction = this.draggedHierarchyFaction || null;
        const draggedCategory = this.draggedHierarchyCategory || null;
        const targetFaction = factionName || null;
        const targetCategory = categoryName || null;
        
        console.log('Category drop validation:', { 
          draggedFaction, 
          draggedCategory, 
          targetFaction, 
          targetCategory,
          match: draggedFaction === targetFaction && draggedCategory === targetCategory
        });
        
        if (draggedFaction !== targetFaction || draggedCategory !== targetCategory) {
          console.log('Category validation failed: different parents');
          return; // Can't drag categories between different parents
        }
        
        // Reorder categories within a parent (supports nested paths via categoryName)
        let targetObject;
        if (categoryName) {
          // Level 3+ category: navigate through parent path
          targetObject = this.hierarchy[this.hierarchySection][factionName][categoryName];
        } else if (factionName) {
          // Level 2 category: direct child of faction
          targetObject = this.hierarchy[this.hierarchySection][factionName];
        } else {
          // Level 1 category: direct child of root
          targetObject = this.hierarchy[this.hierarchySection];
        }
        
        const categoryNames = Object.keys(targetObject).filter(k => k !== '_name');
        const fromIndex = categoryNames.indexOf(this.draggedHierarchyItem);
        const toIndex = categoryNames.indexOf(targetItem);
        
        if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) return;
        
        const newCategories = {};
        const reorderedNames = [...categoryNames];
        reorderedNames.splice(fromIndex, 1);
        reorderedNames.splice(toIndex, 0, this.draggedHierarchyItem);
        
        reorderedNames.forEach(name => {
          newCategories[name] = targetObject[name];
        });
        
        // Update the correct level
        if (categoryName && factionName) {
          this.$set(this.hierarchy[this.hierarchySection][factionName], categoryName, newCategories);
        } else if (factionName) {
          this.$set(this.hierarchy[this.hierarchySection], factionName, newCategories);
        } else {
          this.$set(this.hierarchy, this.hierarchySection, newCategories);
        }
        console.log('Category reordered successfully');
      } else if (type === 'unit' && categoryName) {
        const draggedFaction = this.draggedHierarchyFaction || null;
        const draggedCategory = this.draggedHierarchyCategory || null;
        const targetFaction = factionName || null;
        const targetCategory = categoryName || null;

        const resolveArray = (faction, category) => {
          if (faction && category) {
            let obj = this.hierarchy[this.hierarchySection][faction];
            if (category.includes('/')) {
              for (const part of category.split('/')) obj = obj[part];
              return obj;
            }
            return obj[category];
          } else if (category) {
            return this.hierarchy[this.hierarchySection][category];
          }
          return null;
        };

        const sourceArray = resolveArray(draggedFaction, draggedCategory);
        const targetArray = resolveArray(targetFaction, targetCategory);

        if (!Array.isArray(sourceArray) || !Array.isArray(targetArray)) return;

        const fromIndex = this.draggedHierarchyItem;
        const toIndex = targetItem;

        if (sourceArray === targetArray && fromIndex === toIndex) return;

        // Remove from source
        const [item] = sourceArray.splice(fromIndex, 1);

        // Insert into target (if same array, toIndex may have shifted)
        const adjustedToIndex = sourceArray === targetArray && fromIndex < toIndex ? toIndex - 1 : toIndex;
        targetArray.splice(adjustedToIndex, 0, item);

        console.log('Unit moved successfully');
      }

      this.dirtyGameFiles['hierarchy.json'] = true;
      this.hasUnsavedChanges = true;
    },
    
    onDragEndHierarchy() {
      console.log('Drag end - cleaning up');
      this.draggedHierarchyItem = null;
      this.draggedHierarchyType = null;
      this.draggedHierarchyFaction = null;
      this.draggedHierarchyCategory = null;
    },
    
    // UI Config initialization
    initUIConfig() {
      this.$set(this.selectedEntity, 'UIConfig', {
        Width: 63.5,
        Length: 88.9,
        IsRound: false,
        ShowInitials: false,
        Border: false,
        ImageURL: '',
        ImageURLBack: ''
      });
    },
  },
  
  watch: {
    editorMode(newMode) {
      this.selectedEntity = null;
      if (newMode === 'game') {
        this.selectType('rules');
      } else {
        this.selectType('units');
      }
    }
  }
};
</script>

<style scoped>
* {
  font-family: 'Segoe UI', Arial, sans-serif;
}

.wds2-editor-shell {
  width: 100vw;
  margin-left: calc(-50vw + 50%);
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%);
  min-height: 100vh;
  padding: 2rem;
}

.wds2-editor-content {
  max-width: 95vw;
  margin: 0 auto;
}

/* Header */
.editor-header {
  margin-bottom: 2rem;
  animation: fadeIn 0.5s ease-in;
}

.header-title-row {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.v3-badge {
  display: inline-block;
  background: linear-gradient(135deg, #6366f1, #3b82f6);
  color: #fff;
  font-size: 0.7rem;
  font-weight: 700;
  padding: 0.2rem 0.65rem;
  border-radius: 999px;
  letter-spacing: 0.05em;
  white-space: nowrap;
  margin-bottom: 1rem;
  cursor: default;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
}

.header-title-group {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-right: auto;
}

.editor-header h1 {
  font-size: 2rem;
  font-weight: 800;
  color: #f1f5f9;
  text-shadow: 0 2px 10px rgba(59, 130, 246, 0.3);
  margin: 0;
}

.mode-selector {
  display: flex;
  align-items: center;
  gap: 0;
  padding: 0.375rem;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  border-radius: 0.5rem;
  backdrop-filter: blur(10px);
  border: none;
  width: fit-content;
}

.mode-btn {
  padding: 0.75rem 1.5rem;
  border-radius: 0.375rem;
  background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
  color: #cbd5e1;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 600;
  font-size: 0.938rem;
  position: relative;
  overflow: hidden;
}

.mode-btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.5s ease;
}

.mode-btn:hover::before {
  left: 100%;
}

.mode-btn:hover {
  background: linear-gradient(135deg, #334155 0%, #475569 100%);
  color: #e2e8f0;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.2);
}

.mode-btn.active {
  background: linear-gradient(135deg, #1e3a5f 0%, #1e40af 100%);
  color: #fff;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
  transform: none;
}

.faction-selector-inline {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-left: 0;
}

.faction-selector-inline label {
  color: #cbd5e1;
  font-weight: 600;
  white-space: nowrap;
}

.faction-selector-inline .faction-dropdown {
  min-width: 250px;
}

.add-faction-btn {
  padding: 0.625rem 1rem;
  border-radius: 0.5rem;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  border: none;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 600;
  transition: all 0.3s ease;
  white-space: nowrap;
}

.add-faction-btn:hover {
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.faction-dropdown {
  width: 100%;
  padding: 0.875rem 1rem;
  border-radius: 0.5rem;
  background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
  color: #f1f5f9;
  border: 1px solid #475569;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.faction-dropdown option {
  background: #1e293b;
  color: #f1f5f9;
  padding: 0.5rem;
}

.faction-dropdown:hover {
  border-color: #3b82f6;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.2);
}

.faction-dropdown:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

/* Three Panel Layout */
.three-panel-layout {
  display: grid;
  grid-template-columns: 220px 380px 1fr;
  gap: 1.5rem;
  height: calc(100vh - 250px);
}

.editor-panel.full-width {
  grid-column: 2 / -1;
}

/* Type Panel */
.type-panel {
  background: linear-gradient(135deg, #1e293b 0%, #2d3748 100%);
  border-radius: 0.75rem;
  padding: 1.25rem;
  overflow-y: auto;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  border: 1px solid #334155;
}

.type-btn {
  display: block;
  width: 100%;
  padding: 0.875rem 1rem;
  margin-bottom: 0.5rem;
  border-radius: 0.5rem;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  color: #cbd5e1;
  border: 1px solid transparent;
  cursor: pointer;
  text-align: left;
  transition: all 0.3s ease;
  font-size: 0.875rem;
  font-weight: 500;
  position: relative;
}

.type-btn::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: #3b82f6;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.type-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #334155 0%, #475569 100%);
  color: white;
  transform: translateX(4px);
  border-color: #3b82f6;
}

.type-btn:hover:not(:disabled)::before {
  opacity: 1;
}

.type-btn.active {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  font-weight: 600;
  border-color: #60a5fa;
  box-shadow: 0 4px 15px rgba(59, 130, 246, 0.3);
}

.type-btn.active::before {
  opacity: 1;
}

.type-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* List Panel */
.list-panel {
  background: linear-gradient(135deg, #1e293b 0%, #2d3748 100%);
  border-radius: 0.75rem;
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  min-height: 0;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  border: 1px solid #334155;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #334155;
}

.panel-title {
  font-size: 1.125rem;
  font-weight: 700;
  color: #f1f5f9;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.entity-id-small {
  font-size: 0.75rem;
  font-weight: 400;
  color: #94a3b8;
  margin-left: 0.5rem;
  opacity: 0.8;
}

.add-btn {
  padding: 0.625rem 1.25rem;
  border-radius: 0.5rem;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  border: none;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 600;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.add-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(16, 185, 129, 0.4);
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
}

.add-btn:active {
  transform: translateY(0);
}

.search-container {
  position: relative;
  margin-bottom: 1rem;
}

.search-input {
  width: 100%;
  padding: 0.75rem 2.5rem 0.75rem 1rem;
  border-radius: 0.5rem;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  color: white;
  border: 1px solid #334155;
  transition: all 0.3s ease;
}

.search-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.search-clear-btn {
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #94a3b8;
  font-size: 1rem;
  cursor: pointer;
  padding: 0.375rem 0.625rem;
  border-radius: 0.375rem;
  transition: all 0.2s;
  line-height: 1;
}

.search-clear-btn:hover {
  background-color: #334155;
  color: white;
}

.entity-list {
  flex: 1;
  overflow-y: auto;
  padding-right: 0.25rem;
}

.entity-list::-webkit-scrollbar {
  width: 8px;
}

.entity-list::-webkit-scrollbar-track {
  background: #0f172a;
  border-radius: 4px;
}

.entity-list::-webkit-scrollbar-thumb {
  background: #334155;
  border-radius: 4px;
}

.entity-list::-webkit-scrollbar-thumb:hover {
  background: #475569;
}

.entity-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  margin-bottom: 0.375rem;
  border-radius: 0.5rem;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  border: 1px solid transparent;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.entity-item::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: linear-gradient(180deg, #3b82f6, #8b5cf6);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.entity-icon {
  font-size: 1.125rem;
  flex-shrink: 0;
  filter: drop-shadow(0 2px 4px rgba(59, 130, 246, 0.3));
}

.entity-icon--no-skin {
  cursor: help;
  filter: none;
}

.entity-icon--no-hierarchy {
  cursor: help;
  filter: none;
  color: #f59e0b;
}

.entity-thumbnail {
  width: 4rem;
  height: 4rem;
  border-radius: 4px;
  object-fit: cover;
  flex-shrink: 0;
  border: 1px solid #475569;
  margin: -0.875rem 0;
  margin-left: -1rem;
  margin-right: 0.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}

.entity-item:hover {
  background: linear-gradient(135deg, #334155 0%, #475569 100%);
  transform: translateX(4px);
  border-color: #3b82f6;
}

.entity-item:hover::before {
  opacity: 1;
}

.entity-item.selected {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  border-color: #60a5fa;
  box-shadow: 0 4px 15px rgba(59, 130, 246, 0.3);
}

.entity-item.selected::before {
  opacity: 1;
}

.entity-name {
  font-size: 0.875rem;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 500;
}

.remove-btn {
  padding: 0.375rem 0.625rem;
  background-color: transparent;
  color: #ef4444;
  border: none;
  cursor: pointer;
  font-size: 0.875rem;
  opacity: 0.7;
  transition: all 0.3s ease;
  border-radius: 0.375rem;
}

.remove-btn:hover {
  opacity: 1;
  background-color: rgba(239, 68, 68, 0.1);
  transform: scale(1.1);
}

/* Editor Panel */
.editor-panel {
  background: linear-gradient(135deg, #1e293b 0%, #2d3748 100%);
  border-radius: 0.75rem;
  padding: 1.75rem;
  overflow-y: auto;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  border: 1px solid #334155;
}

.editor-panel::-webkit-scrollbar {
  width: 10px;
}

.editor-panel::-webkit-scrollbar-track {
  background: #0f172a;
  border-radius: 5px;
}

.editor-panel::-webkit-scrollbar-thumb {
  background: #334155;
  border-radius: 5px;
}

.editor-panel::-webkit-scrollbar-thumb:hover {
  background: #475569;
}

.save-btn {
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  border: none;
  cursor: pointer;
  font-size: 0.938rem;
  font-weight: 600;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.save-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(16, 185, 129, 0.4);
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
}

.save-btn:active {
  transform: translateY(0);
}

.save-btn:disabled {
  background: linear-gradient(135deg, #475569 0%, #64748b 100%);
  cursor: not-allowed;
  opacity: 0.6;
  transform: none;
  box-shadow: none;
}

.editor-content {
  margin-top: 1.5rem;
  width: 100%;
  box-sizing: border-box;
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 1.5rem;
  align-items: start;
}

/* For full-width editor components (tokens, terrains, deployments, objectives, hierarchy), use single column */
.editor-panel.full-width .editor-content {
  grid-template-columns: 1fr;
  width: 100%;
}

.editor-content > p {
  grid-column: 1 / -1;
}

.form-group:has(textarea),
.form-group:has(.attributes-editor),
.form-group:has(.displayable-editor),
.form-group:has(.list-item),
.form-group-wide {
  grid-column: 1 / -1;
}

.form-group {
  margin-bottom: 1.75rem;
}

.form-group label {
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: #cbd5e1;
  margin-bottom: 0.625rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.input-field, .textarea-field {
  width: 100%;
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  color: white;
  border: 1px solid #334155;
  transition: all 0.3s ease;
  font-size: 0.938rem;
}

.input-field:focus, .textarea-field:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.textarea-field {
  resize: vertical;
  font-family: inherit;
  line-height: 1.6;
}

input[type="color"].input-field {
  width: 60px;
  height: 60px;
  padding: 0.25rem;
  cursor: pointer;
}

input[type="number"].input-field {
  width: 5rem;
  text-align: right;
}

/* Additional Editor Styles */
.small-btn {
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  border: none;
  cursor: pointer;
  font-size: 0.813rem;
  font-weight: 600;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
}

.small-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
}

.btn-warning {
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%) !important;
  color: white;
  box-shadow: 0 2px 6px rgba(220, 38, 38, 0.3) !important;
}

.btn-warning:hover {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%) !important;
  box-shadow: 0 2px 8px rgba(220, 38, 38, 0.4) !important;
}

.input-field-small {
  flex: 1;
  padding: 0.625rem;
  border-radius: 0.375rem;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  color: white;
  border: 1px solid #334155;
  font-size: 0.875rem;
  transition: all 0.3s ease;
}

.input-field-small:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
}

.remove-btn-small {
  padding: 0.375rem 0.625rem;
  background-color: transparent;
  color: #ef4444;
  border: none;
  cursor: pointer;
  font-size: 0.875rem;
  transition: all 0.3s ease;
  border-radius: 0.375rem;
}

.remove-btn-small:hover {
  background-color: rgba(239, 68, 68, 0.15);
  transform: scale(1.1);
}

.option-link-btn {
  padding: 0.625rem 1rem;
  border-radius: 0.375rem;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  color: #60a5fa;
  border: 1px solid #334155;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.875rem;
}

.option-link-btn:hover {
  background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
  border-color: #3b82f6;
  color: #93c5fd;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.2);
}

/* Checkbox Styles */
.checkbox-group {
  display: flex;
  align-items: center;
  gap: 0.625rem;
}

input[type="checkbox"] {
  width: 20px;
  height: 20px;
  cursor: pointer;
  accent-color: #3b82f6;
  border-radius: 0.25rem;
}

input[type="checkbox"]:hover {
  transform: scale(1.05);
}

.checkbox-input {
  width: 1.125rem;
  height: 1.125rem;
  cursor: pointer;
  accent-color: #3b82f6;
}

.checkbox-input:hover {
  transform: scale(1.05);
}

/* Attribute and Displayable Rows */
.attribute-row, .displayable-row, .list-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
  padding: 0.75rem;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  border-radius: 0.5rem;
  border: 1px solid #334155;
  transition: all 0.3s ease;
}

.attribute-row:hover, .displayable-row:hover, .list-item:hover {
  border-color: #3b82f6;
  transform: translateX(2px);
}

.attributes-editor, .displayable-editor {
  padding: 1.25rem;
  background: linear-gradient(135deg, rgba(15, 23, 42, 0.5) 0%, rgba(30, 41, 59, 0.5) 100%);
  border-radius: 0.75rem;
  border: 1px solid #334155;
}

.displayable-icon {
  font-size: 1.125rem;
  flex-shrink: 0;
  margin-right: 0.5rem;
  filter: drop-shadow(0 2px 4px rgba(59, 130, 246, 0.3));
}

.ui-config-editor {
  padding: 1.25rem;
  background: linear-gradient(135deg, rgba(15, 23, 42, 0.5) 0%, rgba(30, 41, 59, 0.5) 100%);
  border-radius: 0.75rem;
  border: 1px solid #334155;
}

.ui-config-editor .form-group {
  margin-bottom: 1.25rem;
}

.ui-config-editor .form-group:last-child {
  margin-bottom: 0;
}

.linked-option-item {
  margin-bottom: 1.75rem;
  padding: 1.25rem;
  background: linear-gradient(135deg, rgba(15, 23, 42, 0.6) 0%, rgba(30, 41, 59, 0.6) 100%);
  border-radius: 0.75rem;
  border: 1px solid #334155;
  transition: all 0.3s ease;
}

.linked-option-item:hover {
  border-color: #3b82f6;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.1);
}

.linked-option-item:last-child {
  margin-bottom: 0;
}

/* ID Links */
.id-link {
  color: #60a5fa;
  text-decoration: none;
  padding: 0.625rem 1rem;
  border-radius: 0.5rem;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  border: 1px solid #334155;
  font-size: 0.875rem;
  font-family: 'Consolas', 'Monaco', monospace;
  transition: all 0.3s ease;
  display: inline-block;
  cursor: pointer;
  font-weight: 500;
}

.id-link:hover {
  background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
  border-color: #60a5fa;
  color: #93c5fd;
  text-decoration: underline;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.2);
}

/* Drag and Drop */
.drag-handle {
  cursor: grab;
  color: #64748b;
  font-size: 1.25rem;
  padding: 0 0.75rem;
  user-select: none;
  margin-right: 0.5rem;
  transition: color 0.3s ease;
}

.drag-handle:hover {
  color: #94a3b8;
}

.drag-handle:active {
  cursor: grabbing;
}

.dragging {
  opacity: 0.5;
  background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
  border: 2px dashed #3b82f6;
}

.list-item,
.displayable-row,
.attribute-row {
  cursor: move;
}

/* Modal Dialog */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.3s ease;
}

.entity-search-overlay {
  z-index: 1100;
}

.modal-dialog {
  background: linear-gradient(135deg, #1e293b 0%, #2d3748 100%);
  border-radius: 0.75rem;
  border: 1px solid #334155;
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.7);
  animation: slideUp 0.3s ease;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.modal-dialog-small {
  max-width: 400px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #334155;
}

.modal-header h3 {
  margin: 0;
  color: #f1f5f9;
  font-size: 1.25rem;
  font-weight: 700;
}

.close-btn {
  background: none;
  border: none;
  color: #94a3b8;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0;
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.375rem;
  transition: all 0.3s ease;
}

.close-btn:hover {
  background-color: rgba(239, 68, 68, 0.2);
  color: #ef4444;
  transform: rotate(90deg);
}

.type-filter {
  display: flex;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #334155;
  flex-wrap: wrap;
}

.type-filter-btn {
  padding: 0.625rem 1rem;
  border-radius: 0.5rem;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  color: #94a3b8;
  border: 1px solid transparent;
  cursor: pointer;
  font-size: 1rem;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 2.5rem;
  font-weight: 500;
}

.type-filter-btn:hover {
  background: linear-gradient(135deg, #334155 0%, #475569 100%);
  color: white;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.2);
}

.type-filter-btn.active {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  border-color: #60a5fa;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
}

.type-filter-btn:hover {
  background: linear-gradient(135deg, #334155 0%, #475569 100%);
  color: white;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.2);
}

.type-filter-btn.active {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  border-color: #60a5fa;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
}

.modal-body {
  padding: 1.5rem;
  max-height: 600px;
  overflow-y: auto;
}

.search-header {
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.search-header .search-input {
  flex: 1;
}

.btn-add-entity {
  padding: 0.625rem 1.25rem;
  border-radius: 0.5rem;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  border: none;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 600;
  transition: all 0.2s;
  white-space: nowrap;
  box-shadow: 0 2px 6px rgba(16, 185, 129, 0.3);
}

.btn-add-entity:hover {
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 10px rgba(16, 185, 129, 0.4);
}

.search-input {
  width: 100%;
  padding: 0.75rem;
  background-color: #0f172a;
  border: 1px solid #334155;
  border-radius: 0.375rem;
  color: white;
  font-size: 1rem;
}

.search-input:focus {
  outline: none;
  border-color: #3b82f6;
}

.search-results {
  overflow-y: auto;
  max-height: 400px;
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
}

.search-results::-webkit-scrollbar {
  width: 8px;
}

.search-results::-webkit-scrollbar-track {
  background: #0f172a;
  border-radius: 4px;
}

.search-results::-webkit-scrollbar-thumb {
  background: #334155;
  border-radius: 4px;
}

.search-results::-webkit-scrollbar-thumb:hover {
  background: #475569;
}

.search-result-item {
  padding: 1rem 1.25rem;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  border: 1px solid #334155;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  min-height: 50px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.search-result-item::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: linear-gradient(180deg, #3b82f6, #8b5cf6);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.search-result-item:hover {
  background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
  border-color: #3b82f6;
  transform: translateX(4px);
}

.search-result-item:hover::before {
  opacity: 1;
}

.preview-tooltip {
  position: fixed;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  border: 2px solid #3b82f6;
  border-radius: 0.75rem;
  padding: 1rem;
  max-width: 350px;
  z-index: 10001;
  pointer-events: none;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.7), 0 0 20px rgba(59, 130, 246, 0.3);
}

.preview-header {
  color: #60a5fa;
  font-weight: 700;
  font-size: 0.938rem;
  margin-bottom: 0.75rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #334155;
}

.preview-body {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.preview-field {
  display: flex;
  gap: 0.625rem;
  font-size: 0.813rem;
}

.preview-key {
  color: #94a3b8;
  font-weight: 600;
  min-width: 80px;
}

.preview-value {
  color: #e2e8f0;
  flex: 1;
  word-break: break-word;
}

.result-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.375rem;
}

.result-icon {
  font-size: 1.25rem;
  flex-shrink: 0;
  filter: drop-shadow(0 2px 4px rgba(59, 130, 246, 0.3));
}

.result-name {
  color: #f1f5f9;
  font-weight: 600;
  font-size: 1rem;
}

.no-results {
  padding: 3rem 2rem;
  text-align: center;
  color: #64748b;
  font-size: 1rem;
}

.dialog-actions {
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  margin-top: 1.5rem;
}

.btn-primary,
.btn-secondary {
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  font-size: 0.938rem;
}

.btn-primary {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.btn-primary:hover {
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(59, 130, 246, 0.4);
}

.btn-secondary {
  background: linear-gradient(135deg, #334155 0%, #475569 100%);
  color: #e2e8f0;
}

.btn-secondary:hover {
  background: linear-gradient(135deg, #475569 0%, #64748b 100%);
  transform: translateY(-2px);
}

/* Hierarchy Editor Styles */
.hierarchy-editor {
  padding: 1.25rem;
}

.hierarchy-faction {
  background: linear-gradient(135deg, #1e293b 0%, #2d3748 100%);
  border: 2px solid #334155;
  border-radius: 0.75rem;
  padding: 1.25rem;
  margin-bottom: 1.75rem;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.hierarchy-faction:hover {
  border-color: #3b82f6;
  box-shadow: 0 6px 20px rgba(59, 130, 246, 0.2);
}

.hierarchy-faction.dragging {
  opacity: 0.5;
  border-color: #3b82f6;
  cursor: grabbing;
  transform: scale(0.98);
}

.hierarchy-faction:not(.dragging) {
  cursor: grab;
}

.hierarchy-faction-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #334155;
}

.hierarchy-faction-input {
  flex: 1;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  border: 1px solid #334155;
  color: #f1f5f9;
  padding: 0.625rem 0.875rem;
  border-radius: 0.5rem;
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.3s ease;
}

.hierarchy-faction-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.hierarchy-categories {
  padding-left: 2rem;
}

.hierarchy-category {
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  border: 1px solid #334155;
  border-radius: 0.5rem;
  padding: 1rem;
  margin-bottom: 1.25rem;
  transition: all 0.3s ease;
}

.hierarchy-category:hover {
  border-color: #3b82f6;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
}

.hierarchy-category.dragging {
  opacity: 0.5;
  border-color: #3b82f6;
  cursor: grabbing;
  transform: scale(0.98);
}

.hierarchy-category:not(.dragging) {
  cursor: grab;
}

.hierarchy-category-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.hierarchy-category-input {
  flex: 1;
  background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
  border: 1px solid #334155;
  color: #e2e8f0;
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  font-weight: 500;
  transition: all 0.3s ease;
}

.hierarchy-category-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.hierarchy-units {
  padding-left: 2rem;
}

.hierarchy-unit {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: linear-gradient(135deg, #1e293b 0%, #2d3748 100%);
  border: 1px solid #334155;
  border-radius: 0.5rem;
  margin-bottom: 0.625rem;
  transition: all 0.3s ease;
}

.hierarchy-unit:hover {
  background: linear-gradient(135deg, #334155 0%, #475569 100%);
  border-color: #3b82f6;
  transform: translateX(2px);
}

.hierarchy-unit.dragging {
  opacity: 0.5;
  border: 2px dashed #3b82f6;
  cursor: grabbing;
  transform: scale(0.98);
}

.hierarchy-unit:not(.dragging) {
  cursor: grab;
}

.hierarchy-icon {
  font-size: 1.125rem;
  filter: drop-shadow(0 2px 4px rgba(59, 130, 246, 0.3));
}

.collapse-btn {
  background: none;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  padding: 0.25rem;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  transition: all 0.3s ease;
  border-radius: 0.375rem;
}

.collapse-btn:hover {
  color: #e2e8f0;
  background-color: rgba(59, 130, 246, 0.1);
  transform: scale(1.15);
}

.hierarchy-section-toggle {
  display: inline-flex;
  gap: 0.625rem;
  margin-left: 1rem;
}

.hierarchy-toggle-btn {
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  background: linear-gradient(135deg, #334155 0%, #475569 100%);
  color: #94a3b8;
  border: 1px solid transparent;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 600;
  transition: all 0.3s ease;
}

.hierarchy-toggle-btn:hover {
  background: linear-gradient(135deg, #475569 0%, #64748b 100%);
  color: #e2e8f0;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.2);
}

.hierarchy-toggle-btn.active {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  border-color: #60a5fa;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
}

/* Hierarchy Node Actions */
.hierarchy-node-actions {
  display: flex;
  gap: 0.375rem;
  margin-left: auto;
  margin-right: 0.5rem;
}

.hierarchy-action-btn {
  padding: 0.375rem 0.625rem;
  border-radius: 0.375rem;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  border: 1px solid #60a5fa;
  cursor: pointer;
  font-size: 0.813rem;
  font-weight: 600;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
}

.hierarchy-action-btn:hover {
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
  transform: translateY(-2px) scale(1.05);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.5);
}

.hierarchy-action-btn:active {
  transform: translateY(0) scale(1);
}

/* Duplicate Keys Modal */
.duplicate-keys-dialog {
  max-width: 560px;
}

.duplicate-keys-warning-text {
  color: #fbbf24;
  font-size: 0.9rem;
  margin: 0 0 1rem 0;
  line-height: 1.5;
}

.duplicate-keys-list {
  max-height: 320px;
  overflow-y: auto;
  border: 1px solid #334155;
  border-radius: 0.5rem;
  background: #0f172a;
  margin-bottom: 1.25rem;
}

.duplicate-key-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 0.875rem;
  border-bottom: 1px solid #1e293b;
}

.duplicate-key-item:last-child {
  border-bottom: none;
}

.duplicate-key-source {
  font-size: 0.75rem;
  font-weight: 600;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  flex-shrink: 0;
  min-width: 100px;
}

.duplicate-key-id {
  font-family: monospace;
  font-size: 0.875rem;
  color: #f87171;
  word-break: break-all;
  flex: 1;
}

.fix-duplicate-btn {
  flex-shrink: 0;
  padding: 0.25rem 0.625rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: #fff;
  background: #16a34a;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  transition: background 0.15s;
}

.fix-duplicate-btn:hover {
  background: #15803d;
}

/* Structure Validation Dialog */
.structure-validation-dialog {
  max-width: 700px;
}

.validation-warning-text {
  color: #60a5fa;
  font-size: 0.9rem;
  margin: 0 0 1rem 0;
  line-height: 1.5;
}

.validation-issues-list {
  max-height: 450px;
  overflow-y: auto;
  border: 1px solid #334155;
  border-radius: 0.5rem;
  background: #0f172a;
  margin-bottom: 1.25rem;
}

.validation-issue-item {
  padding: 1rem;
  border-bottom: 1px solid #1e293b;
}

.validation-issue-item:last-child {
  border-bottom: none;
}

.validation-issue-item.error-item {
  background: rgba(239, 68, 68, 0.05);
  border-left: 3px solid #ef4444;
}

.validation-issue-item.warning-item {
  background: rgba(255, 193, 7, 0.05);
  border-left: 3px solid #ffc107;
}

.validation-issue-item.warning-item .validation-issue-type {
  color: #ffc107;
}

.validation-issue-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

.validation-issue-icon {
  font-size: 1.25rem;
}

.validation-issue-type {
  font-size: 0.875rem;
  font-weight: 700;
  color: #f59e0b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.data-type-badge {
  display: inline-block;
  padding: 0.125rem 0.5rem;
  margin-right: 0.25rem;
  font-size: 0.75rem;
  background: rgba(59, 130, 246, 0.2);
  color: #60a5fa;
  border-radius: 0.25rem;
  font-family: monospace;
  font-weight: 600;
}

.validation-issue-body {
  margin-left: 1.75rem;
}

.validation-issue-detail {
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
  color: #cbd5e1;
}

.validation-issue-detail strong {
  color: #e2e8f0;
  margin-right: 0.5rem;
}

.validation-issue-detail code {
  font-family: monospace;
  font-size: 0.875rem;
  color: #fbbf24;
  background: #1e293b;
  padding: 0.125rem 0.375rem;
  border-radius: 0.25rem;
}

.validation-issue-locations {
  list-style: disc;
  margin-left: 1.5rem;
  margin-top: 0.5rem;
  margin-bottom: 0.75rem;
  color: #94a3b8;
  font-size: 0.875rem;
}

.validation-issue-locations li {
  margin-bottom: 0.25rem;
}

.validation-issue-suggestion {
  margin-top: 0.75rem;
  padding: 0.75rem;
  background: #1e293b;
  border-left: 3px solid #3b82f6;
  border-radius: 0.25rem;
  font-size: 0.875rem;
  color: #cbd5e1;
  line-height: 1.5;
}

.validation-issue-suggestion strong {
  color: #60a5fa;
  margin-right: 0.375rem;
}

.validation-issue-actions {
  margin-top: 1rem;
  display: flex;
  gap: 0.5rem;
}

.fix-structure-btn {
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: white;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border: 1px solid #10b981;
  border-radius: 0.375rem;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.375rem;
}

.fix-structure-btn:hover {
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4);
  transform: translateY(-1px);
}

.fix-structure-btn:active {
  transform: translateY(0);
}

.validate-structure-btn {
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: white;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  border: 1px solid #3b82f6;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.375rem;
}

.validate-structure-btn:hover {
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
  transform: translateY(-1px);
}

.validate-structure-btn:active {
  transform: translateY(0);
}

/* Git Sync Status Badge */
.floating-git-status {
  position: fixed;
  bottom: 2rem;
  left: 2rem;
  padding: 0.75rem 1.25rem;
  border-radius: 0.75rem;
  color: white;
  font-size: 0.9rem;
  font-weight: 600;
  z-index: 9999;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  animation: slideInLeft 0.5s ease;
  transition: all 0.3s ease;
  user-select: none;
}

/* Green state — in sync with Git */
.floating-git-status.no-changes {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border: 1px solid #10b981;
  box-shadow: 0 4px 15px rgba(16, 185, 129, 0.3);
  cursor: default;
}

/* Amber state — unpublished changes */
.floating-git-status.has-changes {
  background: linear-gradient(135deg, #d97706 0%, #b45309 100%);
  border: 1px solid #f59e0b;
  box-shadow: 0 8px 25px rgba(217, 119, 6, 0.5);
  cursor: pointer;
  animation: amberPulse 2s ease-in-out infinite, slideInLeft 0.5s ease;
}

.floating-git-status.has-changes:hover {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  box-shadow: 0 12px 35px rgba(217, 119, 6, 0.65);
  transform: translateY(-2px);
}

@keyframes amberPulse {
  0%, 100% { box-shadow: 0 8px 25px rgba(217, 119, 6, 0.5); }
  50% { box-shadow: 0 8px 35px rgba(245, 158, 11, 0.75); }
}

@keyframes slideInLeft {
  from { opacity: 0; transform: translateX(-100px); }
  to   { opacity: 1; transform: translateX(0); }
}

.git-status-icon {
  font-size: 1.25rem;
  line-height: 1;
  flex-shrink: 0;
}

.git-status-text {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
}

.git-status-primary {
  font-size: 0.85rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.git-status-version {
  font-size: 0.7rem;
  opacity: 0.75;
  font-weight: 400;
}

/* Custom Dialog Modal */
.custom-dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  animation: fadeInOverlay 0.2s ease-out;
}

@keyframes fadeInOverlay {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.custom-dialog {
  background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
  border: 2px solid #475569;
  border-radius: 1rem;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  min-width: 400px;
  max-width: 600px;
  animation: slideIn 0.3s ease-out;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-50px) scale(0.9);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.custom-dialog-header {
  padding: 1.5rem;
  border-bottom: 1px solid #475569;
  background: rgba(30, 41, 59, 0.6);
  border-radius: 1rem 1rem 0 0;
}

.custom-dialog-title {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  color: #f1f5f9;
  text-shadow: 0 2px 10px rgba(59, 130, 246, 0.3);
}

.custom-dialog-body {
  padding: 2rem 1.5rem;
  color: #cbd5e1;
  font-size: 1.1rem;
  line-height: 1.6;
}

.custom-dialog-body p {
  margin: 0.5rem 0;
  white-space: pre-wrap;
}

.custom-dialog-footer {
  padding: 1rem 1.5rem;
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  border-top: 1px solid #475569;
  background: rgba(30, 41, 59, 0.3);
  border-radius: 0 0 1rem 1rem;
}

.dialog-btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 0.5rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.dialog-btn-confirm {
  background: linear-gradient(135deg, #facc15 0%, #eab308 100%);
  color: #1e293b;
  box-shadow: 0 4px 15px rgba(250, 204, 21, 0.3);
}

.dialog-btn-confirm:hover {
  background: linear-gradient(135deg, #fde047 0%, #facc15 100%);
  box-shadow: 0 6px 20px rgba(250, 204, 21, 0.5);
  transform: translateY(-2px);
}

.dialog-btn-cancel {
  background: #475569;
  color: #f1f5f9;
  box-shadow: 0 4px 15px rgba(71, 85, 105, 0.3);
}

.dialog-btn-cancel:hover {
  background: #64748b;
  box-shadow: 0 6px 20px rgba(71, 85, 105, 0.5);
  transform: translateY(-2px);
}

.dialog-btn:active {
  transform: translateY(0);
}

/* ══════════════════════════════════════════════════════════
   Setup Wizard
   ══════════════════════════════════════════════════════════ */

.wizard-launch-btn {
  background: linear-gradient(135deg, #6366f1, #4f46e5);
  color: #fff;
  border: none;
  border-radius: 0.5rem;
  padding: 0.4rem 1rem;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: 1rem;
  white-space: nowrap;
}
.wizard-launch-btn:hover {
  background: linear-gradient(135deg, #818cf8, #6366f1);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.4);
}
.folder-icon-btn {
  background: linear-gradient(135deg, #1e3a5f, #1e40af);
  color: #93c5fd;
  border: 1px solid #2563eb;
  border-radius: 0.5rem;
  padding: 0.4rem 0.8rem;
  font-size: 1.25rem;
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: 1rem;
  white-space: nowrap;
}
.folder-icon-btn:hover {
  background: linear-gradient(135deg, #1e40af, #2563eb);
  border-color: #3b82f6;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.4);
}
.force-sync-btn {
  background: linear-gradient(135deg, #0e7490, #06b6d4);
  color: #cffafe;
  border: 1px solid #06b6d4;
  border-radius: 0.5rem;
  padding: 0.4rem 0.8rem;
  font-size: 1.25rem;
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: 1rem;
  white-space: nowrap;
  margin-left: 0.5rem;
}
.force-sync-btn:hover {
  background: linear-gradient(135deg, #06b6d4, #22d3ee);
  border-color: #22d3ee;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(6, 182, 212, 0.4);
}
.change-game-btn {
  background: linear-gradient(135deg, #1e3a5f, #1e40af);
  color: #93c5fd;
  border: 1px solid #2563eb;
  border-radius: 0.5rem;
  padding: 0.4rem 1rem;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: 1rem;
  white-space: nowrap;
}
.change-game-btn:hover {
  background: linear-gradient(135deg, #1e40af, #2563eb);
  border-color: #3b82f6;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.4);
}
.download-files-btn {
  background: linear-gradient(135deg, #1e3a5f, #1e40af);
  color: #fff;
  border: 1px solid #2563eb;
  border-radius: 0.5rem;
  padding: 0.4rem 1rem;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: 1rem;
  white-space: nowrap;
}
.download-files-btn:hover {
  background: linear-gradient(135deg, #1e40af, #2563eb);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.4);
}
.upload-git-btn {
  background: linear-gradient(135deg, #1e3a5f, #1e40af);
  color: #fff;
  border: 1px solid #2563eb;
  border-radius: 0.5rem;
  padding: 0.4rem 1rem;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: 1rem;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.upload-git-btn:hover {
  background: linear-gradient(135deg, #1e40af, #2563eb);
  border-color: #3b82f6;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(37, 99, 235, 0.4);
}
.upload-git-btn .github-icon {
  width: 1.25rem;
  height: 1.25rem;
  filter: brightness(0) invert(1);
}
.clear-sample-btn {
  background: linear-gradient(135deg, #7f1d1d, #991b1b);
  color: #fca5a5;
  border: 1px solid #b91c1c;
  border-radius: 0.5rem;
  padding: 0.4rem 1rem;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: 1rem;
  white-space: nowrap;
}
.clear-sample-btn:hover {
  background: linear-gradient(135deg, #991b1b, #b91c1c);
  border-color: #dc2626;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(185, 28, 28, 0.4);
}
.clear-sample-btn {
  background: linear-gradient(135deg, #7f1d1d, #991b1b);
  color: #fca5a5;
  border: 1px solid #b91c1c;
  border-radius: 0.5rem;
  padding: 0.4rem 1rem;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: 1rem;
  white-space: nowrap;
}
.clear-sample-btn:hover {
  background: linear-gradient(135deg, #991b1b, #b91c1c);
  border-color: #dc2626;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(185, 28, 28, 0.4);
}
/* ── Git Modal ── */
.git-modal-dialog {
  background: #1e293b;
  border: 1px solid #334155;
  border-radius: 1rem;
  width: 100%;
  max-width: 560px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.git-modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 1.25rem 1.5rem 1rem;
  border-bottom: 1px solid #334155;
}
.git-modal-header-left {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}
.git-modal-gh-icon {
  width: 2rem;
  height: 2rem;
  color: #e2e8f0;
  flex-shrink: 0;
}
.git-modal-title {
  font-size: 1.2rem;
  font-weight: 700;
  color: #f1f5f9;
  margin: 0 0 0.1rem;
}
.git-modal-subtitle {
  font-size: 0.8rem;
  color: #94a3b8;
  margin: 0;
}
.git-modal-body {
  padding: 1.25rem 1.5rem;
  overflow-y: auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.git-modal-desc {
  font-size: 0.85rem;
  color: #94a3b8;
  margin: 0;
}
.git-modal-desc a { color: #60a5fa; }
.git-modal-field {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}
.git-modal-label {
  font-size: 0.8rem;
  font-weight: 600;
  color: #cbd5e1;
}
.git-modal-optional {
  font-weight: 400;
  color: #64748b;
}
.git-modal-label-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.git-modal-input {
  background: #0f172a;
  border: 1px solid #334155;
  border-radius: 0.4rem;
  color: #f1f5f9;
  padding: 0.45rem 0.75rem;
  font-size: 0.875rem;
  width: 100%;
  box-sizing: border-box;
}
.git-modal-input:focus {
  outline: none;
  border-color: #60a5fa;
}
.git-modal-hint {
  font-size: 0.75rem;
  color: #64748b;
  margin: 0;
}
.git-modal-hint code { color: #94a3b8; }
.git-modal-error {
  font-size: 0.78rem;
  color: #f87171;
  margin: 0;
}
.git-help-btn {
  background: #334155;
  color: #94a3b8;
  border: none;
  border-radius: 50%;
  width: 1.3rem;
  height: 1.3rem;
  font-size: 0.7rem;
  font-weight: 700;
  cursor: pointer;
  line-height: 1;
}
.git-help-box {
  background: #0f172a;
  border: 1px solid #334155;
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
  font-size: 0.8rem;
  color: #94a3b8;
}
.git-help-box ol { margin: 0; padding-left: 1.2rem; }
.git-help-box li { margin-bottom: 0.35rem; }
.git-help-box a { color: #60a5fa; }
.git-help-box code { color: #a5b4fc; }
.git-progress-wrap {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}
.git-progress-track {
  flex: 1;
  background: #0f172a;
  border-radius: 999px;
  height: 0.5rem;
  overflow: hidden;
}
.git-progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #3b82f6, #6366f1);
  border-radius: 999px;
  transition: width 0.25s ease;
}
.git-progress-label {
  font-size: 0.75rem;
  color: #94a3b8;
  white-space: nowrap;
}
.git-message {
  padding: 0.6rem 0.9rem;
  border-radius: 0.5rem;
  font-size: 0.85rem;
}
.git-message--success {
  background: rgba(16, 185, 129, 0.15);
  border: 1px solid #10b981;
  color: #6ee7b7;
}
.git-message--error {
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid #ef4444;
  color: #fca5a5;
}
.git-folder-link {
  display: block;
  margin-top: 0.3rem;
  color: #60a5fa;
  text-decoration: none;
  font-size: 0.8rem;
}
.git-modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid #334155;
}
.git-cancel-btn {
  background: transparent;
  color: #94a3b8;
  border: 1px solid #334155;
  border-radius: 0.5rem;
  padding: 0.45rem 1.1rem;
  font-size: 0.875rem;
  cursor: pointer;
}
.git-cancel-btn:hover { border-color: #64748b; color: #e2e8f0; }
.git-upload-btn {
  background: linear-gradient(135deg, #1f2937, #374151);
  color: #e2e8f0;
  border: 1px solid #6b7280;
  border-radius: 0.5rem;
  padding: 0.45rem 1.3rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  transition: all 0.2s;
}
.git-upload-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #374151, #4b5563);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
}
.git-upload-btn:disabled { opacity: 0.6; cursor: not-allowed; }

.wizard-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
  padding: 1rem;
}

.wizard-dialog {
  background: #1e293b;
  border: 1px solid #334155;
  border-radius: 1rem;
  width: 100%;
  max-width: 680px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  overflow: hidden;
}

/* Neutralise any global "input { width: 100% }" inside the wizard */
.wizard-dialog input:not([type=checkbox]):not([type=radio]) {
  width: auto;
}

/* Header */
.wizard-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #334155;
  background: #0f172a;
  flex-shrink: 0;
}
.wizard-header-left {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}
.wizard-icon {
  font-size: 2rem;
}
.wizard-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #f1f5f9;
  margin: 0;
}
.wizard-subtitle {
  font-size: 0.8rem;
  color: #64748b;
  margin: 0;
}
.wizard-close-btn {
  background: none;
  border: none;
  color: #64748b;
  font-size: 1.1rem;
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  transition: color 0.15s;
}
.wizard-close-btn:hover { color: #f1f5f9; }

/* Progress dots */
.wizard-progress {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border-bottom: 1px solid #334155;
  background: #0f172a;
  flex-shrink: 0;
}
.wizard-progress-dot {
  width: 0.65rem;
  height: 0.65rem;
  border-radius: 50%;
  background: #334155;
  transition: all 0.2s;
}
.wizard-progress-dot.done { background: #22c55e; }
.wizard-progress-dot.active { background: #6366f1; transform: scale(1.3); }

/* Body */
.wizard-body {
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
}

.wizard-step-header {
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
}
.step-icon {
  font-size: 2.5rem;
  flex-shrink: 0;
}
.step-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #f1f5f9;
  margin: 0 0 0.25rem;
}
.step-desc {
  font-size: 0.85rem;
  color: #94a3b8;
  margin: 0;
  line-height: 1.5;
}
.optional-tag {
  font-size: 0.7rem;
  background: #334155;
  color: #94a3b8;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  font-weight: 400;
  vertical-align: middle;
  margin-left: 0.5rem;
}

/* Faction blocks */
.wizard-faction-block {
  background: #0f172a;
  border: 1px solid #334155;
  border-radius: 0.5rem;
  padding: 1rem;
  margin-bottom: 1rem;
}
.wizard-faction-name {
  font-size: 0.95rem;
  font-weight: 700;
  color: #f1f5f9;
  margin: 0 0 0.75rem;
}
.wizard-subsection-block {
  margin-top: 0.5rem;
}
.wizard-subsection-label {
  display: inline-block;
  font-size: 0.8rem;
  color: #6366f1;
  font-weight: 600;
  margin-bottom: 0.4rem;
}

/* Rows */
.wizard-list {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  margin-bottom: 0.75rem;
}

.wizard-template-group {
  border: 1px solid #444;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
  background: #2a2a2a;
}

.wizard-template-header {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
  align-items: center;
}

.wiz-template-name {
  flex: 1;
  font-weight: 600;
}

.wizard-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.wizard-row-header {
  font-size: 0.75rem;
  color: #64748b;
  padding-bottom: 0.1rem;
}
.wiz-col-label {
  font-size: 0.75rem;
  color: #64748b;
  font-weight: 600;
}

/* Inputs */
.wiz-input {
  width: calc(10ch + 1.3rem);
  flex: 0 0 auto;  background: #1e293b;
  border: 1px solid #475569;
  border-radius: 0.375rem;
  color: #f1f5f9;
  padding: 0.4rem 0.65rem;
  font-size: 0.85rem;
  outline: none;
  transition: border-color 0.15s;
}
.wiz-input:focus { border-color: #6366f1; }

.wiz-input-sm {
  width: calc(3ch + 0.6rem);
  flex-shrink: 0;
  background: #1e293b;
  border: 1px solid #475569;
  border-radius: 0.375rem;
  color: #f1f5f9;
  padding: 0.4rem 0.3rem;
  font-size: 0.85rem;
  outline: none;
  text-align: center;
  transition: border-color 0.15s;
  -moz-appearance: textfield;
}
.wiz-input-sm:focus { border-color: #6366f1; }
.wiz-input-sm::-webkit-outer-spin-button,
.wiz-input-sm::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }

.wiz-select {
  background: #1e293b;
  border: 1px solid #475569;
  border-radius: 0.375rem;
  color: #f1f5f9;
  padding: 0.4rem 0.5rem;
  font-size: 0.85rem;
  outline: none;
  cursor: pointer;
}

.wiz-remove-btn {
  background: none;
  border: none;
  color: #ef4444;
  cursor: pointer;
  font-size: 0.85rem;
  padding: 0.2rem 0.4rem;
  border-radius: 0.25rem;
  flex-shrink: 0;
  transition: background 0.15s;
}
.wiz-remove-btn:hover { background: rgba(239, 68, 68, 0.15); }

.wiz-add-btn {
  background: none;
  border: 1px dashed #475569;
  color: #94a3b8;
  border-radius: 0.375rem;
  padding: 0.35rem 0.75rem;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.15s;
}
.wiz-add-btn:hover {
  border-color: #6366f1;
  color: #818cf8;
}

/* Footer */
.wizard-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid #334155;
  background: #0f172a;
  flex-shrink: 0;
}
.wiz-back-btn {
  background: #334155;
  color: #cbd5e1;
  border: none;
  border-radius: 0.5rem;
  padding: 0.55rem 1.1rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
}
.wiz-back-btn:hover { background: #475569; }

.wiz-skip-btn {
  background: none;
  border: none;
  color: #64748b;
  font-size: 0.85rem;
  cursor: pointer;
  padding: 0.4rem 0.6rem;
  border-radius: 0.375rem;
  margin-right: auto;
  transition: color 0.15s;
}
.wiz-skip-btn:hover { color: #94a3b8; }

.wiz-next-btn {
  background: linear-gradient(135deg, #6366f1, #4f46e5);
  color: #fff;
  border: none;
  border-radius: 0.5rem;
  padding: 0.55rem 1.3rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
}
.wiz-next-btn:hover {
  background: linear-gradient(135deg, #818cf8, #6366f1);
  transform: translateY(-1px);
}

.wiz-apply-btn {
  background: linear-gradient(135deg, #22c55e, #16a34a);
  color: #fff;
  border: none;
  border-radius: 0.5rem;
  padding: 0.55rem 1.4rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
}
.wiz-apply-btn:hover {
  background: linear-gradient(135deg, #4ade80, #22c55e);
  transform: translateY(-1px);
}
</style>


